function __parseQS(url) {
  try { const u = new URL(url); return u.searchParams; } catch { return new URLSearchParams(); }
}
// src/components/InspectraNetworkView.jsx
import React, { useMemo, useState, useEffect, useRef, useCallback, useDeferredValue } from "react";
import { useNavigate } from "react-router-dom";
import Alert from '@mui/material/Alert';
import { useTraffic } from "../context/TrafficContext";
import { runValidations } from "../validation/validators";
import AdDebugModal from "./AdDebugModal";
import FullRequestAnalysisPane from "./FullRequestAnalysisPane";
import CompareRequestsModal from "./CompareRequestsModal";
import { DiffToolModal } from "./DiffTool";
import { ReplayModal } from "./ReplayTool";
import { buildHarFromRequests } from "../utils/buildHar";
import { bodyState, methodExpectsBody } from "../utils/bodyState";
import { rowMatchesSearch } from "../utils/requestSearch";
import { faviconUrlFor } from "../utils/faviconSafety";
import { loadBody } from "../storage/db";
import { describeRewrite, describeChaosPatch } from "../utils/describeRewrite";
import { saveHarToServer } from "../api/client";
import ConnectDeviceModal from './ConnectDeviceModal';
import AndroidCaptureButton from './AndroidCaptureButton';
import { downloadIabSheet } from "../utils/exportIabSheet";
import SlotStoryView from "./SlotStoryView";
import { buildSlotLifecycles, TierBadge } from "./AdLifecycleView";
import useCoalescedPost from "../utils/useCoalescedPost";
import { useLatestWins } from "../utils/latestWins";
import { postMediationHealth } from "../api/mediationHealth";
import A3pInlineDecode from "./A3pInlineDecode";
import BidHealthView from "./BidHealthView";
import VideoLifecycleView, { VideoSummaryPanel, buildVideoSessions } from "./VideoLifecycleView";
import SecureSignalsHealthPanel from "./SecureSignalsHealthPanel";
import { buildSecureSignalsHealth } from "../validation/secureSignalsHealth";
import { buildMediationHealth, mediationStatusMeta } from "../validation/mediationHealth";
import VideoPassport from './VideoPassport';
import { openSignOffReport, openBlankReportWindow, closeBlankReportWindow } from "../utils/buildSignOffReport";
import { postLifecycleGroups } from "../api/lifecycle";
import { postCustParamsRules } from "../api/custParamsRules";
import { getActiveProfile } from "../config/appProfiles";
import { loadExcludeList, isExcluded, hostnameFromUrl } from "../utils/excludeList";
import { describeBinaryBody } from "../utils/binaryMarker";
import { aggregatePacks, getPacks } from "../api/appPacks";
import { getApsParamDictionary, getPrebidParamDictionary } from "../api/checks";
import { buildRowParamConformance } from "../utils/buildRowParamConformance";
import { requestLink } from "../utils/deepLinks";
// Slot Story step 4 (Raw requests, engineer view)
import RequestsViewTabs from "./RequestsViewTabs";
import SelectionBar from "./SelectionBar";
import ToolbarOverflowMenu from "./ToolbarOverflowMenu";
import RequestHeadline from "./RequestHeadline";
import useRequestsLifecycles from "../utils/useRequestsLifecycles";
import { readRequestsAdOnly, writeRequestsAdOnly, partitionAdRequests, slotReferencedKeys, needsUnhide } from "../utils/adRequestFilter";
import { mergeBillingPings, foldMergedRows, duplicateBillingTip } from "../utils/mergeBillingPings";import { codeCellFor, methodBadgeFor } from "../utils/requestRowDisplay";
import { buildRowHeadline, buildRowSlotIndex } from "../utils/rowHeadline";
import { PAGE_INHERIT_TYPES, BEACON_TYPES_SET } from "../utils/requestTypeSets";


/**
 * Props:
 *  data: Array<{
 *    id, requestId, method, url, status|responseCode, type, startedAt, durationMs, size,
 *    requestHeaders, responseHeaders, requestBody, responseBody, cookies, requestType,
 *    // optional: configAttributes, contextRef, validData, issues, serverReport, etc.
 *  }>
 *  onRowOpen?: (row) => void
 *  configAttributesGlobal?: object  <-- device/config flags to embed into HAR
 */

/* ── ATT per-request extraction ─────────────────────────────────────────────
   Returns { val: '0'|'1'|'2'|'3', source: string } or null if not detectable.
   val maps to: 0=Not Determined  1=Restricted  2=Denied  3=Authorized
────────────────────────────────────────────────────────────────────────────── */
const ATT_META = {
  '0': { label: 'ATT: ?',       color: '#6b7280' },
  '1': { label: 'ATT: Restr',   color: '#d97706' },
  '2': { label: 'ATT: Denied',  color: '#dc2626' },
  '3': { label: 'ATT: Auth',    color: '#16a34a' },
};
const ATT_FULL = { '0': 'Not Determined', '1': 'Restricted', '2': 'Denied', '3': 'Authorized' };

function getRowATT(r) {
  const rt = r.requestType || r.type || '';
  const url = r.url || '';

  // GAM display / video — proxy parses attmas into validData
  if (rt === 'google' || rt === 'gam_video') {
    const v = r.validData?.attmas;
    if (v != null) return { val: String(v), source: 'GAM attmas' };
    try { const p = new URL(url).searchParams.get('attmas'); if (p != null) return { val: p, source: 'GAM attmas' }; } catch {}
  }

  // Prebid — proxy writes issues[].code==='att' with note "ATT=3 (Authorized)"
  if (rt === 'prebid') {
    const issue = (r.issues || []).find(i => i.code === 'att');
    if (issue) { const m = (issue.note || '').match(/ATT[=\s]+(\d)/); if (m) return { val: m[1], source: 'Prebid device.ext.atts' }; }
  }

  // IMA — same attmas param as GAM
  if (rt === 'ima') {
    try { const p = new URL(url).searchParams.get('attmas'); if (p != null) return { val: p, source: 'IMA attmas' }; } catch {}
    try { const p = new URL(url).searchParams.get('is_lat'); if (p === '0') return { val: '3', source: 'IMA is_lat=0' }; if (p === '1') return { val: '2', source: 'IMA is_lat=1' }; } catch {}
  }

  // Amazon A2S (OTT C2S — OpenRTB)
  if (rt === 'amazon_video') {
    const body = r.requestBodyParsed || (() => { try { return JSON.parse(r.requestBody); } catch { return null; } })();
    const v = body?.device?.ext?.atts ?? body?.device?.att;
    if (v != null) return { val: String(v), source: 'A2S device.ext.atts' };
  }

  // Amazon APS (display — proprietary)
  if (rt === 'amazon') {
    try { const p = new URL(url).searchParams.get('att'); if (p != null) return { val: p, source: 'APS att param' }; } catch {}
    const body = r.requestBodyParsed || (() => { try { return JSON.parse(r.requestBody); } catch { return null; } })();
    const v = body?.att ?? body?.device?.ext?.atts;
    if (v != null) return { val: String(v), source: 'APS body' };
  }

  // SpringServe VAST
  if (url.includes('springserve.com')) {
    try { const p = new URL(url).searchParams.get('att'); if (p != null) return { val: p, source: 'SpringServe att' }; } catch {}
  }

  return null;
}

/* ---------------- tiny helpers ---------------- */

const fmtBytes = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let i = -1,
    v = n;
  do {
    v /= 1024;
    i++;
  } while (v >= 1024 && i < units.length - 1);
  return `${v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0)} ${units[i]}`;
};
const fmtMs = (n) => (n || n === 0) ? `${Math.round(n)} ms` : "";

// A row whose in-memory body was trimmed may still have it in the IndexedDB stash
// (the detail pane shows it as "recovered from local stash"). Exports read the
// stash too, or a downloaded/saved HAR carries an empty body the dashboard shows.
const hasBody = (v) => v != null && v !== '';
async function withStashedBodies(rows) {
  return Promise.all((rows || []).map(async (r) => {
    if (!r || (hasBody(r.requestBody) && hasBody(r.responseBody))) return r;
    const rec = await loadBody(r.requestId || r.id);
    if (!rec) return r;
    const requestBody = hasBody(r.requestBody) ? r.requestBody : (rec.requestBody ?? r.requestBody);
    const responseBody = hasBody(r.responseBody) ? r.responseBody : (rec.responseBody ?? r.responseBody);
    return { ...r, requestBody, responseBody };
  }));
}
// Prefer startedAt, fall back to a few common fields, else 0
// Always returns a number — handles ISO strings, epoch numbers, and missing values.
const _toMs = (v) => { if (!v && v !== 0) return 0; if (typeof v === 'number') return v; const n = Date.parse(v); return isNaN(n) ? 0 : n; };
// Exported so callers upstream of this view (MainApp's row pipeline) normalize
// timestamps the exact same way — e.g. the session-boundary split (sessionBoundary.js)
// must agree with this view on what "before/after" means for a mixed ISO/epoch row set.
export const ts = (r) =>
  _toMs(r?.startedAt) ||
  _toMs(r?.startedAtMs) ||
  _toMs(r?.timing?.start) ||
  _toMs(r?.startedAtISO) ||
  _toMs(r?.timestamp) ||
  0;

// docs/validate-ad-only-spec.md — validate a row iff it's ad-relevant. Every
// broadcast row carries `adCategory` (proxy-server/adTraffic/classifyAdTraffic.js,
// `classifyRows()`). That module's own AD_ISH set is the source of truth —
// mirrored here (NOT imported; the proxy and frontend don't share a build):
//   AD_ISH = new Set(['ad_request','bid_request','creative','ad_tracking','vast','ivt_verification'])
// 'unknown' gets the benefit of the doubt (same arithmetic the Checks page's
// "N ad-relevant of M" banner uses — ChecksPage.jsx's adRelevantCount). Excluded:
// 'non_ad', 'analytics', 'consent'.
//
// Back-compat, on purpose: a row with NO adCategory at all (old sessions,
// imported HARs, pre-classifier captures) validates exactly as it did before
// this predicate existed. Getting this backwards would silently stop validating
// real ad rows with no visible signal in the UI — the highest-risk failure mode
// this predicate can have, so the missing-category branch must stay `true`.
export const AD_ISH_CATEGORIES = new Set([
  'ad_request', 'bid_request', 'creative', 'ad_tracking', 'vast', 'ivt_verification',
]);
const NON_AD_CATEGORIES = new Set(['non_ad', 'analytics', 'consent']);

export function isAdRelevantRow(row) {
  const cat = row && row.adCategory;
  if (cat === undefined || cat === null) return true; // no classifier stamp ⇒ validate
  if (cat === 'unknown') return true;
  if (AD_ISH_CATEGORIES.has(cat)) return true;
  if (NON_AD_CATEGORIES.has(cat)) return false;
  return true; // unrecognized future category — never silently skip a row we can't place
}

const fmtTime = (n) => (n ? new Date(n).toTimeString().slice(0, 8) : "");

// Robust clipboard copy — navigator.clipboard is undefined / throws outside a
// secure context (plain-http dashboards), so fall back to execCommand. Returns
// true on success so callers can show feedback.
function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(String(text));
      return true;
    }
  } catch { /* fall through to legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = String(text);
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    ta.remove();
    return true;
  } catch { return false; }
}

// Decode the GAM/IMA `a3p` secure-signals param (URL-safe base64). The payload
// is a binary/protobuf blob carrying encrypted signals (UID2, LiveRamp, etc.),
// so we surface the byte length, the printable ASCII extracted from it, and a
// best-effort detection of which signal sources are present.
function decodeA3p(raw) {
  if (!raw) return { present: false };
  let s = String(raw).trim();
  try { s = decodeURIComponent(s); } catch { /* not url-encoded */ }
  s = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = s.length % 4;
  if (pad) s += '='.repeat(4 - pad);
  let bin = '';
  try { bin = atob(s); } catch { return { present: true, error: true, rawLen: String(raw).length, raw: String(raw) }; }
  let printable = '';
  for (let i = 0; i < bin.length; i++) {
    const c = bin.charCodeAt(i);
    printable += (c >= 0x20 && c <= 0x7e) ? bin[i] : '·';
  }
  const hay = bin.toLowerCase();
  const SIG = [
    { label: 'UID2 / EUID', re: /uid2|uidapi|euid/ },
    { label: 'LiveRamp ATS', re: /liveramp|rampid|rlid|liveintent/ },
    { label: 'ID5', re: /id5/ },
    { label: 'Criteo', re: /criteo/ },
    { label: 'Meta / Audience Network', re: /facebook|fban|audiencenetwork/ },
  ];
  const detected = SIG.filter(x => x.re.test(hay)).map(x => x.label);

  // Extract registered secure-signal adapter classes (reverse-DNS ...Adapter)
  // from the printable bytes — these are the human-meaningful part of the blob
  // and tell QA which signal SDKs are actually registered on this request.
  const ADAPTER_LABELS = {
    vungle: 'Vungle', uid2: 'UID2', euid: 'EUID', liveramp: 'LiveRamp ATS',
    lrats: 'LiveRamp ATS', id5: 'ID5', criteo: 'Criteo', prebid: 'Prebid',
  };
  const adapters = [];
  const seenCls = new Set();
  const adapterRe = /[a-zA-Z][a-zA-Z0-9_]*(?:\.[a-zA-Z0-9_]+)+Adapter/g;
  let am;
  while ((am = adapterRe.exec(printable)) !== null) {
    const cls = am[0];
    if (seenCls.has(cls)) continue;
    seenCls.add(cls);
    const lc = cls.toLowerCase();
    const key = Object.keys(ADAPTER_LABELS).find(k => lc.includes(k));
    const last = cls.split('.').pop().replace(/MediationAdapter|Adapter/, '');
    const label = key ? ADAPTER_LABELS[key] : (last.replace(/([a-z0-9])([A-Z])/g, '$1 $2') || last);
    adapters.push({ cls, label });
  }

  return { present: true, rawLen: String(raw).length, bytes: bin.length, printable, detected, adapters, raw: String(raw) };
}

const parseURL = (u) => {
  try {
    const a = new URL(u);
    return { host: a.host, path: a.pathname + (a.search || "") };
  } catch {
    return { host: "", path: u || "" };
  }
};
const hostOf = (u = "") => {
  try {
    return new URL(u).host.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const decodePlus = (s = "") => decodeURIComponent(String(s).replace(/\+/g, "%20"));
function qsToObj(qs) {
  const out = {};
  if (!qs) return out;
  const s = qs.replace(/^\?/, "");
  for (const part of s.split("&")) {
    if (!part) continue;
    const [k, v = ""] = part.split("=");
    if (!k) continue;
    const val = decodePlus(v);
    if (out[k] != null) Array.isArray(out[k]) ? out[k].push(val) : (out[k] = [out[k], val]);
    else out[k] = val;
  }
  return out;
}

const safeJson = (txt) => {
  if (txt == null || txt === "") return { ok: false, value: undefined, err: "empty" };
  if (typeof txt === "object") return { ok: true, value: txt, err: null };
  if (typeof txt === "string" && txt.startsWith("__BINARY__"))
    return { ok: false, value: undefined, err: "binary" };
  try {
    return { ok: true, value: JSON.parse(txt), err: null };
  } catch (e) {
    return { ok: false, value: undefined, err: e.message };
  }
};

const looksLikeHttp = (s) => typeof s === "string" && /^https?:\/\//i.test(s);
const canonicalPage = (u) => {
  try {
    const x = new URL(u);
    x.hash = "";
    return `${x.origin}${x.pathname}`;
  } catch {
    return "";
  }
};

function deepFindLikelyUrl(obj, keyRx = /(content_url|page_url|page|cturl|url)$/i) {
  const q = [obj];
  while (q.length) {
    const cur = q.shift();
    if (!cur || typeof cur !== "object") continue;
    for (const [k, v] of Object.entries(cur)) {
      const kL = String(k).toLowerCase();
      if (typeof v === "string") {
        if (looksLikeHttp(v) && (keyRx.test(kL) || kL.includes("url") || kL.includes("page")))
          return v;
      } else if (v && typeof v === "object") q.push(v);
    }
  }
  return "";
}

/* ── Bid Correlation helpers ─────────────────────────────────────────────── */

/** Parse hb_* values from a GAM URL's cust_params. Used as fallback when
 *  validData.expanded doesn't have them (older captures). */
function extractGAMHbParams(url) {
  try {
    const u = new URL(url);
    const cp = u.searchParams.get('cust_params');
    if (!cp) return {};
    const sp = new URLSearchParams(decodeURIComponent(cp));
    return {
      hbCacheId: sp.get('hb_cache_id') || '',
      hbBidder:  sp.get('hb_bidder')   || '',
      hbPb:      sp.get('hb_pb')       || '',
    };
  } catch { return {}; }
}

/** Build a Map from rowKey → correlation record.
 *  A "correlation" links a Prebid auction row to its downstream GAM row
 *  via the shared hb_cache_id. */
function buildBidCorrelation(rows) {
  const map = new Map();

  // Index every Prebid bid by its cache ID
  const prebidByCacheId = new Map(); // cacheId → { row, bid }
  rows.forEach(r => {
    if ((r.type || r.requestType) !== 'prebid') return;
    (r.validData?.bids || []).forEach(bid => {
      const cid = bid.cache;
      if (cid && cid !== '-') prebidByCacheId.set(cid, { row: r, bid });
    });
  });

  // For each GAM row, find its matching Prebid auction
  rows.forEach(r => {
    if ((r.type || r.requestType) !== 'google') return;

    const exp = r.validData?.expanded || {};
    const fromUrl = exp.prebidId ? {} : extractGAMHbParams(r.url);

    const cacheId   = exp.prebidId  || fromUrl.hbCacheId || '';
    const gamBidder = exp.hbBidder  || fromUrl.hbBidder  || '';
    const gamHbPb   = exp.hbPb      || fromUrl.hbPb      || '';

    if (!cacheId) return;
    const match = prebidByCacheId.get(cacheId);
    if (!match) return;

    const prebidHbPb  = match.bid.hb_pb !== '-' ? match.bid.hb_pb : '';
    const discrepancy = !!(gamHbPb && prebidHbPb && gamHbPb !== prebidHbPb);

    const corr = {
      cacheId,
      bidder:       match.bid.seat || gamBidder || '?',
      prebidPrice:  match.bid.price,
      prebidHbPb,
      gamHbPb,
      discrepancy,
      gamRowKey:    r._k,
      prebidRowKey: match.row._k,
    };

    map.set(r._k,          { ...corr, role: 'gam'    });
    map.set(match.row._k,  { ...corr, role: 'prebid' });
  });

  return map;
}

// Anti-flap window for return-to-page detection: an immediate flip BACK to the page
// we just left within this window is treated as interleave noise (concurrent fresh
// loads), not a real navigation, so it doesn't spawn a spurious visit.
const CONTEXT_FLAP_MS = 1500;

// Hosts that must NEVER be treated as a content page even if a request resolves
// a URL on them (via referer/params/body). A denylist is inherently incomplete —
// see also the pattern-based consent/privacy rejection and the referer
// self-reference guard in getPageUrlForRequest, which catch new hosts generically.
//
// Brand-neutral entries live here in code. Brand-owned hosts (e.g. the old
// foxnews.com/assets, moxie.foxnews.com, privacy.foxnews.com literals) now come
// from each App Pack's contentApis.ignoredPageHosts, unioned via
// aggregatePacks() below — see proxy-server/config/apps/README.md.
const GENERIC_IGNORED_PAGE_HOSTS = [
  'googleapis.com',
  'doubleclick.net',
  'googlesyndication.com',
  'amazon-adsystem.com',
  'adnxs.com',
  'springserve.com',
  'apple.com',
  'outbrain.com',
  // Ad verification / traffic-quality / creative CDNs
  'adtrafficquality.google',
  '2mdn.net',
  'doubleverify.com',
  'moatads.com',
  'adsafeprotected.com',
  // Consent / privacy / CMP
  'betrad.com',          // Evidon/Crashlytics consent
  'onetrust.com',
  'cookielaw.org',
  'sourcepoint.mgr.consensu.org',
  // Analytics / telemetry / attribution
  'optimizely.com',
  'scorecardresearch.com',
  'chartbeat.com',
  'chartbeat.net',
  'segment.com',
  'segment.io',
  'amplitude.com',
  'branch.io',
  'app-measurement.com',
  'crashlytics.com',
  'sentry.io',
  'demdex.net',
  'omtrdc.net',
];

// Recomputed (not memoized) so it always reflects the latest pack data —
// cheap: aggregatePacks() is itself memoized and this list is only walked via
// `.some()` per request, not in a hot loop.
function getIgnoredPageHosts() {
  const { ignoredPageHosts } = aggregatePacks();
  return [...GENERIC_IGNORED_PAGE_HOSTS, ...(ignoredPageHosts || [])];
}

/** Try to pull the logical "page URL" for a given ad request */
function getPageUrlForRequest(req) {
  let candidate = "";

  // 1) GAM / query params — content_url is strongest signal
  try {
    const u = new URL(req.url);
    const p = qsToObj(u.search);
    const fromQs = p.content_url || p.page_url || p.url;
    if (looksLikeHttp(fromQs)) candidate = canonicalPage(fromQs);

    // 1b) GAM cust_params may contain content_url or cturl
    if (!candidate && p.cust_params) {
      try {
        const cp = qsToObj('?' + decodeURIComponent(p.cust_params));
        const fromCp = cp.content_url || cp.cturl || cp.page_url;
        if (looksLikeHttp(fromCp)) candidate = canonicalPage(fromCp);
      } catch { }
    }

    // 1c) Reconstruct page from GAM ad unit + description_url combo
    //     description_url carries the page URL for many mobile integrations
    if (!candidate && p.description_url) {
      const du = decodeURIComponent(p.description_url);
      if (looksLikeHttp(du)) candidate = canonicalPage(du);
    }
  } catch { }

  // 2) Prebid / APS / OpenRTB body
  if (!candidate) {
    const sj = safeJson(req.requestBody);
    if (sj.ok) {
      const j = sj.value || {};
      const direct =
        j?.app?.content?.url || // Prebid app
        j?.site?.page ||         // ORTB site
        j?.site?.ref  ||         // ORTB referrer
        j?.pjs?.cstm?.cturl ||   // APS
        "";
      const v = looksLikeHttp(direct) ? direct : deepFindLikelyUrl(j);
      if (looksLikeHttp(v)) candidate = canonicalPage(v);
    }
  }

  // 3) Referer header — reliable for in-app WebView and Safari ad calls.
  //    Self-reference guard: an ad/infra sub-request fired from within an ad
  //    iframe carries a Referer pointing at its OWN host (e.g. a beacon on
  //    adtrafficquality.google refers to adtrafficquality.google). That is not a
  //    content page — only trust the referer when it points to a DIFFERENT host
  //    than the request itself (a real content page differs from the ad server).
  if (!candidate) {
    const ref = req?.requestHeaders?.referer || req?.requestHeaders?.Referer;
    if (looksLikeHttp(ref)) {
      let refHost = '', reqHost = '';
      try { refHost = new URL(ref).hostname.toLowerCase(); } catch {}
      try { reqHost = new URL(req.url).hostname.toLowerCase(); } catch {}
      if (refHost && refHost !== reqHost) candidate = canonicalPage(ref);
    }
  }

  // 4) X-Page-Url custom header (some app SDKs pass this)
  if (!candidate) {
    const xp = req?.requestHeaders?.['x-page-url'] || req?.requestHeaders?.['x-content-url'];
    if (looksLikeHttp(xp)) candidate = canonicalPage(xp);
  }

  // Validation: reject ad/infra/consent/telemetry hosts — never content pages.
  if (candidate) {
    try {
      const h = new URL(candidate).hostname.toLowerCase();
      // 1) Explicit denylist (substring match).
      if (getIgnoredPageHosts().some(ignored => h.includes(ignored))) {
        return "";
      }
      // 2) Pattern match — consent/privacy/CMP & telemetry subdomains generically,
      //    so new hosts (privacy.* , consent.* , cmp.* , metrics.* …) are caught
      //    without having to enumerate every vendor.
      const firstLabel = h.split('.')[0];
      // Note: deliberately excludes 'events'/'log'/'trust' — those are real content
      // subdomains on some sites (events.*, trust.*); rely on the host denylist for those.
      if (/^(privacy|consent|cmp|gdpr|usp|ccpa|cookie|cookies|onetrust|sourcepoint|metrics|telemetry|analytics|logx|beacon|pixel|collect)$/.test(firstLabel)) {
        return "";
      }
    } catch { return ""; }
  }

  return candidate;
}

/* ── Page identity for visit segmentation ─────────────────────────────────
 * content_url is the authoritative page signal: getPageUrlForRequest()
 * resolves it to origin + path (full page URL) for GAM/Prebid/APS/referer.
 * Each distinct content_url is its own page; navigating to a new page changes
 * content_url and therefore starts a new page group / visit.
 *
 * getPageIdentity() returns { key, label, url } or null. When null, the caller
 * inherits the surrounding page — used for auction/beacon requests, which fire
 * inside a page load and should never split off into their own page group.
 */


// host + path label from a real page URL (module-level twin of pageLabelOf).
function pageLabelFromUrl(pageUrl) {
  if (!pageUrl || pageUrl === '(unknown)') return '';
  try {
    const u = new URL(pageUrl);
    const path = u.pathname.replace(/\/$/, '') || '/';
    return `${u.host}${path}`;
  } catch { return pageUrl; }
}

function getPageIdentity(req) {
  // Auction/beacon requests never define a page on their own — inherit the
  // surrounding page so they don't fragment a page group.
  const type = (req.type || req.requestType || '').toLowerCase();
  if (PAGE_INHERIT_TYPES.has(type)) return null;
  // content_url (origin + path) is the authoritative page signal.
  const url = getPageUrlForRequest(req);
  if (url) return { key: url, label: pageLabelFromUrl(url), url };
  return null;
}

function getEncoding(req) {
  return (
    req?.contentEncoding ||
    req?.responseHeaders?.["content-encoding"] ||
    req?.responseHeaders?.["Content-Encoding"] ||
    "identity"
  ).toLowerCase();
}

function getMime(req) {
  const ct = (
    req?.responseContentType ||
    req?.responseHeaders?.["content-type"] ||
    req?.responseHeaders?.["Content-Type"] ||
    ""
  ).toLowerCase();
  return ct.split(";")[0];
}

function shortMime(mime) {
  if (!mime) return "";
  if (mime.includes("json")) return "json";
  if (mime.includes("javascript")) return "js";
  if (mime.includes("xml")) return "xml";
  if (mime.startsWith("text/html")) return "html";
  if (mime.startsWith("text/css")) return "css";
  if (mime.startsWith("image/")) return "img";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  return mime.split("/")[1] || mime;
}

function Pill({ children, tone = "neutral", title }) {
  const tones = {
    success: { background: "var(--success-bg)", color: "var(--success-text)", border: "1px solid var(--success-border)" },
    warn: { background: "var(--warn-bg)", color: "var(--warn-text)", border: "1px solid var(--warn-border)" },
    error: { background: "var(--error-bg)", color: "var(--error-text)", border: "1px solid var(--error-border)" },
    neutral: { background: "var(--neutral-bg)", color: "var(--neutral-text)", border: "1px solid var(--neutral-border)" },
    info: { background: "rgba(30,120,220,.13)", color: "#5b9cf6", border: "1px solid rgba(91,156,246,.3)" },
  };
  return <span style={{ ...styles.pill, ...tones[tone] }} title={title}>{children}</span>;
}

// Vendor badge — filled chip with brand colour, distinguishable from secondary format/mime badges
function VendorBadge({ type }) {
  const cfg = {
    google:   { label: 'Google',  bg: 'rgba(66,133,244,.18)',  color: '#4285f4', border: '1px solid rgba(66,133,244,.35)' },
    prebid:   { label: 'Prebid',  bg: 'rgba(245,166,35,.18)',  color: '#c97a00', border: '1px solid rgba(245,166,35,.35)' },
    amazon:   { label: 'Amazon',  bg: 'rgba(255,153,0,.18)',   color: '#b36200', border: '1px solid rgba(255,153,0,.35)' },
    omid:     { label: 'OMID',    bg: 'rgba(0,188,212,.13)',   color: '#00838f', border: '1px solid rgba(0,188,212,.3)'  },
    ima:      { label: 'IMA DAI', bg: 'rgba(66,133,244,.15)',  color: '#3a6dc9', border: '1px solid rgba(66,133,244,.3)' },
    content:  { label: 'Content', bg: 'rgba(144,164,174,.13)', color: '#546e7a', border: '1px solid rgba(144,164,174,.3)' },
    analytics:{ label: 'Analytics', bg: 'rgba(103,58,183,.13)', color: '#7c4dff', border: '1px solid rgba(103,58,183,.25)' },
    unclassified: { label: 'Unclassified', bg: 'rgba(120,120,120,.10)', color: '#8a9099', border: '1px dashed rgba(120,120,120,.5)' },
  };
  const t = (type || '').toLowerCase().replace(/[_-]/g, '');
  const key = t.startsWith('google') || t === 'gam' ? 'google'
    : t.startsWith('prebid') ? 'prebid'
    : t.startsWith('amazon') ? 'amazon'
    : t.startsWith('omid') ? 'omid'
    : t === 'ima' ? 'ima'
    : t === 'analytics' ? 'analytics'
    : t === 'content' || t === 'videocontent' ? 'content'
    : t === 'unclassified' ? 'unclassified'
    : null;
  if (!key) return null;
  const { label, bg, color, border } = cfg[key];
  return (
    <span style={{ ...styles.pill, background: bg, color, border, fontWeight: 800 }}>
      {label}
    </span>
  );
}

// Subtle secondary badge — format (Display/Video), encoding, MIME, etc.
// Smaller and lighter than Pill so it doesn't compete with VendorBadge.
function MetaBadge({ children, tone = "neutral", title }) {
  const colors = {
    neutral: { color: "var(--muted)", border: "1px solid var(--line)", background: "transparent" },
    warn:    { color: "var(--warn-text)", border: "1px solid var(--warn-border)", background: "var(--warn-bg)" },
    success: { color: "var(--success-text)", border: "1px solid var(--success-border)", background: "var(--success-bg)" },
    error:   { color: "var(--error-text)", border: "1px solid var(--error-border)", background: "var(--error-bg)" },
    info:    { color: "#5b9cf6", border: "1px solid rgba(91,156,246,.3)", background: "rgba(30,120,220,.08)" },
  };
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center",
      padding: "1px 5px", borderRadius: 3,
      fontSize: 9, fontWeight: 600, lineHeight: "14px",
      whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.05em",
      ...colors[tone],
    }}>{children}</span>
  );
}

// Ad unit / slot path badge — monospace, truncated, clearly secondary
function AdUnitBadge({ children, title }) {
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center",
      padding: "1px 6px", borderRadius: 3,
      fontSize: 9.5, fontWeight: 500, lineHeight: "15px",
      whiteSpace: "nowrap", fontFamily: "monospace",
      background: "var(--neutral-bg)", color: "var(--neutral-text)",
      border: "1px solid var(--neutral-border)",
      maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis",
    }}>{children}</span>
  );
}

const statusTone = (s) => {
  if (!s) return "neutral";
  if (s >= 500) return "error";
  if (s >= 400) return "warn";
  if (s >= 300) return "warn";
  if (s >= 200) return "success";
  return "neutral";
};
const toneFromOverall = (s) => (s === "fail" ? "error" : s === "warn" ? "warn" : "success");

const statusColor = (s) =>
  s === "fail" ? "var(--error-text)" : s === "warn" ? "var(--warn-text)" : s === "pass" ? "var(--success-text)" : "var(--muted)";
const StatusDot = ({ s }) => (
  <span title={s} style={{ display: "inline-block", width: 10, height: 10, borderRadius: 999, background: statusColor(s) }} />
);

/* ---------------- helpers for GAM ---------------- */


const isGAMRequest = (req) => {
  // Only match the actual GAM ad-request endpoint, NOT impression/click tracking beacons.
  // Impression beacons go to ad.doubleclick.net/ddm/trackimp/... and must NOT open the creative debugger.
  const u = (req?.url || "").toLowerCase();
  return u.includes("/gampad/ads") || u.includes("securepubads.g.doubleclick.net");
};

// --- Visit-level helpers for ad/content validation ---
const isAdRequestAny = (req) => {
  const u = (req?.url || '').toLowerCase();
  return (
    u.includes('doubleclick.net') ||
    u.includes('securepubads') ||
    u.includes('amazon-adsystem.com') ||
    u.includes('adnxs.com') ||
    u.includes('prebid')
  );
};

const isContentLikeRequest = (req) => {
  const u = (req?.url || '').toLowerCase();
  return (
    u.includes('/article') ||
    u.includes('/content') ||
    u.includes('/graphql') ||
    u.includes('/api/')
  );
};

/* ---------------- main component ---------------- */

/* --- Helper to extract Ad Unit Identifiers --- */
function getAdUnitLabel(r) {
  const u = (r.url || "").toLowerCase();
  const t = (r.type || "").toLowerCase();

  // 1. GAM: 'iu' parameter
  if (t === 'google' || u.includes('/gampad/ads')) {
    const qs = __parseQS(r.url);
    return qs.get('iu') || "";
  }

  // 2. Prebid: storedrequest.id or code
  if (t === 'prebid') {
    const sj = safeJson(r.requestBody);
    if (sj.ok) {
      const imp = sj.value?.imp?.[0];
      return imp?.ext?.prebid?.storedrequest?.id || imp?.code || "";
    }
  }

  // 3. Amazon: slotID
  if (t === 'amazon') {
    const sj = safeJson(r.requestBody);
    if (sj.ok) {
      return sj.value?.slots?.[0]?.slotID || "";
    }
  }

  return "";
}

/* ─── Lifecycle Summary Panel ───────────────────────────────────────────────
 * Shown in the right panel when a lifecycle phase node is clicked.
 * Gives a full-slot overview: phases, winner, issues, latencies.
 * Replaces scrolling through raw request details for the common QA workflow. */
const S_COLOR = {
  pass: { bg: 'rgba(93,202,165,0.10)', border: '#3DAA85', text: '#4DC99A' },
  warn: { bg: 'rgba(239,159,39,0.10)', border: '#CC8A20', text: '#EF9F27' },
  fail: { bg: 'rgba(240,100,100,0.10)', border: '#C05050', text: '#E07070' },
  miss: { bg: 'transparent', border: 'var(--line)', text: 'var(--muted)' },
};

/* ─── Partner Incident Report export ──────────────────────────────────────
 * Bundles lifecycle data (issues, bids, HAR entries for each phase) into a
 * structured JSON file that can be sent directly to a partner (e.g. PubMatic)
 * to escalate a passback or malformed-creative incident.
 */
function downloadIncidentReport(lifecycle) {
  const { adUnit, sizes, issues, phases, timestamp, pageUrl, visitKey } = lifecycle;
  const partner = phases?.prebid?.winner?.seat || phases?.gam?.hbBidder || 'unknown';

  // Convert a row object to a minimal HAR-like entry (strips heavy internal fields)
  const toHarEntry = (row) => {
    if (!row) return null;
    const bodyStr = (b) => {
      if (!b) return null;
      if (typeof b === 'string') return b;
      try { return JSON.stringify(b, null, 2); } catch { return String(b); }
    };
    return {
      requestType: row.requestType || row.type,
      url: row.url,
      method: row.method || 'GET',
      status: row.status || row.responseCode,
      startedAt: row.startedAt || row.startedAtISO || row.timestamp,
      requestHeaders: row.requestHeaders || {},
      responseHeaders: row.responseHeaders || {},
      requestBody: bodyStr(row.requestBody || row.requestBodyParsed),
      responseBody: bodyStr(row.responseBody),
    };
  };

  const report = {
    reportedAt: new Date().toISOString(),
    tool: 'AdLens FX — Partner Incident Report v1',
    partner,
    incident: {
      adUnit,
      slotSizes: sizes,
      gamTimestamp: new Date(timestamp).toISOString(),
      page: pageUrl || 'unknown',
      sessionVisit: visitKey || 'unknown',
      issues: (issues || [])
        .filter(i => i.level === 'fail' || i.level === 'warn')
        .map(i => ({ level: i.level, description: i.msg })),
    },
    prebidAuction: phases?.prebid?.fired ? {
      gpid: phases.prebid.gpid || null,
      storedRequestId: phases.prebid.storedRequestId || null,
      impSizes: phases.prebid.impSizes || null,
      tmax: phases.prebid.tmax || null,
      matchedBy: phases.prebid.matchedBy || null,
      winner: phases.prebid.winner ? {
        bidder: phases.prebid.winner.seat,
        cpm: phases.prebid.winner.price,
        hb_pb: phases.prebid.winner.hb_pb,
        creativeSize: phases.prebid.winner.size,
        latencyMs: phases.prebid.winner.latencyMs,
        dealId: phases.prebid.winner.dealid !== '-' ? phases.prebid.winner.dealid : null,
      } : null,
      allBids: (phases.prebid.bids || []).map(b => ({
        bidder: b.seat, cpm: b.price, size: b.size, status: b.status, latencyMs: b.latencyMs,
      })),
    } : null,
    gamDecision: {
      adUnit, sizes,
      hbBidder: phases?.gam?.hbBidder || null,
      hbPb: phases?.gam?.hbPb || null,
      winnerCpm: phases?.gam?.winnerCpm || null,
      demand: phases?.gam?.adapterLabel || phases?.gam?.adapter || null,
      isRefresh: phases?.gam?.isRefresh || false,
    },
    impression: phases?.impression?.fired ? {
      beaconType: phases.impression.beaconType,
      latencyMs: phases.impression.latencyMs,
      matchTier: phases.impression.matchTier,
      slow: phases.impression.latencyMs > 30000
        && ['xai', 'sai', 'impCorrelator', 'correlator', 'adUnit'].includes(phases.impression.matchTier),
    } : { fired: false },
    harEntries: [
      toHarEntry(phases?.prebid?.row),
      toHarEntry(phases?.gam?.row),
      toHarEntry(phases?.impression?.row),
      toHarEntry(phases?.amazon?.row),
      toHarEntry(phases?.click?.row),
    ].filter(Boolean),
  };

  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const slug = adUnit.split('/').filter(Boolean).slice(-2).join('_');
  a.download = `incident-${partner}-${slug}-${Date.now()}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

function LifecycleSummaryPanel({ lifecycle, activeSel, onSwitchToDetails }) {
  // Hooks must be called unconditionally — guard return comes after
  const [debugOpen, setDebugOpen] = useState(false);

  // Check whether ad_html is present in the GAM response body.
  // Fallback: if impression fired, infer fill even without response body
  // (proxy strips responseBody from disk history on restart to save space).
  const adHtmlStatus = useMemo(() => {
    const body = lifecycle?.phases?.gam?.row?.responseBody;
    const impressionFired = lifecycle?.phases?.impression?.fired;

    if (!body) {
      // No response body — check impression beacon as proof of fill
      if (impressionFired) {
        return { present: true, inferred: true, reason: 'impression confirmed fill' };
      }
      return { present: false, bodyMissing: true, reason: 'response body not in session (reconnect clears it)' };
    }

    let parsed;
    if (typeof body === 'object') {
      parsed = body;
    } else {
      try { parsed = JSON.parse(String(body)); }
      catch {
        // Body present but not JSON — check if it contains ad markup directly
        const hasHtml = String(body).length > 200 && (String(body).includes('<script') || String(body).includes('googletag'));
        if (hasHtml) return { present: true, sizeKb: (body.length / 1024).toFixed(1), tiny: false };
        if (impressionFired) return { present: true, inferred: true, reason: 'impression confirmed fill' };
        return { present: false, reason: 'response body is not valid JSON' };
      }
    }
    const nets = Array.isArray(parsed?.ad_networks) ? parsed.ad_networks : [];
    if (!nets.length) {
      if (impressionFired) return { present: true, inferred: true, reason: 'impression confirmed fill' };
      return { present: false, reason: 'no ad_networks in response' };
    }
    const html = nets.map(n => n?.ad?.ad_html || n?.ad?.html || '').join('');
    if (!html.trim()) {
      if (impressionFired) return { present: true, inferred: true, reason: 'impression confirmed fill' };
      return { present: false, reason: 'ad_html is empty — no creative HTML in response' };
    }
    let sizeKb = '?';
    try { sizeKb = (new TextEncoder().encode(html).length / 1024).toFixed(1); } catch {}
    // Tiny HTML (<300 chars) is usually a passback/beacon, not a real ad
    const tiny = html.trim().length < 300;
    return { present: true, sizeKb, tiny };
  }, [lifecycle?.phases?.gam?.row?.responseBody, lifecycle?.phases?.impression?.fired]);

  // Impression render delay — GAM request → first impression beacon
  // >30s means the ad slot was viewable but the creative took too long to paint.
  const impressionDelayMs = useMemo(() => {
    const gamTs  = lifecycle?.phases?.gam?.row?.timestamp;
    const impTs  = lifecycle?.phases?.impression?.row?.timestamp;
    if (gamTs == null || impTs == null) return null;
    const diff = Number(impTs) - Number(gamTs);
    return diff > 0 ? diff : null;
  }, [lifecycle?.phases?.gam?.row?.timestamp, lifecycle?.phases?.impression?.row?.timestamp]);

  if (!lifecycle) return null;
  const { adUnit, slotLabel, sizes, overallStatus, issues, phases, timestamp, isRefresh } = lifecycle;
  const sc = S_COLOR[overallStatus] || S_COLOR.miss;
  const fmtMs = (n) => n == null ? '—' : n >= 1000 ? `${(n/1000).toFixed(1)}s` : `${Math.round(n)}ms`;
  const fmtCpm = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;
  const fmtTime = (n) => n ? new Date(n).toTimeString().slice(0,8) : '—';
  const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

  // Computed flags used in multiple sections
  const hasFail = issues?.some(i => i.level === 'fail');
  const hasWarn = issues?.some(i => i.level === 'warn');
  // Render delay is only trustworthy when the impression↔GAM join is reliable.
  // Deterministic tiers (xai/sai/correlator) + adUnit drive the incident; a
  // nearest-time-only match is shown as "needs verification" and does NOT trigger
  // the incident, to avoid false >30s flags on repeated-iu slots.
  const impMatchConfident = ['xai', 'sai', 'impCorrelator', 'correlator', 'adUnit']
    .includes(phases?.impression?.matchTier);
  const slowOver30 = impressionDelayMs != null && impressionDelayMs > 30000;
  const hasSlowRender = slowOver30 && impMatchConfident;
  const hasSlowRenderUncertain = slowOver30 && !impMatchConfident;
  const showExport = hasFail || hasWarn || hasSlowRender;

  return (
    <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: '10px 12px', fontSize: 12 }}>

      {/* ── Compact slot header ────────────────────────────────────── */}
      <div style={{
        borderLeft: `3px solid ${sc.border}`, background: sc.bg,
        borderRadius: 8, padding: '9px 12px', marginBottom: 16,
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <span title={adUnit} style={{ fontFamily: MONO, fontWeight: 600, fontSize: 12.5, letterSpacing: '-0.1px', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{slotLabel || adUnit}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 999, flexShrink: 0, letterSpacing: '0.05em',
          background: 'transparent', border: `1px solid ${sc.border}`, color: sc.text, textTransform: 'uppercase',
        }}>{overallStatus}</span>
        {isRefresh && <span style={{ fontSize: 10, color: 'var(--muted)', border: '1px solid var(--line)', borderRadius: 999, padding: '1px 7px', flexShrink: 0 }}>refresh</span>}
        <div style={{ width: '100%', color: 'var(--muted)', fontSize: 10, marginTop: 1, fontFamily: MONO, letterSpacing: '-0.2px' }}>
          {sizes && <span>{sizes} · </span>}GAM {fmtTime(timestamp)}
        </div>
      </div>

      {/* ── Phase diagram ───────────────────────────────────────────── */}
      {(() => {
        const PHASES = [
          { key: 'prebid',     icon: '🎯', label: 'Prebid',    rowKey: phases?.prebid?.row?._k },
          { key: 'amazon',     icon: '📦', label: 'Amazon',    rowKey: phases?.amazon?.row?._k },
          { key: 'gam',        icon: '📺', label: 'GAM',       rowKey: phases?.gam?.row?._k },
          { key: 'impression', icon: '👁', label: 'Impression', rowKey: phases?.impression?.row?._k },
          { key: 'click',      icon: '👆', label: 'Click',     rowKey: phases?.click?.row?._k },
        ];
        const phaseColor = (key) => {
          const p = phases?.[key];
          if (!p?.fired) return { dot: 'var(--muted)', border: 'var(--line)', text: 'var(--muted)', sub: 'var(--muted)', dim: true };
          const warn = (key === 'prebid' && !p?.winner)
            || (key === 'amazon' && !p?.passedToGAM && p?.fired)
            || (key === 'impression' && p?.latencyMs > 30000);
          if (warn) return { dot: 'var(--warn-text)', border: 'var(--warn-border)', text: 'var(--ink)', sub: 'var(--warn-text)', dim: false };
          return { dot: 'var(--success-text)', border: 'var(--line)', text: 'var(--ink)', sub: 'var(--muted)', dim: false };
        };
        const phaseSub = (key) => {
          const p = phases?.[key];
          if (!p?.fired) return 'not seen';
          if (key === 'prebid') return p.winner ? `${p.winner.seat}` : 'no winner';
          if (key === 'amazon') return p.passedToGAM ? 'bid in GAM' : p.bidStatus === 'nobid' ? 'no-bid' : 'bid not in GAM';
          if (key === 'gam') return p.isRefresh ? `refresh` : 'initial';
          if (key === 'impression') return p.latencyMs != null ? fmtMs(p.latencyMs) : 'fired';
          if (key === 'click') return 'tracked';
          return '';
        };
        // Gap from previous fired phase — shows "+1.2s" on the connector line label
        const phaseGap = (key, i) => {
          if (i === 0) return null;
          const p = phases?.[key];
          if (!p?.fired) return null;
          // Find the last fired phase before this one
          const prevFiredKey = PHASES.slice(0, i).reverse().find(ph => phases?.[ph.key]?.fired)?.key;
          if (!prevFiredKey) return null;
          const prevTs = phases[prevFiredKey]?.row?.timestamp;
          const thisTs = p.row?.timestamp;
          if (!prevTs || !thisTs) return null;
          const gap = Number(thisTs) - Number(prevTs);
          if (gap < 0) return null;
          return gap >= 1000 ? `+${(gap / 1000).toFixed(1)}s` : `+${Math.round(gap)}ms`;
        };
        const phaseTime = (key) => {
          const p = phases?.[key];
          if (!p?.fired || !p?.row?.timestamp) return null;
          return fmtTime(p.row.timestamp);
        };
        return (
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 10, overflowX: 'auto', paddingBottom: 2 }}>
            {PHASES.map((ph, i) => {
              const c = phaseColor(ph.key);
              const pTime = phaseTime(ph.key);
              return (
                <React.Fragment key={ph.key}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div
                      onClick={() => ph.rowKey && onSwitchToDetails && onSwitchToDetails(ph.rowKey)}
                      title={ph.rowKey ? `Click to view ${ph.label} request` : `${ph.label} not captured`}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, gap: 3,
                        width: 66, padding: '8px 4px 7px', borderRadius: 9, textAlign: 'center',
                        background: 'var(--surface)', border: `1px ${c.dim ? 'dashed' : 'solid'} ${c.border}`,
                        opacity: c.dim ? 0.5 : 1,
                        cursor: ph.rowKey ? 'pointer' : 'default',
                        transition: 'border-color 0.12s, background 0.12s',
                      }}
                    >
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: c.dot }} />
                      <div style={{ fontSize: 10.5, fontWeight: 600, color: c.text }}>{ph.label}</div>
                      <div style={{ fontSize: 9, color: c.sub, lineHeight: 1.25 }}>{phaseSub(ph.key)}</div>
                    </div>
                    {pTime && (
                      <div style={{ fontSize: 8.5, color: 'var(--muted)', textAlign: 'center', marginTop: 4, fontFamily: MONO }}>{pTime}</div>
                    )}
                  </div>
                  {i < PHASES.length - 1 && (() => {
                    const nextGap = phaseGap(PHASES[i + 1].key, i + 1);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, alignSelf: 'flex-start', marginTop: 22 }}>
                        <div style={{
                          width: 14, height: 1,
                          background: phases?.[ph.key]?.fired
                            ? 'var(--success-border)'
                            : 'repeating-linear-gradient(90deg,var(--line) 0,var(--line) 3px,transparent 3px,transparent 6px)',
                        }} />
                        {nextGap && (
                          <div style={{ fontSize: 7, color: 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap' }}>{nextGap}</div>
                        )}
                      </div>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </div>
        );
      })()}

      {/* ── ALERTS (top — most actionable) ─────────────────────────── */}

      {/* Render delay */}
      {hasSlowRender && (
        <div style={{
          marginBottom: 6, padding: '7px 12px', borderRadius: 6, fontSize: 11,
          background: 'var(--warn-bg)', border: '1px solid var(--line)', color: '#c2410c',
        }}>
          ⏱ <strong>{(impressionDelayMs / 1000).toFixed(1)}s render delay</strong>
          {impressionDelayMs > 45000 ? ' — critically late (>45s)' : ' — ad took >30s to display'}
        </div>
      )}

      {/* Low-confidence render delay — surfaced for review, but does not trigger an incident */}
      {hasSlowRenderUncertain && (
        <div style={{
          marginBottom: 6, padding: '7px 12px', borderRadius: 6, fontSize: 11,
          background: 'var(--bg)', border: '1px dashed var(--line)', color: 'var(--muted)',
        }}>
          ⏱ {(impressionDelayMs / 1000).toFixed(1)}s gap to impression — <strong>needs verification</strong>: the impression was matched to this GAM by nearest-time only, so this may be mis-bound on a repeated ad unit (not counted as an incident).
        </div>
      )}

      {/* Issues */}
      {issues?.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {issues.map((iss, i) => {
            const c = S_COLOR[iss.level] || S_COLOR.miss;
            return (
              <div key={i} style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                padding: '5px 10px', marginBottom: 3, borderRadius: 6,
                background: c.bg, borderLeft: `3px solid ${c.border}`,
              }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: c.text, textTransform: 'uppercase', marginTop: 1, flexShrink: 0 }}>{iss.level}</span>
                <span style={{ color: 'var(--neutral-text)', fontSize: 11 }}>{iss.msg}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Export Incident Report */}
      {showExport && (
        <button
          onClick={() => downloadIncidentReport(lifecycle)}
          style={{
            width: '100%', padding: '7px 0', borderRadius: 6, fontSize: 11, fontWeight: 600,
            border: hasFail ? '1px solid #fca5a5' : '1px solid #fed7aa',
            background: hasFail ? 'var(--error-bg)' : 'var(--warn-bg)',
            color: hasFail ? '#b91c1c' : '#c2410c',
            cursor: 'pointer', marginBottom: 10,
          }}
          title="Export a JSON bundle with bid details, creative info, and HAR entries to escalate this issue with the partner"
        >
          📋 Export Incident Report{phases?.prebid?.winner?.seat ? ` → ${phases.prebid.winner.seat}` : ''}
        </button>
      )}

      {/* ── WP6: Mediation (inferred) — participation + outcome, never proven ── */}
      {phases?.mediation?.buyers?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
              Mediation
            </span>
            <TierBadge tier="inferred" style={{ fontSize: 9 }} />
          </div>
          <div>
            {phases.mediation.buyers.map((b, bi) => {
              const invited = !!b.reachedViaA3p;
              const fired = Array.isArray(b.rowKeys) && b.rowKeys.length > 0;
              const won = phases.mediation.winner === b.id;
              return (
                <div key={b.id} style={{
                  display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap',
                  padding: '7px 0', fontSize: 11,
                  borderTop: bi > 0 ? '1px solid var(--line)' : 'none',
                }}>
                  <span style={{ fontWeight: 600, color: 'var(--ink)', flex: 1, minWidth: 0 }}>{b.name}</span>
                  {invited && <span title="present in GAM row's a3p buyer list" style={{ fontSize: 9.5, padding: '1.5px 7px', borderRadius: 999, boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--muted)' }}>invited</span>}
                  {fired && <span title="sdk_bidding traffic observed within the timing window" style={{ fontSize: 9.5, padding: '1.5px 7px', borderRadius: 999, boxShadow: `inset 0 0 0 1px ${S_COLOR.pass.border}`, color: S_COLOR.pass.text }}>fired</span>}
                  {won && <span title="named by GAM response adapterLabel" style={{ fontSize: 9.5, padding: '1.5px 7px', borderRadius: 999, background: S_COLOR.pass.bg, color: S_COLOR.pass.text }}>won</span>}
                  <span style={{ fontSize: 10, color: 'var(--muted)', fontFamily: MONO }}>{String(b.outcome || '').replace(/_/g, ' ')}</span>
                </div>
              );
            })}
          </div>
          {phases.mediation.reasons?.length > 0 && (
            <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {phases.mediation.reasons.map((r, i) => (
                <span key={i} style={{ fontSize: 10.5, color: S_COLOR.warn.text, display: 'flex', gap: 6 }}><span aria-hidden="true">△</span>{r}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── GAM Decision — always shown when a GAM row exists ───────── */}
      {phases?.gam && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>GAM Decision</div>
          <div>
            {(() => {
              // Determine demand source label
              let demand = 'AdX / Open Auction';
              // An SDK-bidding winner named by GAM's response (ad_type "" + adapter, e.g. Meta
              // via GADMediationAdapterFacebook) or by a billing/impression beacon beats the
              // AdX default — every Meta and Liftoff win used to read "AdX / Open Auction".
              const med = phases.mediation;
              const medName = med?.winner ? ((med.buyers || []).find((b) => b && b.id === med.winner)?.name || String(med.winner)) : null;
              if (medName) demand = `SDK bidding — ${medName}`;
              else if (phases.prebid?.winner?.seat) demand = `Prebid — ${phases.prebid.winner.seat}`;
              else if (phases.amazon?.passedToGAM) demand = 'Amazon APS';
              else if (phases.gam?.hbBidder) demand = phases.gam.hbBidder;

              const impLabel = phases.impression?.fired
                ? fmtMs(phases.impression?.latencyMs) + ' impression'
                : 'impression not seen';

              const rows = [
                ['Demand', demand],
                ['Impression', impLabel],
                ['Refresh', phases.gam.isRefresh ? `#${phases.gam.refreshIndex ?? 0}` : 'Initial load'],
              ];
              if (phases.amazon?.passedToGAM && phases.amazon?.amznB) {
                rows.push(['APS token', String(phases.amazon.amznB).slice(0, 20) + '…']);
              }
              if (phases.prebid?.timeouts > 0) {
                rows.push(['Timeouts', `${phases.prebid.timeouts} bidder(s) timed out`]);
              }
              return rows.map(([label, val], ri) => (
                <div key={label} style={{
                  display: 'grid', gridTemplateColumns: '96px 1fr', gap: 10,
                  padding: '6px 0', fontSize: 11,
                  borderTop: ri > 0 ? '1px solid var(--line)' : 'none',
                }}>
                  <span style={{ color: 'var(--muted)' }}>{label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--ink)', textAlign: 'right', wordBreak: 'break-word' }}>{val}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* ── Winning Bid (Prebid only) ─────────────────────────────── */}
      {phases.prebid?.winner && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Winning Bid</span>
            {phases.prebid.winner.seat && <span style={{ fontSize: 9.5, color: S_COLOR.pass.text, fontWeight: 600 }}>· {phases.prebid.winner.seat} won</span>}
          </div>
          <div>
            {[
              ['Bidder', phases.prebid.winner.seat || '—'],
              ['CPM', fmtCpm(phases.prebid.winner.price)],
              ['hb_pb', phases.prebid.winner.hb_pb && phases.prebid.winner.hb_pb !== '-' ? phases.prebid.winner.hb_pb : '—'],
              ['Size', phases.prebid.winner.size || '—'],
              ['Latency', fmtMs(phases.prebid.winner.latencyMs)],
              ['Deal', phases.prebid.winner.dealid && phases.prebid.winner.dealid !== '-' ? phases.prebid.winner.dealid : 'none'],
            ].map(([label, val], ri) => (
              <div key={label} style={{
                display: 'grid', gridTemplateColumns: '96px 1fr', gap: 10,
                padding: '6px 0', fontSize: 11,
                borderTop: ri > 0 ? '1px solid var(--line)' : 'none',
              }}>
                <span style={{ color: 'var(--muted)' }}>{label}</span>
                <span style={{ fontWeight: 600, color: 'var(--ink)', fontFamily: MONO, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{val}</span>
              </div>
            ))}
            {phases.prebid.tmax && (
              <div style={{ padding: '4px 10px', fontSize: 10, color: 'var(--muted)', borderTop: '1px solid var(--line)' }}>
                tmax: {phases.prebid.tmax}ms
                {phases.prebid.timeouts > 0 && <span style={{ color: S_COLOR.warn.text }}> · {phases.prebid.timeouts} timeout{phases.prebid.timeouts > 1 ? 's' : ''}</span>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Creative status ─────────────────────────────────────────── */}
      {phases?.gam?.row && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Creative</div>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            background: adHtmlStatus.present
              ? (adHtmlStatus.inferred ? 'var(--bg-raised)' : (adHtmlStatus.tiny ? S_COLOR.warn.bg : S_COLOR.pass.bg))
              : S_COLOR.warn.bg,
            border: `1px solid ${adHtmlStatus.present
              ? (adHtmlStatus.inferred ? 'var(--line)' : (adHtmlStatus.tiny ? S_COLOR.warn.border : S_COLOR.pass.border))
              : S_COLOR.warn.border}`,
            borderRadius: 6, padding: '8px 12px',
          }}>
            <span style={{
              fontSize: 11, flex: 1, minWidth: 0,
              color: adHtmlStatus.present
                ? (adHtmlStatus.inferred ? 'var(--neutral-text)' : (adHtmlStatus.tiny ? S_COLOR.warn.text : S_COLOR.pass.text))
                : S_COLOR.warn.text,
            }}>
              {adHtmlStatus.present
                ? adHtmlStatus.inferred
                  ? `✓ Fill confirmed via impression beacon`
                  : adHtmlStatus.tiny
                    ? `⚠ ad_html tiny (${adHtmlStatus.sizeKb}KB) — may be passback`
                    : `✓ ad_html present (${adHtmlStatus.sizeKb}KB)`
                : `⚠ ${adHtmlStatus.reason}`}
            </span>
            {/* Always show Inspect button for GAM rows — opens GAM Request Inspector */}
            <button
              onClick={() => setDebugOpen(true)}
              style={{
                fontSize: 11, padding: '3px 10px', borderRadius: 6, flexShrink: 0,
                border: '1px solid var(--primary)', background: 'transparent',
                color: 'var(--primary)', cursor: 'pointer', fontWeight: 600,
              }}
            >🔍 Inspect</button>
          </div>
        </div>
      )}

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <button
        onClick={onSwitchToDetails}
        style={{
          width: '100%', padding: '7px 0', borderRadius: 6, fontSize: 11, fontWeight: 600,
          border: '1px solid var(--line)', background: 'transparent', color: 'var(--muted)',
          cursor: 'pointer',
        }}
      >
        View raw request →
      </button>

      {/* Creative debugger modal — opens on "Debug creative" button click */}
      {debugOpen && phases?.gam?.row && (
        <AdDebugModal
          open={debugOpen}
          onClose={() => setDebugOpen(false)}
          req={phases.gam.row}
        />
      )}
    </div>
  );
}

/* ---------------- main component ---------------- */

/* ============================================================================
   Ad Server Integration card
   Renders the GMA SDK Publisher Settings (getconfig/pubsetting) summary —
   what mediation / secure-signals adapters and ad-unit mappings the app is
   currently integrated with on the ad server. Fires once per app launch; the
   latest snapshot replaces the prior one (handled upstream in MainApp).
   ========================================================================== */
// `serverMediationHealth` (WP4c) is the proxy's own rollup, fetched once by the
// parent — see its POST there for why the declared lists travel in the request.
// Null means the proxy had no answer (unreachable, or it threw on this one
// rollup), in which case the card computes locally and says so. Never merged.
function AdServerIntegrationCard({ data, rows = [], onOpenRequest, serverMediationHealth = null }) {
  const [open, setOpen] = useState(true);
  const [showUnits, setShowUnits] = useState(false);
  if (!data) return null;

  // The raw publisher_settings (getconfig/pubsetting) request behind this card —
  // latest wins. Clicking the header opens it in the right Inspect panel.
  const sourceRow = (() => {
    const list = Array.isArray(rows) ? rows : [];
    for (let i = list.length - 1; i >= 0; i--) {
      const r = list[i];
      if (r?.requestType === 'publisher_settings' || r?.type === 'publisher_settings') return r;
    }
    return null;
  })();
  const canOpen = !!(onOpenRequest && sourceRow);

  const {
    appId, adapters = [], adUnits = [], secureSignalAdapters = [],
    permissions = [], cacheTtlHuman, capturedAt, autoCollectLocation,
  } = data;

  // Observed secure signals: decode of the a3p param on the most recent GAM
  // (/gampad/ads) request. Publisher Settings only DECLARES intent — a3p is the
  // proof the signal actually reached the ad server.
  const observed = (() => {
    const list = Array.isArray(rows) ? rows : [];
    for (const r of list) {
      const isGam = r?.requestType === 'google' || r?.type === 'google';
      const ss = r?.validData?.secureSignals || r?.validData?.expanded?.secureSignals;
      if (isGam && ss) return ss;
    }
    return null;
  })();
  // Only UID2 / LiveRamp travel through a3p in our setup — compare against those.
  const trackable = secureSignalAdapters.filter(n => /uid2|liveramp/i.test(n));

  // SDK Bidding reconciliation: declared buyers (mediation adapters that are
  // third-party SDK-Bidding buyers) vs buyers actually observed firing in the
  // captured sdk_bidding traffic. Always-on equivalent of Ad Inspector's
  // per-source ("single ad source") check.
  const usingServerMediation = !!serverMediationHealth;
  const mediationHealth = serverMediationHealth || buildMediationHealth(rows, adapters);

  const capturedLabel = capturedAt
    ? new Date(capturedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
    : null;

  const chip = (bg, color, border) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
    background: bg, color, border,
  });

  return (
    <div style={{
      margin: "8px 8px 0", border: "1px solid var(--line)", borderRadius: 10,
      background: "var(--neutral-bg)", overflow: "hidden",
    }}>
      {/* Header — click opens the underlying publisher_settings request in the
          right Inspect panel; the chevron toggles this card's collapse. */}
      <div
        onClick={() => { if (canOpen) onOpenRequest(sourceRow); }}
        title={canOpen ? "Open the GMA Publisher Settings request in the Inspect panel" : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 8, cursor: canOpen ? "pointer" : "default",
          padding: "8px 12px", userSelect: "none",
        }}
      >
        <span style={{ fontSize: 14 }}>🔌</span>
        <span style={{ fontWeight: 800, fontSize: 13, color: "var(--neutral-text)" }}>
          Ad Server Integration
        </span>
        <span style={{ fontSize: 11, color: "var(--muted)" }}>
          GMA Publisher Settings · once per launch
        </span>
        {canOpen && (
          <span style={{ fontSize: 11, color: "var(--primary)", fontWeight: 700 }}>Inspect →</span>
        )}
        <span style={{ flex: 1 }} />
        {capturedLabel && (
          <span style={{ fontSize: 11, color: "var(--muted)" }} title="Replaced each app relaunch">
            updated {capturedLabel}
          </span>
        )}
        <span
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          title={open ? "Collapse" : "Expand"}
          style={{ fontSize: 11, color: "var(--muted)", cursor: "pointer", padding: "0 4px" }}
        >{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          {/* App ID */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "var(--muted)", minWidth: 64 }}>App ID</span>
            <code style={{ fontSize: 12, color: "var(--neutral-text)" }}>{appId || "—"}</code>
          </div>

          {/* Mediation / secure-signals adapters */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "var(--muted)", minWidth: 64, paddingTop: 2 }}>
              Adapters
            </span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
              {adapters.length === 0 && <span style={{ fontSize: 12, color: "var(--muted)" }}>None</span>}
              {adapters.map((a, i) => (
                <span key={i} style={chip("var(--neutral-bg)", "var(--neutral-text)", "1px solid var(--neutral-border)")}>
                  {a.name}
                  {a.placementCount > 0 && (
                    <span style={{ color: "var(--muted)", fontWeight: 600 }}>·{a.placementCount}</span>
                  )}
                  {a.secureSignals && (
                    <span title="Collects secure signals" style={{ color: "var(--success-text)" }}>🔒</span>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* Secure signals — configured (declared) */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "var(--muted)", minWidth: 64 }}>Configured</span>
            {secureSignalAdapters.length > 0 ? (
              <span style={chip("var(--success-bg)", "var(--success-text)", "1px solid var(--success-border)")}>
                🔒 {secureSignalAdapters.join(", ")}
              </span>
            ) : (
              <span style={chip("var(--warn-bg)", "var(--warn-text)", "1px solid var(--warn-border)")}>
                none declared
              </span>
            )}
          </div>

          {/* Secure signals — observed in a3p on the GAM request */}
          {trackable.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "var(--muted)", minWidth: 64 }}>
                In a3p
              </span>
              {!observed ? (
                <span style={{ fontSize: 11, color: "var(--muted)" }}>no ad request captured yet</span>
              ) : !observed.present ? (
                // a3p reaching GAM is a PER-REQUEST property — the authoritative
                // pass/fail lives on the specific GAM ad request, now via the
                // Checks engine (baseline-a3p-decodes). Keep this global config
                // card neutral so it doesn't read as a config-level failure.
                <span style={chip("var(--neutral-bg)", "var(--muted)", "1px solid var(--neutral-border)")}
                  title="Secure signals (a3p) are checked per GAM ad request by the Checks rules (baseline-a3p-decodes), not at config level.">
                  not seen on latest GAM request · checked per-request in Checks
                </span>
              ) : (
                trackable.map((n, i) => {
                  const ok = (observed.detected || []).includes(n);
                  return (
                    <span key={i} style={ok
                      ? chip("var(--success-bg)", "var(--success-text)", "1px solid var(--success-border)")
                      : chip("var(--error-bg)", "var(--error-text)", "1px solid var(--error-border)")}>
                      {ok ? "✓" : "✗"} {n}
                    </span>
                  );
                })
              )}
            </div>
          )}

          {/* SDK Bidding — third-party buyers (declared vs observed) */}
          {mediationHealth && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "var(--muted)", minWidth: 64 }}>SDK Bidding</span>
                <span style={(() => {
                  const t = mediationHealth.overall;
                  if (t === "fail") return chip("var(--error-bg)", "var(--error-text)", "1px solid var(--error-border)");
                  if (t === "warn") return chip("var(--warn-bg)", "var(--warn-text)", "1px solid var(--warn-border)");
                  return chip("var(--success-bg)", "var(--success-text)", "1px solid var(--success-border)");
                })()}>
                  {mediationHealth.observedCount}/{Math.max(mediationHealth.declaredCount, mediationHealth.observedCount)} buyers active
                </span>
                <span style={{ fontSize: 11, color: "var(--muted)" }}
                  title="Third-party buyers that bid and render via their own SDK (GAM SDK Bidding). Compares what Publisher Settings declares against buyers actually observed firing — the always-on equivalent of Ad Inspector single ad source.">
                  declared vs observed · {mediationHealth.total} request(s)
                </span>
                {/* WP4c: the fallback announces itself — a verdict computed in
                    the browser can disagree with the proxy's, and the reader is
                    entitled to know which one they are looking at. */}
                {!usingServerMediation && (
                  <span
                    title="The proxy did not answer, so this rollup was computed in the browser. It may differ from what agents and CI report for the same session."
                    style={{
                      fontSize: 11, fontWeight: 400, color: '#854F0B',
                      border: '1px solid #E5C07B', borderRadius: 4, padding: '1px 6px',
                    }}
                  >
                    computed locally
                  </span>
                )}
              </div>

              {mediationHealth.buyers.length === 0 ? (
                <span style={{ fontSize: 11, color: "var(--muted)", paddingLeft: 72 }}>
                  No SDK-Bidding buyers declared or observed yet.
                </span>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 0 }}>
                  {mediationHealth.buyers.map((b, i) => {
                    const meta = mediationStatusMeta(b.status);
                    const toneChip = {
                      success: chip("var(--success-bg)", "var(--success-text)", "1px solid var(--success-border)"),
                      warn:    chip("var(--warn-bg)", "var(--warn-text)", "1px solid var(--warn-border)"),
                      error:   chip("var(--error-bg)", "var(--error-text)", "1px solid var(--error-border)"),
                      neutral: chip("var(--neutral-bg)", "var(--muted)", "1px solid var(--neutral-border)"),
                    }[meta.tone];
                    return (
                      <div key={i} style={{
                        display: "flex", flexDirection: "column", gap: 3,
                        fontSize: 11, padding: "4px 8px", borderRadius: 6,
                        background: "var(--neutral-bg)", border: "1px solid var(--neutral-border)",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 700, color: "var(--neutral-text)", minWidth: 150 }}>{b.name}</span>
                          <span style={toneChip}>{meta.label}</span>
                          {b.declared
                            ? <span style={{ color: "var(--muted)" }} title="Declared in Publisher Settings">declared</span>
                            : <span style={{ color: "var(--warn-text)" }} title="Observed firing but not declared in Publisher Settings">not declared</span>}
                          <span style={{ flex: 1 }} />
                          {b.bids > 0 && <span style={{ color: "var(--muted)" }}>bids {b.bids}</span>}
                          {b.fills > 0 && <span style={{ color: "var(--success-text)" }}>fill {b.fills}</span>}
                          {b.noFills > 0 && <span style={{ color: "var(--warn-text)" }}>no-fill {b.noFills}</span>}
                          {b.errors > 0 && <span style={{ color: "var(--error-text)" }}>SDK errors {b.errors}</span>}
                          {b.telemetry > 0 && <span style={{ color: "var(--muted)" }} title="SDK telemetry/metrics — buyer SDK is initialized and active">active {b.telemetry}</span>}
                          {b.events > 0 && <span style={{ color: "var(--muted)" }}>events {b.events}</span>}
                          {b.placements.length > 0 && (
                            <span style={{ color: "var(--muted)" }} title={b.placements.join(", ")}>
                              · {b.placements.length} placement{b.placements.length > 1 ? "s" : ""}
                            </span>
                          )}
                          {b.signal && b.signal.status !== "unknown" && (
                            b.signal.status === "reaching"
                              ? <span style={{ color: "var(--success-text)" }}
                                      title={`Buyer's secure-signal token found in the decoded a3p blob on ${b.signal.reaching}/${b.signal.checked} GAM request(s)`}>
                                  a3p ✓
                                </span>
                              : <span style={{ color: "var(--warn-text)" }}
                                      title={b.signal.status === "no_a3p"
                                        ? `No a3p param on any of ${b.signal.checked} GAM request(s) — no secure signals transmitting at all`
                                        : `a3p present on GAM requests but this buyer's token was not detected (matcher may be unverified — checked ${b.signal.checked} request(s))`}>
                                  a3p ✗
                                </span>
                          )}
                        </div>
                        {b.messages.length > 0 && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingLeft: 2 }}>
                            {b.messages.map((m, k) => (
                              <span key={k} style={{ color: "var(--error-text)", fontSize: 10.5 }} title="Reported by the buyer SDK">
                                ⚠ {m}
                              </span>
                            ))}
                          </div>
                        )}
                        {b.verdictHint && (
                          <span style={{ fontSize: 10.5, color: "var(--warn-text)", paddingLeft: 2 }}
                                title="Automatic fault routing: buyer SDK error cross-referenced with secure-signal (a3p) delivery">
                            {b.verdictHint === "adapter"
                              ? "→ secure signal IS reaching GAM — fault points at the adapter loadAd call (check ad-unit mapping, adapter/SDK versions, bid-token timing)"
                              : "→ secure signal NOT reaching GAM — fix upstream signal collection before touching the adapter (secure signals enabled? collector registered?)"}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  <span style={{ fontSize: 10, color: "var(--muted)" }}>
                    “not observed” = declared but its SDK never fired (check init/adapter). “undeclared” = firing but missing from Publisher Settings.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Publisher permissions (EOID) + cache TTL */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "var(--muted)", minWidth: 64 }}>Permissions</span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {permissions.map((p, i) => (
                <span key={i} style={chip("var(--neutral-bg)", "var(--neutral-text)", "1px solid var(--neutral-border)")}>
                  {p.platform}: EOID {p.eoidEnabled ? "on" : "off"}
                  {p.sameAppKeyOptOut && <span style={{ color: "var(--warn-text)" }}>· SAK opt-out</span>}
                </span>
              ))}
              {cacheTtlHuman && (
                <span style={chip("var(--neutral-bg)", "var(--muted)", "1px solid var(--neutral-border)")}>
                  cache {cacheTtlHuman}
                </span>
              )}
              {autoCollectLocation && (
                <span style={chip("var(--warn-bg)", "var(--warn-text)", "1px solid var(--warn-border)")}>
                  auto-collect location
                </span>
              )}
            </div>
          </div>

          {/* Ad unit → mediation mapping (expandable) */}
          <div>
            <button
              onClick={() => setShowUnits(s => !s)}
              style={{
                background: "transparent", border: "none", cursor: "pointer", padding: 0,
                fontSize: 11, color: "var(--muted)", fontWeight: 700,
              }}
            >
              {showUnits ? "▾" : "▸"} Ad units & mediation mapping ({adUnits.length})
            </button>
            {showUnits && (
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                {adUnits.map((u, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                    fontSize: 11, padding: "4px 8px", borderRadius: 6,
                    background: "var(--neutral-bg)", border: "1px solid var(--neutral-border)",
                  }}>
                    <code style={{ color: "var(--neutral-text)" }}>{u.adUnitId}</code>
                    {u.format && <span style={{ color: "var(--muted)" }}>{u.format}</span>}
                    {(u.networks || []).map((n, j) => (
                      <span key={j} style={{ color: "var(--muted)" }}>
                        {(n.adapters || []).join("/")}
                        {n.mappingId != null && <span> · map {n.mappingId}</span>}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Friendly names for the single-ad-source restricted_to_buyer_network codes
// (confirmed 2026-07-05). Keeps the 🧪 badge readable instead of raw numeric
// codes; unknown codes fall through to the code itself.
const SAS_CODE_NAMES = {
  '1': 'AdX',
  '1362416': 'Liftoff bidding',
  '1418116': 'Meta bidding',
  '1953547073528090325': 'Liftoff waterfall',
  '10568273599589928883': 'Meta waterfall',
};

/* ── TrafficEmptyState — first-run / no-traffic experience ─────────────────
   Two cases: nothing captured yet (guide the user to connect a device) vs
   captured-but-filtered-out (offer to clear filters). Replaces the bare
   "No requests match the current filters." cell. */
function TrafficEmptyState({ filtered, onConnect, onClearFilters, serverIp, paused, filterSummary = null }) {
  const primaryBtn = {
    padding: '9px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
    background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8,
  };
  const ghostBtn = {
    padding: '7px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
    background: 'transparent', color: 'var(--primary)', border: '1px solid var(--line)', borderRadius: 8,
  };

  if (filtered) {
    return (
      <div style={{ padding: '44px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>No requests match your filters</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, lineHeight: 1.5 }}>
          There’s captured traffic here — the current search / status / group filters just hide all of it.
          {filterSummary && filterSummary.parts.length > 0 && (
            <div style={{ marginTop: 6 }}>Active: {filterSummary.parts.join(' · ')}</div>
          )}
          {filterSummary && filterSummary.excludeActive && (
            <div style={{ marginTop: 6, color: 'var(--warn-text)' }}>
              An exclude list is also active ({filterSummary.excludeCount} host pattern{filterSummary.excludeCount === 1 ? '' : 's'}) — Clear filters does not change it.
            </div>
          )}
        </div>
        <button onClick={onClearFilters} style={ghostBtn}>Clear filters</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '56px 24px', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
      <svg width="54" height="54" viewBox="0 0 48 48" fill="none" style={{ marginBottom: 18 }} aria-hidden="true">
        <rect x="16" y="9" width="15" height="28" rx="3" stroke="var(--primary)" strokeWidth="2" />
        <line x1="20.5" y1="33" x2="26.5" y2="33" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
        <path d="M34.5 17 a9 9 0 0 1 0 14" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" opacity="0.75" />
        <path d="M37.5 13 a14 14 0 0 1 0 22" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
      </svg>
      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 8, letterSpacing: '-0.01em' }}>
        {paused ? 'Capture is paused' : 'Waiting for ad traffic'}
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--muted)', marginBottom: 22 }}>
        Point a device at the proxy and start browsing. GAM, Prebid, Amazon APS, SpringServe, IMA/DAI and OMID
        requests stream in here live and get validated automatically as they fire.
      </div>
      <button onClick={onConnect} style={primaryBtn}>Connect a device</button>
      <div style={{ marginTop: 16, fontSize: 11.5, color: 'var(--muted)', display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: paused ? 'var(--warn-text, #b0761a)' : '#22c55e', display: 'inline-block' }} />
          {paused ? 'Capture paused' : 'Proxy connected'}
        </span>
        <span style={{ opacity: 0.55 }}>·</span>
        <span style={{ fontFamily: 'var(--font-mono, monospace)', opacity: 0.85 }}>{serverIp}</span>
      </div>
    </div>
  );
}

// Stamp _k and page-visit fields (_visitKey/_visitIdx/_visitReason/_pageUrl) on
// every row. Pure; the `allRowsStamped` memo below and the Slots view's
// All-devices scope (run once per device) both use it. `keyPrefix` keeps keys and
// visit keys distinct across devices so their lifecycles never merge.
export function stampVisits(data, groupByPage, keyPrefix = '') {
  const withKeys = (data || []).map((r, idx) => ({ ...r, _k: r.requestId ?? r.id ?? (keyPrefix ? `${keyPrefix}#${idx}` : idx) }));
  if (!groupByPage) return withKeys;

  const chronological = [...withKeys].sort((a, b) => ts(a) - ts(b));
  const pageMap = new Map(); // pageKey → { visits: [{idx, reason}], lastSeen }
  let lastNonEmptyKey = null;
  let foregroundKey = null;     // fresh-load page (see `groups` block) — return detection
  let prevForegroundKey = null; // anti-flap
  let foregroundSince = 0;      // anti-flap

  for (const r of chronological) {
    // Mirror the `groups` page-identity logic: pageName cust_param first
    // (reliable on iOS in-app), content_url fallback, auctions/beacons inherit.
    const ident   = getPageIdentity(r);
    const raw      = ident?.key   || '';
    const rawLabel = ident?.label || '';
    const key = raw || lastNonEmptyKey || '(unknown)';

    if (!pageMap.has(key)) pageMap.set(key, { visits: [], lastSeen: 0, label: rawLabel });
    const g = pageMap.get(key);
    if (rawLabel && !g.label) g.label = rawLabel;

    const isAd      = isAdRequestAny(r);
    const isTracker = (r.url || '').includes('/view') || (r.url || '').includes('/pcs/') || r.type === 'img';
    const threshold = (isAd || isTracker) ? 120000 : 30000;
    const isGAMRefresh = !!(r.validData?.expanded?.isRefresh)
      || (() => { try { return !!new URL(r.url).searchParams.get('prev_scp'); } catch { return false; } })();
    const serverVisitChange = r.visitChanged === true;
    // Match the `groups` block's guard exactly so the two views can't diverge on
    // zero/missing-timestamp rows.
    const timeGap = g.visits.length > 0 ? ts(r) - g.lastSeen : 0;
    // Return-to-page: fresh load (not a refresh) for a page after a DIFFERENT page,
    // with the same anti-flap guard as the `groups` block so the lifecycle source
    // segments visits identically.
    const freshLoad = !!raw && !isGAMRefresh;
    const isFlapBack = freshLoad && raw === prevForegroundKey && (ts(r) - foregroundSince) < CONTEXT_FLAP_MS;
    const isContextSwitch = freshLoad && !!foregroundKey && raw !== foregroundKey && !isFlapBack;
    const splitOnContext   = isContextSwitch;

    const shouldSplit = g.visits.length === 0
      || serverVisitChange
      || (!isGAMRefresh && timeGap > threshold)
      || splitOnContext;

    if (shouldSplit) {
      const reason = g.visits.length === 0 ? 'first'
        : serverVisitChange ? 'navigation'
        : splitOnContext    ? 'returned'
        : 'gap';
      g.visits.push({ idx: g.visits.length + 1, reason });
    }
    const curVisit = g.visits[g.visits.length - 1];
    r._visitKey    = `${keyPrefix ? `${keyPrefix}|` : ''}${key}#v${curVisit.idx}`;
    r._visitIdx    = curVisit.idx;
    r._visitReason = curVisit.reason;
    r._pageUrl     = key; // content_url drives the lifecycle visit header (host + path)
    g.lastSeen = ts(r);
    if (raw) lastNonEmptyKey = raw;
    if (raw) {
      if (foregroundKey === null) { foregroundKey = raw; foregroundSince = ts(r); }
      else if (freshLoad && raw !== foregroundKey && isContextSwitch) {
        prevForegroundKey = foregroundKey; foregroundKey = raw; foregroundSince = ts(r);
      }
    }
  }
  return withKeys;
}

/* ── Pure helpers (exported for tests) ──────────────────────────────────── */

/** Status-code buckets shown by default (all on). Fresh copy per use. */
export const ALL_STATUS_BUCKETS = { s0: true, s1: true, s2: true, s3: true, s4: true, s5: true };

/**
 * Which row filters are currently hiding traffic. Drives "Clear filters" and the
 * empty-state copy; excludeCount is reported but is persisted config, not
 * something Clear filters resets.
 */
export function activeFilterSummary({ query = '', statusFilter = 'all', show = ALL_STATUS_BUCKETS, stepFilter = 'all', hideTrackers = false, typeFilter = null, excludeCount = 0 } = {}) {
  const parts = [];
  if (String(query).trim()) parts.push('search');
  if (statusFilter !== 'all') parts.push(`status: ${statusFilter}`);
  if (Object.keys(ALL_STATUS_BUCKETS).some((k) => show[k] === false)) parts.push('status-code buckets');
  if (stepFilter !== 'all') parts.push(`step: ${stepFilter}`);
  if (hideTrackers) parts.push('Hide trackers');
  if (typeFilter) parts.push(`type: ${typeFilter}`);
  return { parts, excludeActive: excludeCount > 0, excludeCount };
}

/** Message shown in place of a row click's lifecycle when the logcat row cannot act. */
export const LOGCAT_NO_BODY_REASON =
  'USB (logcat) capture is request-only: the request body and response were never seen and the URL may be truncated, so this action is unavailable. Capture through the proxy to use it.';

/** null when the row can run Replay / Diff / Debug, else the reason it cannot. */
export function logcatActionBlockReason(row) {
  return row && row.captureSource === 'logcat' ? LOGCAT_NO_BODY_REASON : null;
}

/** The lifecycle (from a server or local list) whose GAM request is this row. */
export function findLifecycleForRow(lifecycles, rowKey) {
  if (!Array.isArray(lifecycles) || rowKey == null) return null;
  return lifecycles.find((l) => l && (l.phases?.gam?.rowKey === rowKey || l.key === rowKey)) || null;
}

/**
 * Where does a jump target live?
 *  visible     - in the filtered table
 *  filtered    - on this device but hidden by filters (clear them)
 *  otherDevice - on another device tab (switch device, then retry)
 *  missing     - nowhere we can see (yet)
 */
export function resolveJumpTarget(key, { rows = [], allRowsStamped = [], allDevicesStamped = null, currentDevice = null } = {}) {
  const visible = rows.find((r) => r._k === key);
  if (visible) return { kind: 'visible', row: visible };
  const onDevice = allRowsStamped.find((r) => r._k === key);
  if (onDevice) return { kind: 'filtered', row: onDevice };
  const other = Array.isArray(allDevicesStamped) ? allDevicesStamped.find((r) => r._k === key) : null;
  if (other && other._deviceTab && other._deviceTab !== currentDevice) {
    return { kind: 'otherDevice', row: other, device: other._deviceTab };
  }
  return { kind: 'missing', row: null };
}

export default function InspectraNetworkView({
  data = [],
  onRowOpen,
  configAttributesGlobal = {},
  publisherSettings = null,
  paused = false,
  suppressed = 0,
  onTogglePause = () => { },
  serverIp = "localhost",
  onClear,
  readonly = false,
  tlsDrops = [],
  typeFilter = null,
  onTypeFilterChange = () => { },
  allDevicesData = null,   // every device's rows (MainApp), for Slots' All-devices scope
  currentDevice = null,
  onSelectDevice = null,
  slotLink = null,         // ?slot= deep link from MainApp: { key, at } -> open Slots with that slot expanded
  onSlotLinkDone = null,   // called once the linked slot resolved (MainApp then clears ?slot= from the URL)
  harId = null,            // id of the HAR being viewed (null on the live session), for ticket links
}) {
  // clearSignal from context — increments when user hits "Clear All Requests"
  const { clearSignal, jumpTarget, setJumpTarget, setChecksScopeRows, startNewSession } = useTraffic();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  // Defer expensive list re-filter (2,900+ rows → 150ms+ input handler violation per keystroke).
  // Input stays live on `query`; the memo lags behind on `deferredQuery`, so UI is responsive.
  const deferredQuery = useDeferredValue(query);
  // Dropped/pinned-host banner: dismissable, reappears when new hosts drop.
  const [dropsDismissedAt, setDropsDismissedAt] = useState(0);
  const [sel, setSel] = useState(null);
  const [show, setShow] = useState({ s0: true, s1: true, s2: true, s3: true, s4: true, s5: true });
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'fail' | 'warn' | 'pass'
  const [stepFilter, setStepFilter] = useState("all"); // 'all' | '(untagged)' | <row.step value>
  const [hideTrackers, setHideTrackers] = useState(true);
  // "Ad requests only" (Raw requests tab; persisted as requests.adOnly, fails open to ON).
  // A jump to a hidden row lifts it for this session only (setAdOnly without a write).
  const [adOnly, setAdOnly] = useState(readRequestsAdOnly);
  const toggleAdOnly = (next) => { setAdOnly(next); writeRequestsAdOnly(next); };
  // Representative keys of merged billing-ping groups the user expanded.
  const [expandedBills, setExpandedBills] = useState(() => new Set());
  const [excludeList] = useState(() => loadExcludeList());
  const [showExcludePopover, setShowExcludePopover] = useState(false);
  const excludePopoverRef = useRef(null);

  // View mode: default to the per-slot story (docs/slot-story-spec.md)
  const [viewMode, setViewMode] = useState('slots');
  const [focusSlot, setFocusSlot] = useState(null); // Overview -> Slots: open this slot
  // ?slot= deep link: switch to Slots and focus it (SlotStoryView shows the
  // "not in the current session" banner when the key does not resolve).
  useEffect(() => {
    if (slotLink && slotLink.key != null) {
      setViewMode('slots');
      setFocusSlot({ key: slotLink.key, at: slotLink.at || Date.now() });
    }
  }, [slotLink]);
  // Value never read — only the setter matters (it re-runs the jump effect).
  const [, setJumpedFromLifecycle] = useState(false);
  // Right panel tabs: 'summary' = slot lifecycle overview, 'details' = raw request
  const [rightTab, setRightTab] = useState('details');
  // Key of a row to scroll into view after a jump from lifecycle/video views
  const [scrollToKey, setScrollToKey] = useState(null);
  // Full lifecycle object for the currently selected slot (set when phase node is clicked)
  const [selLifecycle, setSelLifecycle] = useState(null);
  // Full video session object for the right-panel video summary (video view)
  // Store only the KEY of the selected video session, then re-resolve the LIVE
  // session each render — so the right-panel summary updates as new beacons stream
  // in (instead of freezing a stale snapshot from when the card was clicked).
  const [selVideoSessionKey, setSelVideoSessionKey] = useState(null);

  // Group by page with visit (revisit) subgroups
  const [groupByPage, setGroupByPage] = useState(true);
  const [collapsed, setCollapsed] = useState({}); // group key -> bool
  const [collapsedVisit, setCollapsedVisit] = useState({}); // `${groupKey}#v${visitIdx}` -> bool

  // Selection for compare (max 2, same host)
  const [picked, setPicked] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [importedCompare, setImportedCompare] = useState(null); // { a, b } from JSON import
  // Diff-vs-pasted for the currently selected row (detail-pane action, not the picked-pair Compare flow)
  const [diffToolOpen, setDiffToolOpen] = useState(false);
  // Replay for the currently selected row (detail-pane action)
  const [replayOpen, setReplayOpen] = useState(false);
  const importInputRef = useRef(null);
  const tableBodyRef   = useRef(null);

  const [connectOpen, setConnectOpen] = useState(false);
  // The isolation notices (components/IsolationNotices.jsx) are mounted app-wide and
  // cannot reach this state, so their "Connect Device" CTA fires this window event.
  useEffect(() => {
    const open = () => setConnectOpen(true);
    window.addEventListener('adlens:open-connect-device', open);
    return () => window.removeEventListener('adlens:open-connect-device', open);
  }, []);
  // Sign-off report notice: { severity: 'error'|'info', text } | null. Replaces
  // the blocking native dialogs that used to report why report generation aborted —
  // same messages, same abort, non-blocking delivery (mirrors LaunchPage's
  // attachWarning). Cleared on each new attempt so a stale notice can't linger.
  const [signOffNotice, setSignOffNotice] = useState(null);
  // Provenance of selLifecycle when a row click resolved it: 'server' | 'local'
  // (null = set by a child view, which announces its own source).
  const [selLifecycleSource, setSelLifecycleSource] = useState(null);
  const selKeyRef = useRef(null);
  // One server lifecycle fetch per row-set; reused by every GAM row click.
  const lcGraphCache = useRef({ rows: null, promise: null });
  const getServerLifecycles = (rowsArg) => {
    const c = lcGraphCache.current;
    if (c.rows === rowsArg && c.promise) return c.promise;
    const promise = postLifecycleGroups(rowsArg, { manualOverrides: {} })
      .then((res) => (Array.isArray(res?.lifecycles) ? res.lifecycles : Promise.reject(new Error('no lifecycles in response'))));
    lcGraphCache.current = { rows: rowsArg, promise };
    promise.catch(() => { if (lcGraphCache.current.promise === promise) lcGraphCache.current = { rows: null, promise: null }; });
    return promise;
  };

  // Reset right panel and selection when requests are cleared
  useEffect(() => {
    if (clearSignal === 0) return; // skip initial mount
    setSel(null);
    setSelLifecycle(null);
    setPicked([]);
  }, [clearSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePick = (row, key) => {
    setPicked((cur) => {
      const has = cur.some((r) => (r.requestId ?? r.id ?? r._k) === key);
      if (has) return cur.filter((r) => (r.requestId ?? r.id ?? r._k) !== key);
      return [...cur, row]; // unlimited — used for both Compare (first 2) and HAR export
    });
  };
  const clearPicked = () => setPicked([]);

  // Download all picked rows as a single HAR file
  const downloadPickedHar = async () => {
    if (!picked.length) return;
    const rows = await withStashedBodies(picked);
    // One HAR builder for every export path (same entries + `_inspectra` as the
    // HAR saved to the backend) so a downloaded file re-imports like a saved one.
    const har = buildHarFromRequests(rows, { title: `${picked.length} selected requests`, globalConfig: configAttributesGlobal });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const blob = new Blob([JSON.stringify(har, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `fx-validator-${picked.length}requests-${ts}.har`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Build a validation index: status by row id + summary counts.
  // docs/validate-ad-only-spec.md: ad-scoped — a non-ad row (non_ad/analytics/
  // consent) gets no map entry and doesn't count toward the tallies below. This
  // is also the perf win the spec calls out: runValidations() only runs on the
  // ad-relevant subset (isAdRelevantRow — see its definition/comment near the
  // top of this file), not every captured row.
  const validationIndex = useMemo(() => {
    const map = new Map();
    let pass = 0, warn = 0, fail = 0;

    (data || []).forEach((r, idx) => {
      if (!isAdRelevantRow(r)) return;
      const key = r.requestId ?? r.id ?? idx;
      try {
        const v = runValidations(r, r.configAttributes || {});
        const status = v.summary.overall; // pass|warn|fail
        map.set(key, status);
        if (status === "fail") fail++;
        else if (status === "warn") warn++;
        else pass++;
      } catch {
        map.set(key, "fail");
        fail++;
      }
    });

    const overall = fail > 0 ? "fail" : warn > 0 ? "warn" : "pass";
    const total = pass + warn + fail;
    return { map, counts: { pass, warn, fail, total }, overall };
  }, [data]);

  // Secure-signal adapters DECLARED in Publisher Settings that travel via a3p
  // (only UID2 / LiveRamp reach GAM through a3p in our setup). Used to build the
  // Secure Signals coverage rollup shown in the lifecycle + Integration views.
  const declaredSecureSignals = useMemo(
    () => (publisherSettings?.secureSignalAdapters || []).filter((n) => /uid2|liveramp/i.test(n)),
    [publisherSettings]
  );
  /* ── WP4c: mediation + secure-signal rollups from the server ──────────────
   * One POST serves both. They read the same rows and the same
   * publisher_settings snapshot, so splitting them would post the row set
   * twice and run two debounces for one screen.
   *
   * The declared lists go IN the request. The spec proposed resolving them
   * server-side from an appId because the server owns app packs — it does not
   * own these: appPacks.js has no adapter or secure-signal field, and both
   * lists come from a captured publisher_settings response that the browser
   * aggregates (latest wins) and that two-tier retention strips from the row
   * once it ages out. A server sourcing them itself would answer with its own
   * idea of the publisher's config.
   *
   * Fetched here rather than inside AdServerIntegrationCard so the card stays
   * a pure renderer and the rows are posted once for both consumers.
   */
  const [serverMediation, setServerMediation] = useState(null);
  const declaredAdapters = useMemo(
    () => publisherSettings?.adapters || [],
    [publisherSettings]
  );
  // Coalesced: one POST in flight, at most every few seconds, latest rows wins.
  const mediationLatest = useLatestWins();
  useCoalescedPost(() => {
    const seq = mediationLatest.begin();
    return postMediationHealth(data || [], declaredAdapters, declaredSecureSignals)
      .then((res) => { if (mediationLatest.accept(seq)) setServerMediation(res); })
      // Screen, not a signed artifact: a failure falls back to the local
      // computation with an announced banner rather than blocking.
      .catch(() => { if (mediationLatest.accept(seq)) setServerMediation(null); });
  }, [data, declaredAdapters, declaredSecureSignals]);

  // Each rollup resolves independently — the route guards them separately, so
  // one throwing server-side must not drag the other onto the local path. Never
  // merged: a rollup is wholly the server's answer or wholly the browser's.
  const usingServerSecureSignals = !!serverMediation?.secureSignalsHealth;
  const secureSignalsHealth = useMemo(
    () => (
      serverMediation?.secureSignalsHealth
      || buildSecureSignalsHealth(data || [], declaredSecureSignals)
    ),
    [serverMediation, data, declaredSecureSignals]
  );

  // Unique row.step values present in the current dataset — drives the step
  // filter dropdown. Absent entirely when no row carries a step (proxy-side
  // flow marker is optional), so the filter stays invisible until relevant.
  const stepOptions = useMemo(
    () => [...new Set((data || []).map((r) => r.step).filter(Boolean))],
    [data]
  );
  const hasStepData = stepOptions.length > 0;

  // Normalize, filter, and sort (latest first)
  const rows = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    const filtered = (data || [])
      .map((r, idx) => ({ ...r, _k: r.requestId ?? r.id ?? idx }))
      .filter((r) => {
        const s = r.status ?? r.responseCode ?? 0;
        const bucket = s === 0 ? 0 : Math.floor(s / 100);
        if (!show[`s${bucket}`]) return false;

        if (statusFilter !== "all") {
          if (validationIndex.map.get(r._k) !== statusFilter) return false;
        }

        // Request-type filter (set by clicking a chip in the activity monitor).
        // Keys on requestType||'unknown' to match the chip's own labeling.
        if (typeFilter) {
          if ((r.requestType || 'unknown') !== typeFilter) return false;
        }

        if (hasStepData && stepFilter !== "all") {
          if (stepFilter === "(untagged)") {
            if (r.step) return false;
          } else if (r.step !== stepFilter) {
            return false;
          }
        }

        // Tracker / beacon filter — Requests tab only (other views assemble from
        // the unfiltered allRows set, so trackers must stay available to them).
        // An explicit type filter (e.g. clicking the google_impression chip) wins
        // over Hide Trackers — otherwise filtering to a beacon type shows nothing.
        if (hideTrackers && viewMode === 'requests' && !typeFilter) {
          const type = (r.type || r.requestType || '').toLowerCase();
          const { host } = parseURL(r.url);
          // Beacon request types: module-level BEACON_TYPES_SET (shared with the visit beacon count)
          const BEACON_TYPES = BEACON_TYPES_SET;
          // Third-party measurement / telemetry vendor hosts
          const TRACKER_HOSTS = [
            'doubleverify', 'moatads', 'integralads', 'scorecardresearch',
            'chartbeat', 'datadoghq', 'outbrain',
          ];
          if (BEACON_TYPES.has(type)) return false;
          if (TRACKER_HOSTS.some(h => host.includes(h))) return false;
        }

        // Exclude list — hide hosts matching user-configured patterns (hostname only, no port)
        if (excludeList.length > 0) {
          if (isExcluded(hostnameFromUrl(r.url), excludeList)) return false;
        }

        // Search text only filters the Requests tab — other views (Lifecycle,
        // Video, Integration, Bid Health) are unaffected by the search box.
        // Comma-separated terms match ANY (utils/requestSearch.js): URL text,
        // request type, buyer name / vendor alias, or a param key pattern.
        if (!q || viewMode !== 'requests') return true;
        return rowMatchesSearch(r, deferredQuery);
      });

    // Newest first
    return filtered.sort((a, b) => ts(b) - ts(a));
  }, [data, deferredQuery, viewMode, show, statusFilter, stepFilter, hasStepData, hideTrackers, excludeList, validationIndex.map, typeFilter]);

  // allRowsStamped: all data rows with _visitKey/_visitIdx/_visitReason/_pageUrl stamped.
  // Used by AdLifecycleView so it can separate Visit 1 / Visit 2 for the same ad slot, and
  // (Raw requests tab) to resolve slot lifecycles. Intentionally does NOT depend on `groups`
  // or `rows` — those are filtered subsets of data and would recompute this on every
  // search/filter change and could produce different visit boundaries than the full-traffic
  // picture lifecycle needs. Mirrors `groups` exactly (same thresholds, same signals) but
  // runs on the complete unfiltered dataset so beacons hidden by hideTrackers or the
  // ad-requests filter still get the correct _visitKey. (Defined here, above `groups`,
  // because the Raw requests filter below needs the lifecycles.)
  const allRowsStamped = useMemo(() => stampVisits(data, groupByPage), [data, groupByPage]);

  // ── Raw requests tab (Slot Story step 4) ─────────────────────────────────
  // Slot lifecycles: proxy answer first, local twin as fallback; only in this view.
  const requestsLifecycles = useRequestsLifecycles(allRowsStamped, viewMode === 'requests');
  // Rows any Slots step points at are never hidden by the ad filter.
  const slotKeys = useMemo(() => slotReferencedKeys(requestsLifecycles), [requestsLifecycles]);
  const adSplit = useMemo(
    () => partitionAdRequests(rows, { adOnly: adOnly && viewMode === 'requests', protectedKeys: slotKeys }),
    [rows, adOnly, viewMode, slotKeys]
  );
  // `rows` (every other filter applied) still feeds Export, jumps and lifecycle views;
  // only the TABLE render uses the ad-filtered set. Hidden rows are never deleted.
  const tableRows = adSplit.visible;
  // Duplicate billing pings fold into one row (x N). Presentation only: `rows` and
  // `tableRows` keep every ping, so picks, HAR export and Checks input are unchanged.
  const billMerge = useMemo(
    () => (viewMode === 'requests' ? mergeBillingPings(tableRows, requestsLifecycles) : null),
    [viewMode, tableRows, requestsLifecycles]
  );
  const rowSlotIndex = useMemo(
    () => (viewMode === 'requests' ? buildRowSlotIndex(requestsLifecycles) : null),
    [viewMode, requestsLifecycles]
  );
  const rowsByKeyForHeadline = useMemo(() => {
    const m = new Map();
    for (const r of allRowsStamped) if (r && r._k != null) m.set(r._k, r);
    return m;
  }, [allRowsStamped]);
  const toggleBillGroup = (repKey) => setExpandedBills((prev) => {
    const next = new Set(prev);
    if (next.has(repKey)) next.delete(repKey); else next.add(repKey);
    return next;
  });

  // Build groups with visit (revisit) subgroups.
  // Unknown page URLs inherit the last non-empty page so they stay nested.
  const groups = useMemo(() => {
    if (!groupByPage) return null;

    // Walk oldest → newest to segment visits correctly.
    const chronological = [...tableRows].sort((a, b) => ts(a) - ts(b));
    const map = new Map();

    let lastNonEmptyKey = null;   // last real page url we saw (any defining request — used for inheritance)
    let foregroundKey = null;     // current foreground page — advances on FRESH loads only, so
                                  // background ad refreshes don't mask a real return to a page
    let prevForegroundKey = null; // the page foreground just before the current one (anti-flap)
    let foregroundSince = 0;      // ts the current foreground became foreground (anti-flap)

    chronological.forEach((r) => {
      // Page identity is driven by pageName cust_param (reliable on iOS in-app),
      // falling back to content_url for web. `raw` = bucket key, `rawUrl` = real
      // URL for favicon, `rawLabel` = pretty display label (e.g. soccer/team/adh).
      const ident   = getPageIdentity(r);
      const raw      = ident?.key   || "";
      const rawUrl   = ident?.url   || "";
      const rawLabel = ident?.label || "";
      const key = raw || lastNonEmptyKey || "(unknown)"; // inherit if missing

      let g = map.get(key);
      if (!g) {
        g = { key, pageUrl: rawUrl, pageLabel: rawLabel, firstSeen: ts(r), lastSeen: 0, visits: [], total: 0 };
        map.set(key, g);
      }
      if (rawUrl && !g.pageUrl) g.pageUrl = rawUrl;
      if (rawLabel && !g.pageLabel) g.pageLabel = rawLabel;

      // Dynamic Threshold:
      const isTracker = (r.url || "").includes("/view") || (r.url || "").includes("/pcs/") || r.type === "img";
      const isAd = isAdRequestAny(r);
      // Give ad/tracker requests a generous runway (120s) — apps typically refresh ads every 30-60s
      // and those should stay in the same visit, not produce a new one.
      const threshold = (isTracker || isAd) ? 120000 : 30000;

      const timeGap = g.visits.length > 0 ? (ts(r) - g.lastSeen) : 0;

      // GAM refresh detection: prev_scp is only present in slot refresh calls.
      // Some apps reuse slot definitions and call .refresh() even after navigation,
      // so prev_scp alone cannot gate ALL visit splits — only the ambiguous ones.
      const isGAMRefresh = !!(r.validData?.expanded?.isRefresh)
        || (() => { try { return !!new URL(r.url).searchParams.get('prev_scp'); } catch { return false; } })();

      // Server-detected navigation is ALWAYS authoritative — even if the slot was "refreshed"
      // for performance (prev_scp present), if content_url / articleId actually changed the
      // proxy knows the user navigated. Never suppress this signal.
      const serverVisitChange = r.visitChanged === true;

      // Context switch / return-to-page: a DIFFERENT content page was the last one
      // committed before this request. Gated by !isGAMRefresh so background ad
      // refreshes (which carry prev_scp) never count — only a FRESH page load marks
      // the user actually returning to this page. No time gate: a page whose ads
      // refresh continuously in the background (e.g. a home feed) never goes quiet,
      // so a quiet-gap test could never fire and the return would be missed.
      const freshLoad = !!raw && !isGAMRefresh;
      // Suppress an immediate flip BACK to the page we just left (< CONTEXT_FLAP_MS):
      // that's concurrent-fresh-load interleave noise, not a navigation.
      const isFlapBack = freshLoad && raw === prevForegroundKey && (ts(r) - foregroundSince) < CONTEXT_FLAP_MS;
      const isContextSwitch = freshLoad && !!foregroundKey && raw !== foregroundKey && !isFlapBack;
      const splitOnContext = isContextSwitch;

      // Start new visit when:
      // 1. First visit for this page (always)
      // 2. Server confirmed navigation via content change (always — overrides isGAMRefresh)
      // 3. Long inactivity gap AND this isn't a known ad refresh
      // 4. Return to this page after a different page (fresh load, not a refresh, not a flap)
      const shouldSplit = g.visits.length === 0
        || serverVisitChange                          // navigation: always split
        || (!isGAMRefresh && timeGap > threshold)    // inactivity: skip for known refreshes
        || splitOnContext;                            // return-to-page: fresh load after a different page

      if (shouldSplit) {
        const visitReason = g.visits.length === 0 ? 'first'
          : serverVisitChange ? 'navigation'
          : splitOnContext   ? 'returned'
          : 'gap';
        g.visits.push({
          idx: g.visits.length + 1,
          startedAt: ts(r),
          items: [],
          beaconCount: 0, // tracks hidden OMID/beacon rows
          visitIssues: [],
          reason: visitReason,
        });
      }
      const curVisit = g.visits[g.visits.length - 1];
      // Count beacons even when hidden so the visit header can show "N hidden"
      const rType = (r.type || r.requestType || '').toLowerCase();
      if (BEACON_TYPES_SET.has(rType)) {
        curVisit.beaconCount += 1;
      }
      // Stamp visit context onto each row so AdLifecycleView can scope slots by visit
      r._visitKey    = `${key}#v${curVisit.idx}`;
      r._visitIdx    = curVisit.idx;
      r._visitReason = curVisit.reason;
      r._pageUrl     = g.pageLabel || g.pageUrl || key;
      curVisit.items.push(r);
      g.total += 1; g.lastSeen = ts(r);

      if (raw) lastNonEmptyKey = raw;
      // Advance the foreground page: seed it on a page's FIRST appearance (even a
      // refresh, so a refresh-only first page still becomes foreground), then move it
      // only on an honored fresh-load switch. A background refresh of another page
      // never moves it, or a real return would look like "no change".
      if (raw) {
        if (foregroundKey === null) { foregroundKey = raw; foregroundSince = ts(r); }
        else if (freshLoad && raw !== foregroundKey && isContextSwitch) {
          prevForegroundKey = foregroundKey; foregroundKey = raw; foregroundSince = ts(r);
        }
      }
    });

    // --- Visit-level validation: check for content but no ad requests ---
    map.forEach((g) => {
      g.visits.forEach((v) => {
        const hasContent = v.items.some(isContentLikeRequest);
        const hasAds = v.items.some(isAdRequestAny);
        if (hasContent && !hasAds) {
          v.visitIssues.push({
            severity: 'warn',
            code: 'ADS_NOT_TRIGGERED_ON_VISIT',
            message: 'Content loaded in this visit but no ad requests were fired'
          });
        }
      });
    });

    // --- Retroactive reassignment of "Unknown page" items ---
    // Requests that fired before any page URL was observed get grouped as "(unknown)".
    // We now look for a real page that started within 45 seconds of each orphan item
    // and move it there. This handles pre-navigation beacons, OMID pings, and
    // requests that lack a Referer but precede a known page visit.
    const unknownGroup = map.get("(unknown)");
    if (unknownGroup) {
      // Collect all real-page groups sorted by firstSeen ascending
      const realGroups = Array.from(map.values())
        .filter(g => g.key !== "(unknown)")
        .sort((a, b) => a.firstSeen - b.firstSeen);

      // Items that couldn't be matched stay in (unknown)
      const orphans = [];

      // Small grace so requests right at a page boundary still count as "inside".
      const CONTAIN_GRACE = 2000;

      for (const item of unknownGroup.visits.flatMap(v => v.items)) {
        const t = ts(item);

        // ── Pass 1 (primary): containment ──────────────────────────────────
        // A request whose timestamp falls inside a real page's active span
        // [firstSeen, lastSeen] belongs to that page — the page was already
        // open and still open when it fired. This is the common mid-visit case:
        // its own host is denylisted (e.g. moxie.foxnews.com) or it's an
        // inherit-type request (prebid/consent/config), so it never resolved a
        // page URL, yet it clearly fired during an ongoing visit.
        // If multiple pages overlap, pick the most recently opened one that was
        // still active (greatest firstSeen ≤ t) — that's the foreground page.
        let best = null;
        for (const rg of realGroups) {
          if (t >= rg.firstSeen - CONTAIN_GRACE && t <= rg.lastSeen + CONTAIN_GRACE) {
            if (!best || rg.firstSeen > best.firstSeen) best = rg;
          }
        }

        // ── Pass 2 (fallback): proximity to a page's first appearance ───────
        // For true pre-navigation requests (fired before any page loaded),
        // attach to the page they were about to land on: up to 45s before it
        // appeared, or up to 15s after.
        if (!best) {
          let bestDelta = Infinity;
          for (const rg of realGroups) {
            const delta = rg.firstSeen - t; // positive = page after item
            if (delta >= -15000 && delta <= 45000 && delta < bestDelta) {
              best = rg;
              bestDelta = delta;
            }
          }
        }

        if (best) {
          // Append to the visit that actually contains this timestamp (the last
          // visit started at or before t), so mid-session requests don't all pile
          // into visit 1.
          let target = best.visits[0];
          for (const v of best.visits) {
            if (v.startedAt <= t) target = v;
          }
          target.items.push(item);
          target.items.sort((a, b) => ts(a) - ts(b)); // keep chrono order
          best.total += 1;
          if (t < best.firstSeen) best.firstSeen = t;
          if (t > best.lastSeen) best.lastSeen = t;
        } else {
          orphans.push(item);
        }
      }

      if (orphans.length === 0) {
        map.delete("(unknown)");
      } else {
        // Rebuild the unknown group with only genuine orphans
        unknownGroup.visits = [{ idx: 1, startedAt: ts(orphans[0]), items: orphans, beaconCount: 0, reason: 'first', visitIssues: [] }];
        unknownGroup.total = orphans.length;
        unknownGroup.firstSeen = ts(orphans[0]);
        unknownGroup.lastSeen = ts(orphans[orphans.length - 1]);
      }
    }

    // Order groups by latest activity (desc / newest first)
    const order = Array.from(map.values()).sort((a, b) => b.lastSeen - a.lastSeen);
    return { order, map };
  }, [tableRows, groupByPage]);

  // All-devices scope for Slots: stamp each device's rows on their own (visit
  // detection is per device), then concatenate.
  const allDevicesStamped = useMemo(() => {
    if (!Array.isArray(allDevicesData)) return null;
    const byTab = new Map();
    for (const r of allDevicesData) {
      const k = r._deviceTab || '';
      if (!byTab.has(k)) byTab.set(k, []);
      byTab.get(k).push(r);
    }
    const out = [];
    for (const [k, rows] of byTab) out.push(...stampVisits(rows, groupByPage, k));
    return out;
  }, [allDevicesData, groupByPage]);

  // Live video sessions (same input VideoLifecycleView uses) so the right-panel
  // summary can re-resolve the selected session by key as new beacons arrive.
  const videoSessions = useMemo(() => {
    try {
      const rowsForVideo = (data || []).map((r, idx) => ({ ...r, _k: r.requestId ?? r.id ?? idx }));
      return buildVideoSessions(rowsForVideo);
    } catch { return []; }
  }, [data]);
  const selVideoSession = useMemo(
    () => (selVideoSessionKey == null ? null : (videoSessions.find(s => (s._k ?? s.timestamp) === selVideoSessionKey) || null)),
    [videoSessions, selVideoSessionKey]
  );

  // Build Prebid ↔ GAM bid correlation map
  const correlationMap = useMemo(() => buildBidCorrelation(rows), [rows]);

  // Open a request from a lifecycle-style view (Lifecycle, Slots): populate the
  // right pane with the lifecycle summary + the raw request, stay in this view.
  // A jump to another device's request (Slots, All devices) switches the device
  // tab first; the jump completes once that device's rows arrive.
  const pendingJump = useRef(null);
  const handleLifecycleJump = (rowKey, ctx, lifecycle) => {
    const target = allRowsStamped.find(r => r._k === rowKey);
    if (!target) {
      const other = allDevicesStamped && allDevicesStamped.find(r => r._k === rowKey);
      if (other && other._deviceTab && onSelectDevice && other._deviceTab !== currentDevice) {
        pendingJump.current = [rowKey, ctx, lifecycle];
        onSelectDevice(other._deviceTab);
      }
      return;
    }
    // "Open request" on a row the Raw requests filter would hide: lift it (session only).
    if (needsUnhide(target, adOnly, slotKeys)) setAdOnly(false);
    selKeyRef.current = rowKey;
    setSelLifecycleSource(null);
    if (lifecycle) { setSelLifecycle(lifecycle); setRightTab('summary'); }
    // A step's "Open request" (Slots view) asks for that request itself, not
    // the slot summary: an engineer clicking Prebid wants the Prebid request.
    if (ctx && ctx.tab) setRightTab(ctx.tab);
    setSel(target);
  };
  useEffect(() => {
    const p = pendingJump.current;
    if (!p || !allRowsStamped.some(r => r._k === p[0])) return;
    pendingJump.current = null;
    handleLifecycleJump(...p);
  }, [allRowsStamped]); // eslint-disable-line react-hooks/exhaustive-deps


  // The detail pane is collapsed until the user actually selects something —
  // a request row, a lifecycle slot, or a video session. No auto-select: an
  // empty right pane (just "Select a request…") wastes half the screen and the
  // table/empty-state read better at full width. Clearing all three collapses it.
  // Bid Health and Integration are whole-session summary views with no per-row
  // selection, so the Request Details pane is never relevant there — keep them
  // full-width even if a request is still selected from the Requests view.
  const showDetail = !!(sel || selLifecycle || selVideoSession)
    && viewMode !== 'bidhealth' && viewMode !== 'integration' && viewMode !== 'overview';
  const clearSelection = useCallback(() => {
    setSel(null);
    setSelLifecycle(null);
    setSelVideoSessionKey(null);
  }, []);

  // Jump to a specific row requested from another page (e.g. Smoke Test
  // jump-to links, Checks row-reference chips — WP6). Also switches to the
  // requests view and asks the scroll effect below to bring it into view —
  // previously this only selected/highlighted the row without scrolling to it.
  const jumpWatch = useRef({ key: null, data: null, cleared: false, switched: false });
  useEffect(() => {
    if (!jumpTarget) { jumpWatch.current = { key: null, data: null, cleared: false, switched: false }; return; }
    if (jumpWatch.current.key !== jumpTarget) {
      jumpWatch.current = { key: jumpTarget, data, cleared: false, switched: false };
    }
    const w = jumpWatch.current;
    // A target that never resolves must not stay armed forever (it would hijack a
    // later, unrelated selection): drop it and say so.
    const drop = () => {
      setJumpTarget(null);
      jumpWatch.current = { key: null, data: null, cleared: false, switched: false };
      setSignOffNotice({ severity: 'info', text: 'Could not find that request in the current capture (it may belong to a cleared session or an excluded host).' });
    };
    const r = resolveJumpTarget(jumpTarget, { rows, allRowsStamped, allDevicesStamped, currentDevice });
    if (r.kind === 'visible') {
      // A hidden (non-ad) row must be visible to be scrolled to: lift the
      // "Ad requests only" filter for this session (not persisted).
      if (needsUnhide(r.row, adOnly, slotKeys)) setAdOnly(false);
      setSel(r.row);
      setJumpTarget(null);
      setViewMode('requests');
      setScrollToKey(r.row._k);
    } else if (r.kind === 'filtered') {
      // On this device but hidden by a filter: clear them once, then select.
      if (w.cleared) { drop(); return; }
      w.cleared = true;
      clearAllFilters();
      setViewMode('requests');
    } else if (r.kind === 'otherDevice') {
      if (!onSelectDevice || w.switched) { drop(); return; }
      w.switched = true;
      w.data = data;
      onSelectDevice(r.device);
    } else if (w.data !== data) {
      // Unresolved after one data change.
      drop();
    }
  }, [jumpTarget, rows, data, allRowsStamped, allDevicesStamped, currentDevice, adOnly, slotKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep selected row object fresh if data updates
  useEffect(() => {
    if (!sel) return;
    const match = rows.find(
      (r) => (r.requestId ?? r.id ?? r._k) === (sel.requestId ?? sel.id ?? sel._k)
    );
    if (match && match !== sel) setSel(match);
  }, [rows, sel]);

  // Scroll a jumped-to row into view after viewMode switches to 'requests'
  useEffect(() => {
    if (!scrollToKey || viewMode !== 'requests') return;
    // A group may have been manually collapsed, so the target row may not be rendered.
    // Expand its owning group first (no-op if already expanded — returns same ref).
    if (groups?.order) {
      const owner = groups.order.find(g =>
        g.visits?.some(v => v.items?.some(it => (it._k ?? it.requestId ?? it.id) === scrollToKey)));
      if (owner) setCollapsed(c => (c[owner.key] !== true ? c : { ...c, [owner.key]: false }));
    }
    const scrollFn = () => {
      const el = tableBodyRef.current?.querySelector(`[data-rk="${scrollToKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setScrollToKey(null);
      }
    };
    // Small delay to let React finish rendering (incl. the just-expanded group) before scrolling
    const t = setTimeout(scrollFn, 120);
    return () => clearTimeout(t);
  }, [scrollToKey, viewMode, groups]);

  const pickedCount = picked.length;
  // Compare needs exactly 2 picks on one host (SelectionBar compareState)
  const comparePair = picked.slice(0, 2);
  const sameHost = comparePair.length < 2 ? true : hostOf(comparePair[0]?.url) === hostOf(comparePair[1]?.url);

  const runChecksOnPicked = () => {
    if (!pickedCount) return;
    setChecksScopeRows(picked);
    navigate('/checks');
  };

  const confirmNewSession = () => {
    // Destructive since 2026-08-11 — gate it the same way the Clear handler in MainApp does.
    if (window.confirm('Start a new session? This clears all captured traffic, on the dashboard and on the proxy. This cannot be undone.')) {
      startNewSession();
    }
  };

  // The "..." menu: rarely used actions, moved (never removed) out of the main row.
  const overflowItems = [
    { key: 'new-session', label: '🆕 New session', hidden: readonly || !startNewSession, onClick: confirmNewSession, danger: true,
      title: "Start a fresh session — clears all captured traffic from the dashboard AND the proxy's retained history, then begins capturing anew. This cannot be undone." },
    { key: 'clear', label: '🧹 Clear', hidden: readonly || !onClear, onClick: () => onClear && onClear(), danger: true,
      title: 'Clear captured traffic for the selected platform only' },
    { key: 'import', label: '📂 Import compare JSON', onClick: () => importInputRef.current?.click(), title: 'Import compare JSON' },
    { key: 'export', label: '📥 Export IAB audit CSV', onClick: () => downloadIabSheet(rows, `iab-audit-${new Date().toISOString()}.csv`), title: 'Export IAB Audit CSV' },
    // Arrow, not a bare reference: handleSignOff is a const declared further down this
    // component, so reading it while this array is built throws a TDZ ReferenceError.
    { key: 'sign-off', label: '📋 Sign-off report', onClick: () => handleSignOff(), title: 'Sign-off Report' },
  ];

  const pageLabelOf = (pageUrl) => {
    if (pageUrl === "(unknown)") return "⚠ Pre-navigation requests";
    try {
      const u = new URL(pageUrl);
      // Strip trailing slash for cleanliness, show host + path
      const path = u.pathname.replace(/\/$/, '') || '/';
      return `${u.host}${path}`;
    } catch {
      return pageUrl;
    }
  };

  // ---- Collapse/Expand all helpers ----
  // Page groups are EXPANDED BY DEFAULT.
  // Semantics: a group is collapsed only when collapsed[key] === true; any other
  // value (incl. undefined) = expanded.
  const collapseAll = () => {
    if (groups) {
      const gFlags = {};
      groups.order.forEach((g) => { gFlags[g.key] = true; }); // true = explicitly collapsed
      setCollapsed(gFlags);
    }
    setCollapsedVisit({});
  };
  const expandAll = () => {
    // Empty map = every group back to the default expanded state.
    setCollapsed({});
    setCollapsedVisit({});
  };

  // ---- Resizable split ----
  const containerRef = useRef(null);
  const [splitPct, setSplitPct] = useState(() => {
    const v = Number(localStorage.getItem("inspectra.splitPct"));
    return Number.isFinite(v) ? Math.max(30, Math.min(70, v)) : 42;
  });
  const splitRef = useRef(splitPct);
  useEffect(() => { splitRef.current = splitPct; }, [splitPct]);

  // Close exclude popover on outside click
  useEffect(() => {
    if (!showExcludePopover) return;
    const handler = (e) => {
      if (excludePopoverRef.current && !excludePopoverRef.current.contains(e.target)) {
        setShowExcludePopover(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExcludePopover]);

  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      let pct = ((e.clientX - rect.left) / rect.width) * 100;
      pct = Math.max(30, Math.min(70, pct)); // clamp 30–70%
      // also enforce a minimum pixel width for the right pane so it can't be dragged unusably narrow
      const minRightPx = 320;
      const minPctForRight = rect.width > 0 ? 100 - (minRightPx / rect.width) * 100 : pct;
      pct = Math.min(pct, minPctForRight);
      setSplitPct(pct);
    };
    const onUp = () => {
      setDragging(false);
      document.body.style.cursor = "";
      localStorage.setItem("inspectra.splitPct", String(splitRef.current));
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
  }, [dragging]);

  const startDrag = (e) => { e.preventDefault(); setDragging(true); };
  const resetSplit = () => { setSplitPct(50); localStorage.setItem("inspectra.splitPct", "50"); };

  // Row renderer (used in grouped + flat views)
  const renderRow = (r) => {
    const { host, path } = parseURL(r.url);
    const isSel = (sel?.requestId ?? sel?.id ?? sel?._k) === r._k;
    const vstat = validationIndex.map.get(r._k);

    const isPicked = picked.some((p) => (p.requestId ?? p.id ?? p._k) === r._k);
    const codeCell = codeCellFor(r);
    const methodBadge = methodBadgeFor(r);
    const billGroup = billMerge ? billMerge.byRep.get(r._k) : null;
    const isBillMember = !!(billMerge && billMerge.memberOf.has(r._k)); // an expanded later ping

    return (
      <tr
        key={r._k}
        data-rk={r._k}
        onClick={() => {
          setSel(r);
          onRowOpen?.(r);
          const rType = (r.requestType || r.type || '').toLowerCase();
          selKeyRef.current = r._k;
          setSelLifecycle(null);
          setSelLifecycleSource(null);
          setRightTab('details');
          if (rType === 'google') {
            // Auto-open the lifecycle summary for GAM rows from the SERVER graph —
            // the same answer Slots renders. The local rebuild is only a fallback
            // (announced as "local fallback"); it used to run on every click and
            // could disagree with the slot card beside it.
            const clickedKey = r._k;
            const rowsAtClick = allRowsStamped;
            getServerLifecycles(rowsAtClick)
              .then((lcs) => ({ lc: findLifecycleForRow(lcs, clickedKey), src: 'server' }))
              .catch(() => ({ lc: findLifecycleForRow(buildSlotLifecycles(rowsAtClick), clickedKey), src: 'local' }))
              .then(({ lc, src }) => {
                if (selKeyRef.current !== clickedKey) return; // user moved on
                if (lc) { setSelLifecycle(lc); setSelLifecycleSource(src); setRightTab('summary'); }
              });
          }
        }}
        style={{
          background: isSel ? "var(--row-active)" : isPicked ? "var(--row-picked, rgba(59,130,246,.08))" : "var(--card)",
          cursor: "pointer", color: "var(--ink)",
          outline: isSel ? "1px solid var(--primary)" : "none",
          outlineOffset: "-1px",
        }}
      >
        <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center", borderLeft: isSel ? "3px solid var(--primary)" : "3px solid transparent" }}>
          <input
            type="checkbox"
            checked={isPicked}
            onChange={() => togglePick(r, r._k)}
            title={isPicked ? "Deselect" : "Select for HAR export / compare"}
          />
        </td>
        <td style={{ textAlign: "center" }}>
          <StatusDot s={vstat} />
        </td>
        <td>{fmtTime(ts(r))}</td>
        <td title={codeCell.title || undefined}>
          {/* USB/logcat row: empty cell (response status is not visible), never "—" */}
          {!codeCell.usb && (
            <Pill tone={codeCell.failed ? "error" : statusTone(codeCell.status)}>
              {codeCell.text}
            </Pill>
          )}
        </td>
        <td style={isBillMember ? { paddingLeft: 18 } : undefined}>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
            {/* ── Method badge: only for non-GET rows (the Method column was dropped);
                the detail pane header still shows the method for every row. */}
            {methodBadge && <MetaBadge tone="neutral" title={`HTTP method: ${methodBadge}`}>{methodBadge}</MetaBadge>}
            {/* ── Merged duplicate billing pings: x N, click to expand each ping */}
            {billGroup && (
              <span
                role="button"
                tabIndex={0}
                data-testid="bill-merge-badge"
                onClick={(e) => { e.stopPropagation(); toggleBillGroup(r._k); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleBillGroup(r._k); } }}
                title={`${duplicateBillingTip(billGroup.count)} — click to ${expandedBills.has(r._k) ? 'collapse' : 'list each ping'}`}
                style={{ cursor: "pointer" }}
              >
                <MetaBadge tone="warn">{expandedBills.has(r._k) ? '▾' : '▸'} ×{billGroup.count}</MetaBadge>
              </span>
            )}
            {/* ── Vendor badge (Google / Prebid / Amazon / OMID…) — primary, colored fill */}
            {!!r.type && (() => {
              const t = (r.type || '').toLowerCase();
              // These have their own distinct badge below
              const specialTypes = ['impression', 'click', 'gam_video', 'vast_error', 'vast_tracking',
                'springserve_vast', 'springserve_ad_request', 'amazon_video', 'video_content', 'sdk_bidding'];
              if (specialTypes.includes(t)) return null;
              return <VendorBadge type={r.type} />;
            })()}

            {/* ── Special event / format pills (not vendor-mapped) */}
            {!!r.type && (() => {
              const eventMap = {
                impression:              { label: 'Impression',  tone: 'success' },
                click:                   { label: 'Click',       tone: 'warn'    },
                gam_video:               { label: 'GAM Video',   tone: 'info'    },
                vast_tracking:           { label: 'VAST Event',  tone: 'success' },
                vast_error:              { label: 'VAST Error',  tone: 'error'   },
                springserve_vast:        { label: 'SS VAST',     tone: 'info'    },
                springserve_ad_request:  { label: 'SS C2S',      tone: 'info'    },
                amazon_video:            { label: 'APS C2S',     tone: 'info'    },
                video_content:           { label: 'Content',     tone: 'neutral' },
                // Not "SDK Bid": with SDK bidding the bid is server-to-server (GAM <-> buyer),
                // so nothing the device sends to a buyer host is a bid.
                sdk_bidding:             { label: 'SDK',         tone: 'info'    },
              };
              let ev = eventMap[r.type];
              if (!ev) return null;
              let buyer = '';
              if (r.type === 'sdk_bidding') {
                const vd = r?.validData || {};
                buyer = (vd.networkName || '').replace(/\s*\(.*\)$/, '');
                const byKind = {
                  error_log: { label: 'SDK Error', tone: 'error'   },
                  telemetry: { label: 'SDK Log',   tone: 'neutral' },
                  config:    { label: 'SDK Config', tone: 'neutral' },
                  event:     { label: 'SDK Event', tone: 'success' },
                  impression:{ label: 'SDK Impression', tone: 'success' },
                  bill:      { label: 'SDK Billing', tone: 'success' },
                  bid:       { label: 'SDK Ad request', tone: 'info' },
                };
                ev = byKind[vd.kind] || ev;
              }
              return <Pill tone={ev.tone}>{ev.label}{buyer ? ` · ${buyer}` : ''}</Pill>;
            })()}

            {/* ── Format badge (Display / Video) — subtle MetaBadge, only when informative */}
            {(() => {
              const u = (r.url || '').toLowerCase();
              const t = (r.type || '').toLowerCase();
              const impliedVideo = ['gam_video', 'ima', 'springserve_vast', 'amazon_video',
                'vast_tracking', 'vast_error', 'video_content', 'springserve_ad_request'];
              if (t === 'impression' || t === 'click' || t === 'img') return null;
              if (impliedVideo.includes(t)) return null;
              let fmt = null;
              if (t === 'google') {
                fmt = (u.includes('env=vp') || u.includes('output=vast') || u.includes('output=xml_vast') || u.includes('output=vmap')) ? 'Video' : 'Display';
              } else if (t === 'prebid') {
                const sj = safeJson(r.requestBody);
                fmt = (sj.ok && sj.value?.imp?.[0]?.video) ? 'Video' : 'Display';
              } else if (t === 'amazon') {
                fmt = 'Display';
              }
              if (!fmt) {
                const uLow = (r.url || '').toLowerCase();
                if (uLow.includes('doubleclick.net') || uLow.includes('amazon-adsystem') ||
                    uLow.includes('adnxs') || uLow.includes('prebid') || uLow.includes('securepubads')) {
                  fmt = 'Display';
                }
              }
              if (!fmt) return null;
              return <MetaBadge tone={fmt === 'Video' ? 'warn' : 'neutral'}>{fmt}</MetaBadge>;
            })()}

            {/* ── GAM Refresh badge */}
            {(() => {
              const expanded = r.validData?.expanded;
              const isRefresh = expanded?.isRefresh
                || (() => { try { return !!new URL(r.url).searchParams.get('prev_scp'); } catch { return false; } })();
              if (!isRefresh) return null;
              return <MetaBadge tone="neutral" title="Ad refresh — prev_scp present">↻ Refresh</MetaBadge>;
            })()}

            {/* ── Ad Inspector test-session badge.
                Verified on-wire signature: adtest=on marks any Ad Inspector test
                request; single-ad-source mode additionally carries
                restricted_to_buyer_network=<GAM network code of the isolated buyer>.
                Targeting params are NOT stripped, so other validators stay valid. */}
            {(() => {
              const ai = r.validData?.adInspector;
              if (!ai) return null;
              return (
                <MetaBadge
                  tone="warn"
                  title={ai.singleAdSource
                    ? `Ad Inspector single-ad-source session — traffic restricted to buyer network ${ai.buyerNetworkCode} (restricted_to_buyer_network)`
                    : 'Ad Inspector test request (adtest=on)'}
                >
                  🧪 {ai.singleAdSource ? `Single source · ${SAS_CODE_NAMES[ai.buyerNetworkCode] || ai.buyerNetworkCode}` : 'Ad test'}
                </MetaBadge>
              );
            })()}

            {/* ── Ad unit / slot identifier — monospace path-style badge */}
            {(() => {
              const idLabel = getAdUnitLabel(r);
              if (!idLabel) return null;
              return <AdUnitBadge title={idLabel}>{idLabel}</AdUnitBadge>;
            })()}

            {/* ── Bid correlation badge */}
            {(() => {
              const corr = correlationMap.get(r._k);
              if (!corr) return null;
              if (corr.role === 'gam') {
                const label = `🏆 ${corr.bidder} $${corr.gamHbPb || (corr.prebidPrice != null ? Number(corr.prebidPrice).toFixed(2) : '?')}`;
                return (
                  <Pill
                    tone={corr.discrepancy ? 'error' : 'success'}
                    title={corr.discrepancy
                      ? `Price mismatch: Prebid hb_pb=${corr.prebidHbPb} vs GAM hb_pb=${corr.gamHbPb}`
                      : `Prebid winner: ${corr.bidder} — cache matched`}
                  >
                    {label}{corr.discrepancy ? ' ⚠' : ''}
                  </Pill>
                );
              }
              if (corr.role === 'prebid') {
                return <MetaBadge tone="info" title="This auction's winning bid was passed to a GAM request">→ GAM</MetaBadge>;
              }
              return null;
            })()}

            {/* ── IU Rewrite badge: a rule changed this request before it left the proxy */}
            {r.iuRewrite && Array.isArray(r.iuRewrite.changes) && r.iuRewrite.changes.length > 0 && (
              <MetaBadge tone="info" title={describeRewrite(r.iuRewrite).join('\n')}>🔀 Rewritten</MetaBadge>
            )}
            {describeChaosPatch(r.chaos).length > 0 && (
              <MetaBadge tone="warn" title={describeChaosPatch(r.chaos).join('\n')}>🌀 Response patched</MetaBadge>
            )}

            {/* ── Encoding badge (GZIP etc.) */}
            {(() => {
              const enc = getEncoding(r);
              return enc && enc !== "identity" ? <MetaBadge tone="warn">{enc}</MetaBadge> : null;
            })()}

            {/* ── ATT (App Tracking Transparency) per-request chip */}
            {(() => {
              const att = getRowATT(r);
              if (!att) return null;
              const meta = ATT_META[att.val] || ATT_META['0'];
              return (
                <span
                  title={`${ATT_FULL[att.val] || att.val} — from ${att.source}`}
                  style={{
                    fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                    background: `${meta.color}22`, border: `1px solid ${meta.color}66`,
                    color: meta.color, whiteSpace: 'nowrap', letterSpacing: 0.3,
                  }}
                >
                  {meta.label}
                </span>
              );
            })()}

            {/* ── MIME badge — only when not implied by type */}
            {(() => {
              const sm = shortMime(getMime(r));
              if (!sm) return null;
              const noisyMime = ['img'];
              const jsonImplied = ['prebid', 'amazon', 'amazon_video', 'springserve_ad_request', 'springserve_vast', 'google', 'gam_video'];
              if (noisyMime.includes(sm)) return null;
              if (sm === 'json' && jsonImplied.includes(r.type)) return null;
              return <MetaBadge tone="neutral">{sm}</MetaBadge>;
            })()}

            {/* ── App flow step badge — proxy-stamped marker of which app tab/flow
                was active when this request fired. Only present when the row
                carries it (row.step), so it stays invisible for untagged captures. */}
            {!!r.step && (
              <MetaBadge tone="info" title={`App flow step: ${r.step}`}>
                {r.step}
              </MetaBadge>
            )}
          </div>
        </td>
        <td>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span title={r.url} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {path || r.url}
            </span>
            <small style={{ color: "var(--muted)" }}>{host}</small>
          </div>
        </td>
        <td>{fmtBytes(r.size)}</td>
        <td>{fmtMs(r.durationMs)}</td>
      </tr>
    );
  };

  // Sign-off report. The blank tab is opened SYNCHRONOUSLY in the click (before any
  // await) — browsers only honour window.open for a user gesture, and the report
  // is built after a server round-trip, which used to get it popup-blocked.
  const handleSignOff = async () => {
    const preWin = openBlankReportWindow();
    // WP: sign-off report now consumes the SERVER's lifecycle graph
    // (GET /proxy/lifecycle?grouped=1&detail=1&full=1 — the same
    // graph agents/CI already get via the `lifecycle_summary` MCP
    // tool / launch rollup), not a browser-recomputed copy — two
    // authoritative-looking answers to "what happened to this ad
    // slot" is exactly the ambiguity this artifact can't afford.
    // Deliberately NOT falling back to buildSlotLifecycles/
    // groupLifecyclesBySlot on failure: a signed report built from
    // an unverifiable source is worse than no report, so a fetch
    // failure blocks report generation with a clear message instead.
    setSignOffNotice(null);
    let data;
    try {
      // POST the DEVICE-SCOPED rows (same call Slots makes) instead of GET: the
      // GET reads the proxy's whole history, which can hold other devices
      // and earlier sessions — the signed report must describe what this
      // dashboard scope shows.
      data = await postLifecycleGroups(allRowsStamped, { limit: 2000, manualOverrides: {} });
    } catch (e) {
      setSignOffNotice({
        severity: 'error',
        text: 'Could not build the sign-off report — the proxy\'s server-side lifecycle graph is required '
          + '(agents/CI grade sessions from that same graph, so the report must not diverge from it). '
          + (e?.message || String(e)),
      });
      closeBlankReportWindow(preWin);
      return;
    }
    const groups = Array.isArray(data?.groups) ? data.groups : [];
    if (!groups.length) {
      setSignOffNotice({ severity: 'info', text: 'No slot lifecycles detected yet — capture some traffic first.' });
      closeBlankReportWindow(preWin);
      return;
    }
    // WP2 (frontend-validation-port-spec.md §4), 4th and final
    // verdict: cust_params rule findings, POST /proxy/validation/
    // cust-params-rules over one GAM/IMA row per group (i-aligned
    // with `groups`, same order buildSignOffReport.js expects).
    // Rules are the ACTIVE PROFILE'S — browser-only state — so
    // they travel as a request parameter; the proxy never
    // resolves a profile itself. A group with no GAM row posts
    // `null` in its slot and gets a null group back, same as a
    // group whose profile has no rules configured — neither
    // blocks the report (see custParamsRuleResults's doc in
    // buildSignOffReport.js for that asymmetry).
    const custParamRows = groups.map((g) => {
      const slot = g.slots && g.slots[0];
      return (slot && slot.phases && slot.phases.gam && slot.phases.gam.row) || null;
    });
    const activeRules = getActiveProfile().custParamRules;
    let custParamsData = null;
    try {
      custParamsData = await postCustParamsRules(custParamRows, activeRules);
    } catch { /* surfaced below via missingFields, same as the other three */ }
    // WP2 (frontend-validation-port-spec.md §4): bid health by
    // app×platform, demand-wasted (bidNoFill), the orphan/
    // duplicate report, and the cust_params rule findings are
    // all server-computed verdicts this report consumes as-is —
    // buildSignOffReport.js never recomputes any of them locally
    // anymore. Signed artifacts follow a STRICTER rule than
    // screens (spec §5 recipe #3): no silent fallback. An older
    // proxy mid-deploy (or one that predates this wiring) omits
    // one or more of these fields instead of sending an
    // empty-but-present value, so check for that explicitly and
    // block — same policy as the fetch-failure branch above, not
    // a second, divergent local answer. A cust_params response
    // with every group null (no rules configured) is NOT missing
    // — only an absent/short-length `results` array is.
    const missingFields = [];
    if (!Array.isArray(data?.bidHealthByAppPlatform)) missingFields.push('bidHealthByAppPlatform');
    if (!data?.bidNoFill || !Array.isArray(data.bidNoFill.items)) missingFields.push('bidNoFill.items');
    if (!data?.orphanReport || typeof data.orphanReport !== 'object') missingFields.push('orphanReport');
    if (!custParamsData || !Array.isArray(custParamsData.results) || custParamsData.results.length !== groups.length) {
      missingFields.push('custParamsRuleResults');
    }
    if (missingFields.length) {
      setSignOffNotice({
        severity: 'error',
        text: `Could not build the sign-off report — the proxy's response is missing ${missingFields.join(', ')} `
          + '(likely an older proxy build, mid-deploy). This report will not fall back to computing them '
          + 'locally, since that would let the signed artifact diverge from what the proxy itself reports. '
          + 'Retry once the proxy has redeployed.',
      });
      closeBlankReportWindow(preWin);
      return;
    }
    const tester = localStorage.getItem('auth_user') || '';
    const pageUrl = groups[0]?.pageUrl || '';
    openSignOffReport(groups, {
      tester,
      pageUrl,
      rows: allRowsStamped,
      lifecycleSource: { mode: 'server' },
      lifecycleTruncated: data?.truncated || null,
      bidHealthByAppPlatform: data.bidHealthByAppPlatform,
      bidNoFillReport: data.bidNoFill,
      orphanReport: data.orphanReport,
      custParamsRuleResults: custParamsData.results.map((r) => r.group),
    }, preWin);
  };

  // Save the picked rows to the backend. No window.prompt / alert: both block the
  // live capture loop. The HAR is titled automatically; result lands in the notice.
  const handleSaveHar = async () => {
    setSignOffNotice(null);
    try {
      // Save ONLY the picked rows. This deliberately has no save-everything
      // fallback (a 2,693-request HAR nobody asked for).
      const saveRows = await withStashedBodies(picked);
      const first = saveRows[0];
      const title = (first
        ? (() => { try { return new URL(first.url).host; } catch { return 'Session'; } })() + " • " + new Date().toLocaleString()
        : new Date().toLocaleString());
      const har = buildHarFromRequests(saveRows, { title, globalConfig: configAttributesGlobal });
      await saveHarToServer(har, { title, favorite: false, pageUrl: first?.url || "" });
      setSignOffNotice({ severity: 'success', text: `Saved ${saveRows.length} request${saveRows.length > 1 ? 's' : ''} as "${title}".` });
    } catch (e) {
      const msg = e?.message || String(e);
      // 401: the api client already fired the shared `auth:expired` flow (in-app
      // re-login overlay, no reload) — rows stay selected for a retry.
      if (!(msg.includes('401') || msg.includes('invalid_token') || msg.includes('unauthorized'))) {
        setSignOffNotice({ severity: 'error', text: 'Save failed: ' + msg });
      }
    }
  };

  // Every filter that can hide rows, reset together (the empty state's "Clear
  // filters" used to leave type / step / Hide trackers on).
  const clearAllFilters = () => {
    setQuery('');
    setStatusFilter('all');
    setShow({ ...ALL_STATUS_BUCKETS });
    setStepFilter('all');
    setHideTrackers(false);
    setAdOnly(false);
    onTypeFilterChange(null);
  };

  return (
    <div
      ref={containerRef}
      style={{
        ...styles.wrap,
        // Collapsed (nothing selected) → left pane fills the row; the resizer +
        // right pane are display:none'd out of the grid flow below.
        // fr (not %) so the 6px divider is subtracted from the total first and
        // the two panes split what's LEFT — `${x}% 6px ${100-x}%` overflows by
        // 6px (sums to 100%+6px), pushing the right pane's edge off-screen.
        gridTemplateColumns: showDetail ? `${splitPct}fr 6px ${100 - splitPct}fr` : '100%',
        position: "relative",
      }}
    >
      {paused && (
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 12,
            zIndex: 50,
            background: "var(--warn-bg)",
            border: "1px solid var(--line)",
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 600,
            color: "var(--warn-text)",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
          }}
          title="Recording is paused — new requests are not being captured"
        >
          <span>⏸ Paused</span>
        </div>
      )}
      {/* LEFT: list */}
      <div style={styles.leftPane}>
        {/* Dropped / pinned hosts — MITM handshake failed, no decryptable request captured */}
        {(() => {
          const drops = Array.isArray(tlsDrops) ? tlsDrops : [];
          const newest = drops.reduce((m, d) => Math.max(m, d.lastSeen || 0), 0);
          if (drops.length === 0 || newest <= dropsDismissedAt) return null;
          const sorted = [...drops].sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0));
          return (
            <div
              style={{
                margin: "8px 8px 0",
                padding: "8px 10px",
                background: "var(--warn-bg)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                fontSize: 12,
                color: "var(--warn-text)",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
              }}
              title="These HTTPS hosts refused the proxy CA — the TLS handshake failed, so no request body could be captured. This is almost always certificate pinning, not a missing request."
            >
              <span style={{ flexShrink: 0 }}>🔒</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  {sorted.length} host{sorted.length > 1 ? "s" : ""} dropped at TLS (likely cert-pinned — not captured)
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {sorted.slice(0, 12).map((d) => (
                    <button
                      key={d.host}
                      onClick={() => setQuery(d.host)}
                      style={{
                        border: "1px solid var(--warn-border)",
                        background: "var(--card)",
                        borderRadius: 6,
                        padding: "2px 6px",
                        fontSize: 11,
                        cursor: "pointer",
                        color: "inherit",
                      }}
                      title={`Seen ${d.count}× · ${Object.keys(d.codes || {}).join(", ")} — click to search`}
                    >
                      {d.host}{d.count > 1 ? ` ×${d.count}` : ""}
                    </button>
                  ))}
                  {sorted.length > 12 && <span>+{sorted.length - 12} more</span>}
                </div>
              </div>
              <button
                onClick={() => setDropsDismissedAt(newest)}
                style={{ border: "none", background: "transparent", cursor: "pointer", color: "inherit", fontSize: 14, lineHeight: 1, flexShrink: 0 }}
                title="Dismiss (reappears if new hosts drop)"
              >
                ×
              </button>
            </div>
          );
        })()}
        {/* Toolbar */}
        <div style={styles.toolbar}>
          {viewMode === 'requests' && (
            <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search URL / host / type / vendor / param. Commas = any of: gampad, prebid, liftoff"
                style={styles.search}
              />
              {query.trim() && (
                <span
                  style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    fontSize: 11, fontFamily: "monospace", color: "var(--muted)",
                    background: "var(--surface, var(--bg))", padding: "1px 7px", borderRadius: 6,
                    pointerEvents: "none", whiteSpace: "nowrap",
                  }}
                >
                  {rows.length} match{rows.length === 1 ? "" : "es"}
                </span>
              )}
            </div>
          )}
          {typeFilter && (
            <button
              onClick={() => onTypeFilterChange(null)}
              title="Clear request-type filter"
              style={{
                ...styles.btn,
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "var(--accent, #1976d2)", color: "#fff", border: "none",
                whiteSpace: "nowrap",
              }}
            >
              type: {typeFilter} &times;
            </button>
          )}
          {/* Row 2: Controls */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
            {/* Group toggle */}
            <label style={styles.chk}>
              <input
                type="checkbox"
                checked={groupByPage}
                onChange={(e) => setGroupByPage(e.target.checked)}
              />
              <span>Group by page</span>
            </label>

            {/* View mode tabs. "Raw requests" is the engineer view (Slot Story step 4);
                the Lifecycle tab was retired 2026-10-18 for Slots. */}
            <RequestsViewTabs
              viewMode={viewMode}
              onSelect={(id) => {
                setViewMode(id);
                setJumpedFromLifecycle(false);
              }}
            />

            {/* Ad requests only (Raw requests view): one toggle, noise is hidden never deleted */}
            {viewMode === 'requests' && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <label style={styles.chk} title="Show only ad traffic (Checks ad scope + lifecycle request types). Unclassified rows stay visible. Rows a slot points at are never hidden.">
                  <input
                    type="checkbox"
                    data-testid="ad-only-toggle"
                    checked={adOnly}
                    onChange={(e) => toggleAdOnly(e.target.checked)}
                  />
                  <span>Ad requests only</span>
                </label>
                {adOnly && adSplit.hiddenCount > 0 && (
                  <button
                    type="button"
                    data-testid="ad-hidden-count"
                    onClick={() => toggleAdOnly(false)}
                    title="Hidden rows stay in the session, HAR export and Checks. Click to show everything."
                    style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, fontSize: 11, color: "var(--muted)", textDecoration: "underline" }}
                  >
                    {adSplit.hiddenCount} hidden · show all
                  </button>
                )}
              </div>
            )}

            {/* Collapse/Expand — the request list's own controls, Requests tab only. */}
            {viewMode === 'requests' && (<>
            <div style={{ width: 1, height: 20, background: "var(--line)", margin: "0 4px" }} />

            {/* Collapse/Expand All */}
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button style={styles.btn} onClick={collapseAll} title="Collapse all groups">Collapse all</button>
              <button style={styles.btn} onClick={expandAll} title="Expand all groups">Expand all</button>
            </div>

            </>)}

            {/* Capture controls act on the whole session, so every tab shows them
                (Slots is the default view, and you capture while watching it). */}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
              <AndroidCaptureButton />
              <button
                style={{ ...styles.btn, background: "var(--primary)", color: "white", border: "none" }}
                onClick={() => setConnectOpen(true)}
              >
                Connect Device
              </button>
              {/* New session, Clear, Import, Export, Sign-off live in the ⋯ menu (two clicks).
                  Clear stays the SCOPED per-platform clear; New session is the unscoped
                  destructive one (a separate "Clear all" was removed 2026-08-11). */}
              <ToolbarOverflowMenu items={overflowItems} />
            </div>

          </div>
          {/* Sign-off report notice — rendered OUTSIDE the flex toolbar above so
              it can't disturb the icon-button row's layout. Dismissable; also
              cleared automatically on the next report attempt. */}
          {signOffNotice && (
            <Alert
              severity={signOffNotice.severity}
              onClose={() => setSignOffNotice(null)}
              sx={{ mt: 1, mb: 1, fontSize: 12 }}
            >
              {signOffNotice.text}
            </Alert>
          )}
          {/* Hidden file input for compare JSON import */}
          <input
            type="file"
            accept="application/json"
            ref={importInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = (ev) => {
                try {
                  const text = String(ev.target?.result || "");
                  const parsed = JSON.parse(text);
                  let a = parsed.a;
                  let b = parsed.b;
                  if ((!a || !b) && parsed.requests) { a = parsed.requests.a; b = parsed.requests.b; }
                  if (!a || !b) { setSignOffNotice({ severity: 'error', text: 'Invalid compare JSON: expected { a, b } structure.' }); return; }
                  setImportedCompare({ a, b });
                  setCompareOpen(true);
                } catch (err) {
                  setSignOffNotice({ severity: 'error', text: 'Failed to read compare JSON: ' + (err?.message || err) });
                } finally {
                  e.target.value = "";
                }
              };
              reader.readAsText(file);
            }}
          />
        </div>

        {/* Selection bar: exists only while rows are picked (replaces the always-greyed icon row). */}
        {viewMode === 'requests' && (
          <SelectionBar
            count={pickedCount}
            sameHost={sameHost}
            onDownloadHar={downloadPickedHar}
            onSave={handleSaveHar}
            onRunChecks={runChecksOnPicked}
            onCompare={() => setCompareOpen(true)}
            onClear={clearPicked}
          />
        )}

        {/* Pause / Resume capture + Hide Trackers — every view (capture acts on the
            whole session; Hide trackers only changes the Requests table). */}
        <div style={{ padding: "8px 8px 0", display: "flex", gap: 8, alignItems: "center" }}>
          <button
            style={styles.btn}
            onClick={() => onTogglePause(!paused)}
            aria-label={paused ? "Resume recording" : "Pause recording"}
            title={paused ? "Resume recording" : "Pause recording"}
          >
            {paused ? "Resume" : "Pause"}
          </button>

          <label style={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none", marginLeft: 12 }}>
            <input
              type="checkbox"
              checked={hideTrackers}
              onChange={(e) => setHideTrackers(e.target.checked)}
              style={{ marginRight: 6 }}
            />
            <span style={{ fontSize: 13, color: "var(--neutral-text)" }} title="Hides OMID / impression beacons and measurement hosts in the Requests table">Hide Trackers</span>
          </label>

          {paused && (
            <span style={{ color: "var(--warn-text)", fontSize: 12, marginLeft: 8 }}>
              ⏸ Paused
            </span>
          )}
        </div>

        {/* Ad Server Integration moved to its own "Integration" tab (see view branch below)
            to free up vertical space in the Requests list. */}

        {/* Validation summary + filter bar — Raw requests only. Per-REQUEST counts:
            the Slots summary line carries the per-slot rollup. */}
        {viewMode === 'requests' && (
          <div style={styles.validationBar}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ color: "var(--neutral-text)" }}>Validation</span>
              <Pill tone={toneFromOverall(validationIndex.overall)}>
                {String(validationIndex.overall).toUpperCase()}
              </Pill>
              <span style={styles.countChip}>
                <span style={{ ...styles.countDot, background: "var(--success-text)" }} /> {validationIndex.counts.pass}
              </span>
              <span style={styles.countChip}>
                <span style={{ ...styles.countDot, background: "var(--warn-text)" }} /> {validationIndex.counts.warn}
              </span>
              <span style={styles.countChip}>
                <span style={{ ...styles.countDot, background: "var(--error-text)" }} /> {validationIndex.counts.fail}
              </span>
            </div>

            {viewMode === 'requests' && (
              <div style={styles.segWrap}>
                <button onClick={() => setStatusFilter("all")} style={{ ...styles.segBtn, ...(statusFilter === "all" ? styles.segActive : {}) }}>All</button>
                <button onClick={() => setStatusFilter("fail")} style={{ ...styles.segBtn, ...(statusFilter === "fail" ? styles.segActive : {}) }}>Fail</button>
                <button onClick={() => setStatusFilter("warn")} style={{ ...styles.segBtn, ...(statusFilter === "warn" ? styles.segActive : {}) }}>Warn</button>
                <button onClick={() => setStatusFilter("pass")} style={{ ...styles.segBtn, ...(statusFilter === "pass" ? styles.segActive : {}) }}>Pass</button>
              </div>
            )}

            {/* Step filter — only present when the proxy tagged at least one row
                with row.step (app-flow marker). Invisible/inert otherwise. */}
            {viewMode === 'requests' && hasStepData && (
              <select
                value={stepFilter}
                onChange={(e) => setStepFilter(e.target.value)}
                title="Filter by app flow step"
                style={{
                  padding: '4px 8px', borderRadius: 6, border: '1px solid var(--line)',
                  background: 'var(--bg)', color: 'var(--ink)', fontSize: 12,
                }}
              >
                <option value="all">All steps</option>
                <option value="(untagged)">(untagged)</option>
                {stepOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Ad Server Integration view (moved out of the request lists into its own tab) */}
        {viewMode === 'integration' && (
          <div style={{ overflow: "auto", flex: 1, minHeight: 0, padding: "8px" }}>
            {publisherSettings ? (
              <AdServerIntegrationCard
                data={publisherSettings}
                rows={data}
                serverMediationHealth={serverMediation?.mediationHealth || null}
                onOpenRequest={(row) => {
                  const list = data || [];
                  const idx = list.findIndex(r => r === row);
                  const target = { ...row, _k: row.requestId ?? row.id ?? idx };
                  setSel(target);
                  setRightTab('details');
                }}
              />
            ) : (
              <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 13 }}>
                No GMA publisher settings captured yet. Launch the app on a connected device —
                the SDK sends its getconfig/pubsetting call once per launch.
              </div>
            )}
            {/* Declared-vs-observed a3p coverage, reconciled across captured GAM requests */}
            {/* WP4c: same announced-fallback rule as the SDK Bidding rollup
                above — this panel resolves independently, so it can be showing
                the proxy's answer while that one shows the browser's. */}
            {!usingServerSecureSignals && (
              <div
                title="The proxy did not answer, so this coverage was computed in the browser. It may differ from what agents and CI report for the same session."
                style={{
                  fontSize: 11, color: '#854F0B', border: '1px solid #E5C07B',
                  borderRadius: 4, padding: '2px 8px', margin: '0 0 6px',
                  display: 'inline-block',
                }}
              >
                Secure Signals coverage computed locally — the proxy did not answer
              </div>
            )}
            <SecureSignalsHealthPanel
              health={secureSignalsHealth}
              onJumpTo={(key) => {
                const list = data || [];
                const idx = list.findIndex((r, i) => (r.requestId ?? r.id ?? i) === key);
                if (idx < 0) return;
                const target = { ...list[idx], _k: list[idx].requestId ?? list[idx].id ?? idx };
                setSel(target);
                setRightTab('inspect');
                setViewMode('requests');
              }}
            />
          </div>
        )}

        {/* Bid Health view (moved out of Lifecycle into its own tab) */}
        {viewMode === 'bidhealth' && (
          <div style={{ overflow: "auto", flex: 1, minHeight: 0 }}>
            <BidHealthView rows={allRowsStamped} />
          </div>
        )}

        {/* Slot story view (default) */}
        {viewMode === 'slots' && (
          <div style={{ overflow: "auto", flex: 1, minHeight: 0, padding: "8px 0" }}>
            <SlotStoryView
              allRows={allRowsStamped}
              allDevicesRows={allDevicesStamped}
              onJumpTo={handleLifecycleJump}
              focusSlot={focusSlot}
              onFocusResolved={(key) => { if (slotLink && String(slotLink.key) === String(key) && onSlotLinkDone) onSlotLinkDone(key); }}
              deviceId={readonly ? null : currentDevice}
              harId={harId}
            />
          </div>
        )}

        {/* Session overview: timeline, who won, funnel, findings (same graph as Slots) */}
        {viewMode === 'overview' && (
          <div style={{ overflow: "auto", flex: 1, minHeight: 0, padding: "8px 0" }}>
            <SlotStoryView
              mode="overview"
              allRows={allRowsStamped}
              allDevicesRows={allDevicesStamped}
              onJumpTo={handleLifecycleJump}
              onShowSlot={(key) => { setFocusSlot({ key, at: Date.now() }); setViewMode('slots'); }}
            />
          </div>
        )}

        {/* Video lifecycle view */}
        {viewMode === 'video' && (
          <div style={{ overflow: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <VideoLifecycleView
              allRows={(data || []).map((r, idx) => ({ ...r, _k: r.requestId ?? r.id ?? idx }))}
              onJumpTo={(rowKey, row) => {
                const allDataRows = (data || []).map((r, idx) => ({ ...r, _k: r.requestId ?? r.id ?? idx }));
                const target = allDataRows.find(r => r._k === rowKey) || row;
                if (!target) return;
                setSel(target);
                setSelLifecycle(null);
                setRightTab('details');
                // Stay in video view — keep selVideoSession so the Summary tab persists
              }}
              onOpenSummary={(session) => {
                // Store the session KEY; the live session is re-resolved each render
                // so the summary keeps updating as new beacons arrive.
                setSelVideoSessionKey(session._k ?? session.timestamp);
                setSelLifecycle(null);
                setRightTab('summary');
              }}
            />
          </div>
        )}

        {/* Table */}
        {viewMode === 'requests' && (
        <div style={{ overflow: "auto", flex: 1, minHeight: 0 }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 28, textAlign: 'center' }}>
                  {/* Select-all / deselect-all checkbox */}
                  <input
                    type="checkbox"
                    title={
                      tableRows.length === 0 ? "No rows to select"
                      : picked.length === tableRows.length ? "Deselect all"
                      : "Select all visible rows for HAR export (merged billing pings included)"
                    }
                    checked={tableRows.length > 0 && picked.length === tableRows.length}
                    ref={el => { if (el) el.indeterminate = picked.length > 0 && picked.length < tableRows.length; }}
                    onChange={() => {
                      if (picked.length === tableRows.length) {
                        setPicked([]);
                      } else {
                        setPicked(tableRows.slice()); // select all visible
                      }
                    }}
                  />
                </th>
                <th style={{ width: 34 }}>Val</th>
                <th style={{ width: 90 }}>Time</th>
                <th style={{ width: 70 }}>Code</th>
                <th style={{ width: 90 }}>Type / Format</th>
                <th>URL</th>
                <th style={{ width: 90 }}>Size</th>
                <th style={{ width: 90 }}>Total</th>
              </tr>
            </thead>
            <tbody ref={tableBodyRef}>
              {!groupByPage && foldMergedRows(tableRows, billMerge, expandedBills).map((r) => renderRow(r))}

              {groupByPage &&
                groups &&
                groups.order.map((g) => {
                  const isCollapsed = collapsed[g.key] === true; // expanded by default
                  const pLabel = g.pageLabel || pageLabelOf(g.pageUrl);
                  const visitsCount = g.visits.length;

                  return (
                    <React.Fragment key={g.key}>
                      {/* ── Page group header ── */}
                      <tr>
                        <td colSpan={8} style={styles.groupHeader}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button
                              style={styles.groupToggle}
                              onClick={() => setCollapsed((c) => ({ ...c, [g.key]: c[g.key] !== true }))}
                              title={isCollapsed ? "Expand" : "Collapse"}
                            >
                              {isCollapsed ? "▶" : "▼"}
                            </button>
                            {/* Favicon — null for proxy-served origins, which would
                                otherwise fetch a favicon off our own listener and
                                loop (see utils/faviconSafety.js). */}
                            {(() => {
                              const favUrl = faviconUrlFor(g.pageUrl);
                              if (!favUrl) return null;
                              return <img src={favUrl} alt="" width={13} height={13} style={{ borderRadius: 2, opacity: 0.7, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />;
                            })()}
                            <span style={{ fontWeight: 700, fontSize: 12 }}>{pLabel}</span>
                            <span style={{
                              fontSize: 10, color: "var(--muted)", background: "var(--bg)",
                              border: "1px solid var(--line)", borderRadius: 10, padding: "0 7px", lineHeight: "17px"
                            }}>
                              {visitsCount} visit{visitsCount !== 1 ? 's' : ''}
                            </span>
                            <span style={{ fontSize: 11, color: "var(--muted)" }}>
                              {fmtTime(g.firstSeen)} → {fmtTime(g.lastSeen)}
                            </span>
                            <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted)" }}>
                              {g.total} req
                            </span>
                          </div>
                        </td>
                      </tr>

                      {!isCollapsed &&
                        [...g.visits].slice().reverse().map((v) => {
                          const vKey = `${g.key}#v${v.idx}`;
                          const vCollapsed = !!collapsedVisit[vKey];
                          const count = v.items.length;
                          const started = fmtTime(v.startedAt);
                          // Visit accent colours cycle through a small palette
                          const VISIT_COLORS = ['#4285f4','#f5a623','#34a853','#ea4335','#9c27b0'];
                          const accentColor = VISIT_COLORS[(v.idx - 1) % VISIT_COLORS.length];

                          return (
                            <React.Fragment key={vKey}>
                              {/* ── Visit subheader ── */}
                              <tr>
                                <td colSpan={8} style={{ ...styles.visitHeader, borderLeft: `3px solid ${accentColor}` }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <button
                                      style={styles.visitToggle}
                                      onClick={() => setCollapsedVisit((c) => ({ ...c, [vKey]: !c[vKey] }))}
                                      title={vCollapsed ? "Expand visit" : "Collapse visit"}
                                    >
                                      {vCollapsed ? "▶" : "▼"}
                                    </button>
                                    {/* Visit number chip — colored to match accent */}
                                    <span style={{
                                      fontSize: 10, fontWeight: 800, letterSpacing: '0.05em',
                                      padding: '1px 8px', borderRadius: 10,
                                      background: `${accentColor}22`, color: accentColor,
                                      border: `1px solid ${accentColor}55`,
                                    }}>
                                      VISIT {v.idx}
                                    </span>
                                    {/* Reason badge */}
                                    {v.reason === 'navigation' && (
                                      <MetaBadge tone="success" title="Proxy detected a new content URL — user navigated">↩ Navigation</MetaBadge>
                                    )}
                                    {v.reason === 'returned' && (
                                      <MetaBadge tone="info" title="User returned to this page after visiting another">↩ Returned</MetaBadge>
                                    )}
                                    {v.reason === 'gap' && (
                                      <MetaBadge tone="neutral" title="No activity for an extended period">Resumed</MetaBadge>
                                    )}
                                    {v.visitIssues?.length > 0 && (
                                      <MetaBadge tone="warn" title={v.visitIssues[0].message}>⚠ Ads missing</MetaBadge>
                                    )}
                                    <span style={{ fontSize: 11, color: "var(--muted)" }}>{started}</span>
                                    <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                                      {hideTrackers && v.beaconCount > 0 && (
                                        <span
                                          style={{ fontSize: 10, color: "var(--muted)", background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 10, padding: "1px 8px", cursor: "pointer" }}
                                          title="Click to show OMID & impression beacons"
                                          onClick={(e) => { e.stopPropagation(); setHideTrackers(false); }}
                                        >
                                          {v.beaconCount} beacon{v.beaconCount !== 1 ? "s" : ""} hidden
                                        </span>
                                      )}
                                      <span style={{ fontSize: 11, color: "var(--muted)" }}>
                                        {count} req
                                      </span>
                                    </span>
                                  </div>
                                </td>
                              </tr>

                              {!vCollapsed && foldMergedRows([...v.items].slice().reverse(), billMerge, expandedBills).map((r) => renderRow(r))}
                            </React.Fragment>
                          );
                        })}
                    </React.Fragment>
                  );
                })}

              {tableRows.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 0, borderBottom: "none" }}>
                    <TrafficEmptyState
                      filtered={data.length > 0}
                      onConnect={() => setConnectOpen(true)}
                      onClearFilters={clearAllFilters}
                      filterSummary={activeFilterSummary({ query, statusFilter, show, stepFilter, hideTrackers, typeFilter, excludeCount: excludeList.length })}
                      serverIp={serverIp}
                      paused={paused}
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* RESIZER */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panels"
        title="Drag to resize (double-click to reset)"
        onMouseDown={startDrag}
        onDoubleClick={resetSplit}
        style={{ ...styles.resizer, display: showDetail ? undefined : 'none' }}
      />

      {/* RIGHT: details — collapsed out of the grid until something is selected */}
      <div style={{ ...styles.rightPane, display: showDetail ? 'flex' : 'none' }} className="right-pane">
        <div style={styles.detailsHeader}>
          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
            {/* Tab switcher — Summary available for a lifecycle slot OR a video session */}
            {(selLifecycle || (selVideoSession && viewMode === 'video')) && (
              <div style={{ display: 'flex', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
                {['summary', 'details'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setRightTab(tab)}
                    style={{
                      fontSize: 11, padding: '3px 12px', border: 'none', cursor: 'pointer', fontWeight: 600,
                      background: rightTab === tab ? 'var(--primary)' : 'transparent',
                      color: rightTab === tab ? '#fff' : 'var(--muted)',
                      textTransform: 'capitalize',
                    }}
                  >{tab}</button>
                ))}
              </div>
            )}
            {!selLifecycle && !(selVideoSession && viewMode === 'video') && <span>Request Details</span>}
            {selLifecycle && selLifecycleSource === 'local' && (
              <MetaBadge tone="warn" title="The proxy's lifecycle graph was unavailable, so this summary was rebuilt in the browser and may differ from the Slots view">local fallback</MetaBadge>
            )}
            {/* In slots/video mode, offer a quick escape to the full requests list */}
            {sel && (viewMode === 'slots' || viewMode === 'video') && (
              <button
                onClick={() => {
                  setQuery('');
                  setStatusFilter('all');
                  setHideTrackers(false);
                  setScrollToKey(sel._k);
                  setViewMode('requests');
                  if (viewMode === 'slots') setJumpedFromLifecycle(true);
                }}
                style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 6,
                  border: '1px solid var(--primary)', background: 'transparent',
                  color: 'var(--primary)', cursor: 'pointer', fontWeight: 600,
                }}
                title="Show this request in the full requests list"
              >
                Show in requests →
              </button>
            )}
          </div>
          {sel && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: 1, fontSize: 12, color: "var(--neutral-text)" }}>
              {/* Primary line: status · method · URL (URL is the focal element) */}
              <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                <Pill tone={statusTone(sel.status ?? sel.responseCode)}>
                  {sel.status ?? sel.responseCode ?? (sel.captureSource ? '—' : 0)}
                </Pill>
                <span style={{ fontWeight: 700, flexShrink: 0 }}>{sel.method || "GET"}</span>
                <span
                  title={sel.url}
                  style={{
                    flex: 1, minWidth: 0,
                    fontFamily: "monospace", fontSize: 11.5, color: "var(--ink)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}
                >
                  {sel.url}
                </span>
              </div>
              {/* Secondary line: encoding / mime / decode badges, compare action right-aligned */}
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {(() => {
                  const enc = getEncoding(sel);
                  return <Pill tone={enc === "identity" ? "neutral" : "warn"}>{enc}</Pill>;
                })()}
                {(() => {
                  const sm = shortMime(getMime(sel));
                  return sm ? <Pill tone="neutral">{sm}</Pill> : null;
                })()}
                {sel.isBinary ? <Pill tone="neutral">binary</Pill> : <Pill tone="success">decoded</Pill>}
                {/* quick add/remove to compare from details */}
                <button
                  style={{ ...styles.btn, marginLeft: "auto", padding: "3px 10px", fontSize: 11 }}
                  onClick={() => togglePick(sel, sel.requestId ?? sel.id ?? sel._k)}
                  title="Toggle selection for compare"
                >
                  {picked.some((p) => (p.requestId ?? p.id ?? p._k) === (sel.requestId ?? sel.id ?? sel._k))
                    ? "Unpick"
                    : "Pick for compare"}
                </button>
              </div>
            </div>
          )}
          {/* Close lives on the HEADER, not inside the tab row. Inside it, a
              narrow pane wrapped it onto a line of its own — an orphaned × with
              nothing beside it. Here it stays pinned top-right at any width. */}
          <button
            onClick={clearSelection}
            title="Close details"
            aria-label="Close details"
            style={{
              // marginLeft:auto is a no-op while the flex:1 URL block is present
              // (no free space to absorb) and pins × right when it is not — the
              // header renders with no row selected too.
              marginLeft: 'auto', flexShrink: 0, alignSelf: 'flex-start',
              width: 24, height: 24, lineHeight: '22px', textAlign: 'center',
              fontSize: 16, borderRadius: 6, cursor: 'pointer',
              border: '1px solid var(--line)', background: 'transparent', color: 'var(--muted)',
            }}
          >×</button>
        </div>

        {/* Summary tab — video session overview (video view) */}
        {rightTab === 'summary' && selVideoSession && viewMode === 'video' ? (
          <VideoSummaryPanel
            session={selVideoSession}
            onJumpTo={(rowKey, row) => {
              const allDataRows = (data || []).map((r, idx) => ({ ...r, _k: r.requestId ?? r.id ?? idx }));
              const target = allDataRows.find(r => r._k === rowKey) || row;
              if (target) { setSel(target); setRightTab('details'); }
            }}
          />
        ) : /* Summary tab — slot lifecycle overview */
        rightTab === 'summary' && selLifecycle ? (
          <LifecycleSummaryPanel
            lifecycle={selLifecycle}
            activeSel={sel}
            onSwitchToDetails={(rowKey) => {
              // If a specific phase rowKey was clicked, pre-select that row
              if (rowKey) {
                const target = allRowsStamped.find(r => r._k === rowKey);
                if (target) setSel(target);
              }
              setRightTab('details');
            }}
          />
        ) : rightTab === 'summary' && !selLifecycle && !selVideoSession ? (
          <div style={{ ...styles.emptyPane, flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 22 }}>📋</span>
            <span style={{ fontWeight: 600, color: 'var(--neutral-text)' }}>No slot selected</span>
            <span style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', maxWidth: 200 }}>
              Click a slot's <strong>Init</strong> or refresh chip to see its lifecycle summary here
            </span>
          </div>
        ) : !sel ? (
          <div style={styles.emptyPane}>
            Select a request to view details
          </div>
        ) : (
          <>
            {/* Details tab — raw request */}
            {(rightTab === 'details' || !selLifecycle) && (
              <>
                <BidCorrelationBanner
                  corr={correlationMap.get(sel._k)}
                  sdkWinner={(() => {
                    // Who actually won GAM's auction, when an SDK bidder did (Meta, Liftoff):
                    // the slot's mediation phase, else the GAM response's adapter label.
                    const m = selLifecycle?.phases?.mediation;
                    if (m?.winner) return (m.buyers || []).find((b) => b && b.id === m.winner)?.name || String(m.winner);
                    return sel?.validData?.expanded?.adapterLabel || null;
                  })()}
                  onJumpTo={(key) => {
                    const target = rows.find(r => r._k === key);
                    if (target) { setSel(target); onRowOpen?.(target); }
                  }}
                />
                <RequestDetails
                  req={sel}
                  onDiffClick={() => setDiffToolOpen(true)}
                  onReplayClick={() => setReplayOpen(true)}
                  headline={buildRowHeadline(sel, {
                    slotIndex: rowSlotIndex || buildRowSlotIndex(buildSlotLifecycles(allRowsStamped)),
                    rowsByKey: rowsByKeyForHeadline,
                    billGroups: billMerge,
                  })}
                />
              </>
            )}
          </>
        )}
      </div>

      {/* Compare modal */}
      {compareOpen && (
        <CompareRequestsModal
          open={compareOpen}
          onClose={() => {
            setCompareOpen(false);
            setImportedCompare(null);
            clearPicked(); // auto-deselect rows when modal is dismissed
          }}
          a={importedCompare?.a || picked[0]}
          b={importedCompare?.b || picked[1]}
        />
      )}

      {/* Diff vs pasted — detail-pane action for the selected row */}
      {sel && (
        <DiffToolModal
          open={diffToolOpen}
          onClose={() => setDiffToolOpen(false)}
          request={sel}
        />
      )}

      {/* Replay — detail-pane action for the selected row */}
      {sel && (
        <ReplayModal
          open={replayOpen}
          onClose={() => setReplayOpen(false)}
          request={sel}
        />
      )}

      <ConnectDeviceModal
        isOpen={connectOpen}
        onClose={() => setConnectOpen(false)}
        serverIp={serverIp}
      />

      {/* Live observer — hidden until tool is production-ready */}
      {/* <LiveObserverPanel rows={rows} clearSignal={observerClearSignal} onJumpTo={(rowKey) => { const target = rows.find(r => (r._k ?? r.requestId ?? r.id) === rowKey); if (target) { setSel(target); setRightTab('details'); } }} /> */}
    </div>
  );
}


/* ── BidCorrelationBanner ────────────────────────────────────────────────── */
function BidCorrelationBanner({ corr, onJumpTo, sdkWinner = null }) {
  if (!corr) return null;

  const isGam = corr.role === 'gam';
  // The link proves Prebid's winner was SENT to GAM as targeting (hb_bidder/hb_pb
  // match), not that it won GAM's auction: an SDK bidder can still win (Meta
  // beat triplelift 0.95, 2026-10-17). Name the real outcome; never say "Win" here.
  const lostToSdk = isGam && !!sdkWinner;
  const accent = corr.discrepancy ? 'var(--error-text)' : lostToSdk ? 'var(--ink)' : 'var(--success-text)';
  const bg     = corr.discrepancy ? 'var(--error-bg)' : lostToSdk ? 'var(--info-bg)' : 'var(--success-bg)';
  const border = corr.discrepancy ? 'var(--error-border)' : 'var(--success-border)';

  const priceDisplay = corr.prebidHbPb
    ? `$${corr.prebidHbPb}`
    : corr.prebidPrice != null
      ? `$${Number(corr.prebidPrice).toFixed(2)}`
      : '—';

  const jumpKey = isGam ? corr.prebidRowKey : corr.gamRowKey;
  const jumpLabel = isGam ? 'Jump to Prebid auction ↗' : 'Jump to GAM request ↗';

  return (
    <div style={{
      margin: '8px 12px 0',
      padding: '10px 14px',
      borderRadius: 8,
      border: `1px solid ${border}`,
      background: bg,
      fontSize: 12,
      lineHeight: 1.5,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontWeight: 700, color: accent }}>
          {isGam
            ? (lostToSdk ? `🔗 Prebid bid sent to GAM — lost to ${sdkWinner}` : '🔗 Prebid bid sent to GAM')
            : '🔗 Prebid Auction Linked to GAM'}
        </span>
        <button
          onClick={() => onJumpTo(jumpKey)}
          style={{
            fontSize: 11, padding: '2px 10px', borderRadius: 6,
            border: `1px solid ${border}`, background: 'var(--card)',
            cursor: 'pointer', color: 'var(--ink)', fontWeight: 600,
          }}
        >
          {jumpLabel}
        </button>
      </div>

      {/* Data grid — theme tokens only; the banner bg follows the dark/light theme */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px 16px' }}>
        <div>
          <span style={{ color: 'var(--muted)', fontSize: 11 }}>Bidder</span><br />
          <b style={{ color: accent }}>{corr.bidder || '—'}</b>
        </div>
        <div>
          <span style={{ color: 'var(--muted)', fontSize: 11 }}>Prebid hb_pb</span><br />
          <b style={{ color: accent }}>{priceDisplay}</b>
        </div>
        <div>
          <span style={{ color: 'var(--muted)', fontSize: 11 }}>GAM hb_pb</span><br />
          <b style={{ color: accent }}>
            {corr.gamHbPb ? `$${corr.gamHbPb}` : '—'}
            {corr.gamHbPb && !corr.discrepancy ? ' ✓' : ''}
            {corr.discrepancy ? ' ✗ mismatch' : ''}
          </b>
        </div>
      </div>

      {/* Cache ID */}
      <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
        hb_cache_id: <code style={{ fontSize: 10, background: 'var(--surface)', color: 'var(--ink)', padding: '1px 4px', borderRadius: 3 }}>
          {corr.cacheId}
        </code>
      </div>

      {corr.discrepancy && (
        <div style={{ marginTop: 6, color: 'var(--error-text)', fontWeight: 600, fontSize: 11 }}>
          ⚠ Price bucket mismatch — Prebid set {priceDisplay} but GAM received ${corr.gamHbPb}. Check Prebid targeting cache.
        </div>
      )}
    </div>
  );
}

/* ── MissingBodyNote — explains WHY a body isn't shown ─────────────────────
   Distinguishes a body that was captured-then-trimmed to save memory
   (recoverable — re-capture live / open from HAR) from one the proxy never
   got (gzip/QUIC/HTTP-3 or a capture miss). Without this the detail pane just
   rendered nothing, so a trimmed body looked identical to a real capture gap. */
/**
 * One body block, one style. The detail pane renders a request/response body
 * from two different code paths — the normal one, and MissingBodyNote's
 * "recovered from local stash" path — and they had drifted apart on nine
 * properties: font size, font family, padding, radius, border, max height,
 * line height, colour, and margin.
 *
 * `margin: 0` is the load-bearing one: a bare <pre> inherits the browser
 * default `margin: 1em 0`, so a recovered body sat ~16px lower and pushed the
 * next section down, which read as a layout bug rather than a style drift.
 */
const BODY_PRE_STYLE = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11.5,
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '10px 12px',
  overflowX: 'auto',
  overflowY: 'auto',
  maxHeight: 320,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  color: 'var(--ink)',
  margin: 0,
  lineHeight: 1.55,
};

function MissingBodyNote({ row, which }) {
  const { state, message } = bodyState(row, which);
  // On-demand load of a trimmed body from the IndexedDB stash (storage/db.js).
  const [loaded, setLoaded] = useState(null);   // string | null
  const [recovered, setRecovered] = useState(false); // loaded via the silent auto-attempt below, vs the manual button
  const [loading, setLoading] = useState(false);
  const [tried, setTried] = useState(false);
  const requestId = row?.requestId || row?.id;
  // Reset when the selected row / body side changes, so a loaded body from a
  // previously-inspected row never leaks into this one.
  useEffect(() => { setLoaded(null); setRecovered(false); setTried(false); setLoading(false); }, [requestId, which]);

  // B7 (body-coverage-spec WP3): the IndexedDB stash (`stashBodies`/`loadBody`
  // in storage/db.js) may still hold a trimmed row's bytes even though the
  // in-memory/history copy was cut down. Attempt recovery automatically —
  // cancel-safe (ignore a stale response if the row changes mid-flight) and
  // completely silent on a miss: the normal "trimmed" label above just stays
  // as-is, no error is surfaced for a background attempt the user never asked
  // for. (The manual "Load full body" button below still reports a miss,
  // since that's a user-initiated action.)
  useEffect(() => {
    if (state !== 'trimmed' || !requestId) return;
    let cancelled = false;
    (async () => {
      try {
        const rec = await loadBody(requestId);
        if (cancelled) return;
        const val = rec ? (which === 'request' ? (rec.requestBody ?? rec.requestBodyParsed) : rec.responseBody) : null;
        if (val != null && val !== '') {
          setLoaded(typeof val === 'string' ? val : JSON.stringify(val));
          setRecovered(true);
        }
      } catch { /* silent — stash miss/error just keeps the current "trimmed" label */ }
    })();
    return () => { cancelled = true; };
  }, [state, requestId, which]);

  if (state === 'present') return null;
  const trimmed = state === 'trimmed';

  // Once loaded, render the recovered body just like a normal body block.
  if (loaded != null && loaded !== '') {
    return (
      <>
        {recovered && (
          <div style={{ fontSize: 10, color: 'var(--muted)', fontStyle: 'italic', marginBottom: 4 }}>
            ↺ recovered from local stash
          </div>
        )}
        <pre style={BODY_PRE_STYLE}>
          {(() => { try { return JSON.stringify(JSON.parse(loaded), null, 2); } catch { return String(loaded).slice(0, 200000); } })()}
        </pre>
      </>
    );
  }

  const loadFull = async () => {
    setLoading(true);
    try {
      const rec = await loadBody(requestId);
      const val = rec ? (which === 'request' ? (rec.requestBody ?? rec.requestBodyParsed) : rec.responseBody) : null;
      if (val != null && val !== '') setLoaded(typeof val === 'string' ? val : JSON.stringify(val));
      else setTried(true);
    } catch { setTried(true); }
    finally { setLoading(false); }
  };

  return (
    <div style={{
      fontSize: 11, lineHeight: 1.5, padding: '6px 8px', borderRadius: 4,
      border: `1px solid ${trimmed ? 'var(--warn-border, #fde68a)' : 'var(--line)'}`,
      background: trimmed ? 'var(--warn-bg, #fffbeb)' : 'var(--bg)',
      color: trimmed ? 'var(--warn-text, #92400e)' : 'var(--muted)',
    }}>
      <b>{trimmed ? '✂ Body trimmed to save memory' : '⚠ Body not captured'}</b><br />
      {message}
      {trimmed && (
        <div style={{ marginTop: 6 }}>
          <button
            onClick={loadFull}
            disabled={loading}
            style={{ fontSize: 10, padding: '2px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'transparent', color: 'var(--primary)', cursor: loading ? 'default' : 'pointer' }}
          >{loading ? 'Loading…' : 'Load full body'}</button>
          {tried && <span style={{ marginLeft: 8, color: 'var(--muted)' }}>Not in the local store (older than on-device retention).</span>}
        </div>
      )}
    </div>
  );
}

/* ── AdInspectView — smart ad-specific request inspector ──────────────────── */
function AdInspectView({ req }) {
  const [showRaw, setShowRaw] = useState(false);
  const rt = req.requestType || req.type || '';
  const status = req.status ?? req.responseCode ?? 0;
  const failed = status >= 400;
  const vd = req.validData || {};
  const fmtMs = (n) => n == null ? '—' : n >= 1000 ? `${(n/1000).toFixed(1)}s` : `${Math.round(n)}ms`;

  // Parse URL query string into flat object
  const qs = useMemo(() => {
    const out = {};
    try { new URL(req.url).searchParams.forEach((v, k) => { out[k] = v; }); } catch {}
    return out;
  }, [req.url]);

  // Parse request body (JSON or pre-parsed)
  const body = useMemo(() => {
    const b = req.requestBodyParsed || req.requestBody;
    if (!b) return null;
    try { return typeof b === 'object' ? b : JSON.parse(b); } catch { return null; }
  }, [req.requestBodyParsed, req.requestBody]);

  // Decode cust_params from URL (GAM, IMA style)
  const custParams = useMemo(() => {
    const raw = qs.cust_params || '';
    if (!raw) return {};
    const out = {};
    try {
      let decoded = raw;
      try { decoded = decodeURIComponent(decoded); } catch {}
      try { decoded = decodeURIComponent(decoded); } catch {}
      decoded.split('&').forEach(pair => {
        const eq = pair.indexOf('=');
        if (eq < 0) return;
        const k = pair.slice(0, eq);
        const v = pair.slice(eq + 1);
        try { out[decodeURIComponent(k)] = decodeURIComponent(v); } catch { out[k] = v; }
      });
    } catch {}
    return out;
  }, [qs.cust_params]);

  const KV = ({ label, val, mono, warn, dim }) => {
    if (val == null || val === '') return null;
    return (
      <div style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: '1px solid var(--line)', alignItems: 'flex-start', minWidth: 0 }}>
        <span style={{ fontSize: 11, color: 'var(--muted)', width: 140, flexShrink: 0, paddingTop: 1 }}>{label}</span>
        <span style={{
          fontSize: 11, color: warn ? 'var(--warn-text)' : dim ? 'var(--muted)' : 'var(--ink)',
          fontFamily: mono ? 'var(--font-mono)' : undefined,
          wordBreak: 'break-all', flex: 1,
        }}>{val}</span>
      </div>
    );
  };

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4, paddingBottom: 3, borderBottom: '2px solid var(--line)' }}>{title}</div>
      {children}
    </div>
  );

  const urlObj = useMemo(() => { try { return new URL(req.url); } catch { return null; } }, [req.url]);
  const displayUrl = urlObj ? `${urlObj.hostname}${urlObj.pathname.slice(0, 50)}` : (req.url || '').slice(0, 80);

  // ── GAM Inspect ──────────────────────────────────────────────────────────
  const GamInspect = () => {
    const sz = (qs.sz || '').split('|').filter(Boolean);
    const iu = qs.iu || '—';
    // GMA SDK sends correlator as 'c' not 'correlator'
    const correlator = vd?.correlator || qs.c || qs.correlator;
    // ATT status from attmas param (human-readable: "authorized" | "denied" | "notdetermined")
    const attmas = qs.attmas || vd?.attmas;
    // Refresh detection: prev_iu_szs present = refresh; seq_num = sequence number
    const isRefresh = !!(qs.prev_iu_szs || qs.prev_scp);
    const refreshCount = custParams.refresh_count;
    return (
      <>
        <Section title="Ad Request">
          <KV label="Ad Unit" val={iu} mono />
          <KV label="Sizes" val={sz.join(' · ') || '—'} />
          <KV label="Correlator" val={correlator} mono />
          <KV label="PPID" val={qs.ppid} mono />
          <KV label="us_privacy" val={qs.us_privacy} />
          <KV label="ATT (attmas)" val={attmas} warn={attmas && attmas !== 'authorized'} />
          <KV label="OMID" val={qs.omid_p} />
          <KV label="SKAN" val={qs.skan} />
          <KV label="Request type" val={isRefresh ? `Refresh${refreshCount != null ? ` #${refreshCount}` : ''}` : 'Initial load'} />
        </Section>
        {/* All cust_params — flat, no hb/amzn split */}
        {Object.keys(custParams).length > 0 && (
          <Section title={`cust_params (${Object.keys(custParams).length})`}>
            {Object.entries(custParams).map(([k, v]) => {
              const isHb = /^hb_/i.test(k);
              const isAmzn = /^amzn/i.test(k);
              return (
                <KV key={k} label={k}
                  val={v.length > 80 ? v.slice(0, 80) + '…' : v}
                  mono
                  warn={isHb || isAmzn}
                />
              );
            })}
            <button
              onClick={() => { copyToClipboard(Object.entries(custParams).map(([k, v]) => `${k}=${v}`).join('\n')); }}
              style={{ fontSize: 10, marginTop: 4, padding: '2px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}
            >Copy cust_params</button>
          </Section>
        )}
        {/* All URL query params — collapsed */}
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 10, color: 'var(--muted)', padding: '3px 0', userSelect: 'none' }}>
            All query params ({Object.keys(qs).length})
          </summary>
          <div style={{ marginTop: 4 }}>
            {Object.entries(qs).map(([k, v]) => (
              <KV key={k} label={k} val={v.length > 80 ? v.slice(0, 80) + '…' : v} mono dim />
            ))}
          </div>
        </details>
      </>
    );
  };

  // ── Prebid Inspect ───────────────────────────────────────────────────────
  // NOTE: Prebid POST bodies may be gzip-compressed so `body` can be null.
  // ALL fields should prefer vd.tables.* and vd.summary.* (always available).
  const PrebidInspect = () => {
    // vd.tables.imps[0] — imp details (GPID, sizes, stored request)
    const imp0vd = vd?.tables?.imps?.[0] || {};
    // vd.summary — SDK metadata, ATT, EIDs
    const sum = vd?.summary || {};
    // Fall back to body if vd isn't populated (older history)
    const imp0body = body?.imp?.[0];
    const gpid = imp0vd.gpid !== '-' ? imp0vd.gpid : (imp0body?.ext?.gpid || null);
    const storedId = imp0vd.stored || imp0body?.ext?.prebid?.storedrequest?.id || null;
    const bannerSizes = imp0vd.sizes || (imp0body?.banner?.format || []).map(f => `${f.w}×${f.h}`).join(', ') || null;
    const sdkV = sum.prebidSdkVersion || sum.prebid;
    const sdkSrc = sum.prebidSource;
    // ATT: device.ext.atts (real field) — vd.summary.att is pre-extracted by proxy
    const att = sum.att;
    const attLabel = sum.attLabel;
    // Identity signals
    const eidCount = sum.eidCount ?? 0;
    const eidSources = sum.eidSources || [];
    const eidsTable = vd?.tables?.eids || [];
    // Device/App
    const deviceStr = sum.device;
    const appName = sum.name || body?.app?.name;
    const appBundle = body?.app?.bundle || null;
    const publisherId = body?.app?.publisher?.id || null;
    // IFA — only relevant when ATT=3
    const ifa = body?.device?.ifa || null;
    const lmt = body?.device?.lmt;
    // Consent
    const usPrivacy = body?.regs?.ext?.us_privacy || body?.regs?.us_privacy;
    const gdpr = body?.user?.ext?.consent;
    // Bids — always from vd.tables
    const bids = vd?.tables?.bids || [];
    return (
      <>
        <Section title="Prebid Auction">
          {(sdkV || sdkSrc) && <KV label="SDK" val={`${sdkSrc || 'prebid-mobile'} v${sdkV || '?'}`} />}
          <KV label="App" val={appName} />
          {appBundle && <KV label="Bundle / Store ID" val={appBundle} mono />}
          {publisherId && <KV label="Publisher ID" val={publisherId} mono />}
          {deviceStr && <KV label="Device" val={deviceStr} />}
          <KV label="ATT" val={att != null ? `${att} — ${attLabel || '?'}` : null} warn={att != null && att !== 3} />
          {att === 3 && ifa && <KV label="IFA" val={ifa} mono dim />}
          {lmt != null && <KV label="LMT" val={String(lmt)} warn={lmt === 1} />}
        </Section>
        <Section title="Impression">
          <KV label="GPID" val={gpid} mono />
          <KV label="Stored Request ID" val={storedId} mono />
          <KV label="Banner Sizes" val={bannerSizes || '—'} />
          <KV label="Floors" val={imp0vd.floor !== '-' && imp0vd.floor != null ? String(imp0vd.floor) : null} />
        </Section>
        <Section title="Consent">
          <KV label="us_privacy" val={usPrivacy} warn={usPrivacy === '1YNN' ? false : !!usPrivacy} />
          {gdpr && <KV label="TCF consent" val={gdpr.slice(0, 20) + '…'} mono dim />}
          <KV label={`EIDs (${eidCount})`}
            val={eidSources.length > 0 ? eidSources.join(', ') : 'none'}
            warn={eidCount === 0} />
        </Section>
        {eidsTable.length > 0 && (
          <Section title="Identity Sources">
            {eidsTable.map((e, i) => (
              <KV key={i} label={e.source} val={`${e.uids} uid${e.uids !== 1 ? 's' : ''}`} mono />
            ))}
          </Section>
        )}
        {bids.length > 0 && (
          <Section title={`Bid Response (${bids.filter(b => b.price > 0).length}/${bids.length} bids)`}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--line)' }}>
                    {['Bidder', 'Status', 'CPM', 'Size', 'Latency'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bids.map((b, i) => {
                    const isWon = b.status === 'won';
                    const isFail = b.status === 'timeout';
                    const isNoBid = b.status === 'nobid';
                    const rowColor = isWon ? 'var(--success-bg)' : isFail ? 'var(--error-bg)' : 'transparent';
                    return (
                      <tr key={i} style={{ background: rowColor, borderBottom: '1px solid var(--line)', opacity: isNoBid ? 0.5 : 1 }}>
                        <td style={{ padding: '4px 8px', fontWeight: isWon ? 700 : 400 }}>{b.seat || '—'}{isWon ? ' 🏆' : ''}</td>
                        <td style={{ padding: '4px 8px', color: isWon ? 'var(--success-text)' : isFail ? 'var(--error-text)' : 'var(--muted)' }}>{b.status}</td>
                        <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>{b.price != null && b.price > 0 ? `$${Number(b.price).toFixed(3)}` : '—'}</td>
                        <td style={{ padding: '4px 8px' }}>{b.size || '—'}</td>
                        <td style={{ padding: '4px 8px', color: 'var(--muted)' }}>{b.latencyMs != null ? fmtMs(b.latencyMs) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </>
    );
  };

  // ── Amazon APS Inspect ───────────────────────────────────────────────────
  // vd = validateAmazonRequest() result = { valid, issues, adUnit, expanded, insights }
  // vd.expanded = full parsed APS request body
  // vd.insights.values = { appId, package, version, sdk, mediation, userAgent, model, skadnCount }
  const AmazonInspect = () => {
    const exp = vd?.expanded || {};
    const ins = vd?.insights?.values || {};
    // Prefer vd.expanded, fall back to body (body may also be available in live session)
    const appId = exp.appId || body?.appId || ins.appId?.value;
    const slots = exp.slots || body?.slots || [];
    const adsdk = ins.sdk?.value || exp.adsdk || body?.adsdk;
    const mediationName = ins.mediation?.value || exp.pj?.mediationName || body?.pj?.mediationName;
    const appPkg = ins.package?.value || exp.pkg?.pn || body?.pkg?.pn;
    const appVer = ins.version?.value || exp.pkg?.vn || body?.pkg?.vn;
    const model = ins.model?.value || exp.dinfo?.model || body?.dinfo?.model;
    // Platform detection: skadn array with entries = iOS; absent or empty = Android
    const skadnCount = ins.skadnCount?.value ?? exp.skadn?.length ?? body?.skadn?.length ?? 0;
    const platform = skadnCount > 0 ? 'iOS' : 'Android';
    // ATT — APS stores it in pj.att
    const att = exp.pj?.att || body?.pj?.att;
    const ATT_LABELS = { '0': 'Not Determined', '1': 'Restricted', '2': 'Denied', '3': 'Authorized' };
    // Privacy
    const usPrivacy = exp.us_privacy || body?.us_privacy;
    const gdprC = exp.gdpr?.c || body?.gdpr?.c;
    // Bid outcome from issues. validateAmazonRequest() issues are now severity-tagged
    // objects ({severity, code, note}) mirroring validateGAMRequest — a few legacy
    // entries may still be plain strings, so normalize before pattern-matching.
    const issueText = (i) => (typeof i === 'string' ? i : (i?.note || i?.message || i?.msg || ''));
    const issueList = vd?.issues || [];

    // Bid outcome — two sources, in priority order:
    // 1. validData.bidOutcome: extracted at capture time and persisted through history body-strip
    // 2. req.responseBody: raw response, only available during live capture (stripped from disk)
    const persistedOutcome = vd?.bidOutcome ?? null;
    let respParsed = null;
    try { respParsed = typeof req.responseBody === 'string' ? JSON.parse(req.responseBody) : req.responseBody; } catch {}
    const liveTokens = Array.isArray(respParsed) ? respParsed.filter(t => t?.amzn_b || t?.b) : null;

    const hasBidToken = !!(persistedOutcome?.hasBid || (liveTokens && liveTokens.length > 0));
    const bidTokenCount = persistedOutcome?.tokenCount ?? liveTokens?.length ?? 0;
    const bodyKnown = persistedOutcome !== null || respParsed !== null;

    return (
      <>
        <Section title="Amazon APS Request">
          <KV label="App ID" val={appId} mono />
          <KV label="SDK Version" val={adsdk} />
          <KV label="Mediation" val={mediationName} />
          <KV label="Platform" val={`${platform} (SKAdN: ${skadnCount})`} />
          <KV label="ATT" val={att != null ? `${att} — ${ATT_LABELS[String(att)] || '?'}` : null} warn={att != null && att !== '3' && att !== 3} />
          <KV label="Package" val={appPkg} mono />
          <KV label="App Version" val={appVer} />
          <KV label="Device Model" val={model} />
          <KV label="us_privacy" val={usPrivacy} />
          {gdprC && <KV label="GDPR consent" val={gdprC.slice(0, 20) + '…'} mono dim />}
        </Section>
        <Section title={`Slots (${slots.length})`}>
          {slots.length === 0
            ? <span style={{ color: 'var(--muted)', fontSize: 11 }}>No slot data — request may be a creative fetch or SDK load</span>
            : slots.map((s, i) => (
                <KV key={i} label={`Slot ${i + 1}`}
                  val={`${s.slot || s.slotId || '?'} · ${s.sz || (s.sizes || []).join(', ') || '?'} · ${(s.supportedMediaTypes || []).join(',') || 'DISPLAY'}`}
                  mono />
              ))
          }
        </Section>
        <Section title="Bid Outcome">
          {hasBidToken
            ? <div style={{ fontSize: 11, color: 'var(--success-text)', padding: '4px 0' }}>
                ✓ Bid received — {bidTokenCount} token{bidTokenCount !== 1 ? 's' : ''}
                {persistedOutcome?.firstToken && (
                  <span style={{ color: 'var(--muted)', marginLeft: 8, fontFamily: 'monospace', fontSize: 10 }}>
                    ({persistedOutcome.firstToken})
                  </span>
                )}
              </div>
            : <div style={{ fontSize: 11, color: 'var(--muted)', padding: '4px 0' }}>
                {bodyKnown ? 'No bid' : 'Response not captured'}
              </div>
          }
          {issueList.length > 0 && issueList.map((iss, i) => {
            const sev = typeof iss === 'string' ? 'issue' : (iss.severity || iss.level || 'issue');
            const text = issueText(iss) || String(iss);
            const isAdvisory = sev === 'optional' || sev === 'info' || sev === 'pass';
            return <KV key={i} label={`Issue ${i+1}`} val={`[${sev}] ${text}`} warn={!isAdvisory} />;
          })}
        </Section>
      </>
    );
  };

  // ── IMA / SSAI Inspect ───────────────────────────────────────────────────
  const ImaInspect = () => {
    const contentUrl = qs.content_url || qs.url || vd?.content_url;
    const adTagUrl = qs.ad_tag_url || qs.adTagUrl;
    const playerName = vd?.ad_player_name || qs.ad_player_name;
    const msid = vd?.msid || qs.msid || qs.dfp_msid;
    return (
      <>
        <Section title="IMA / SSAI Request">
          <KV label="Content URL" val={contentUrl} mono />
          <KV label="Ad Tag URL" val={adTagUrl} mono />
          <KV label="Player" val={playerName} />
          <KV label="MSID" val={msid} mono />
          <KV label="OMID" val={vd?.omid || qs.omid} />
          <KV label="us_privacy" val={qs.us_privacy || vd?.us_privacy} />
          <KV label="GDPR consent" val={qs.gdpr_consent || vd?.gdpr_consent} mono />
        </Section>
        {Object.keys(custParams).length > 0 && (
          <Section title="cust_params">
            {Object.entries(custParams).map(([k, v]) => <KV key={k} label={k} val={v} mono />)}
            <button
              onClick={() => { copyToClipboard(Object.entries(custParams).map(([k, v]) => `${k}=${v}`).join('\n')); }}
              style={{ fontSize: 10, marginTop: 4, padding: '2px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}
            >Copy cust_params</button>
          </Section>
        )}
      </>
    );
  };

  // ── Generic / Impression / Click Inspect ────────────────────────────────
  const GenericInspect = () => {
    const pairs = Object.entries(qs).slice(0, 30);
    return (
      <Section title="Query Parameters">
        {pairs.length === 0
          ? <span style={{ color: 'var(--muted)', fontSize: 11 }}>GET request with no query params</span>
          : pairs.map(([k, v]) => <KV key={k} label={k} val={v} mono />)
        }
      </Section>
    );
  };

  // ── A3P (Secure Signals) decoded — shown for every GAM and IMA request ─────
  const A3pSection = () => {
    const rawA3p = qs.a3p || qs.a3 || '';
    const dec = decodeA3p(rawA3p);
    // Server-side reconciliation: declared-in-Publisher-Settings signals that did
    // NOT reach GAM on this request (codes set by validateGAMRequest.js).
    const ssIssues = (vd?.issues || []).filter(
      i => typeof i?.code === 'string' && i.code.startsWith('secure_signal_a3p')
    );
    return (
      <Section title="A3P · Secure Signals (decoded)">
        {ssIssues.length > 0 && (
          <div style={{ marginBottom: 8, padding: '6px 9px', borderRadius: 6, background: 'var(--warn-bg)', border: '1px solid var(--warn-border)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--warn-text)', marginBottom: 2 }}>
              ⚠ {ssIssues.length} declared signal{ssIssues.length > 1 ? 's' : ''} missing from this request
            </div>
            {ssIssues.map((i, n) => (
              <div key={n} style={{ fontSize: 10, color: 'var(--warn-text)', lineHeight: 1.4 }}>
                {i.note || i.message}
              </div>
            ))}
          </div>
        )}
        {!dec.present ? (
          <div style={{ fontSize: 11, color: 'var(--muted)', padding: '4px 0' }}>
            No <code>a3p</code> param on this request — no secure signals were attached.
          </div>
        ) : dec.error ? (
          <div style={{ fontSize: 11, color: 'var(--warn-text)', padding: '4px 0' }}>
            a3p present ({dec.rawLen} chars) but could not be base64-decoded.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 10, color: 'var(--muted)' }}>{dec.bytes} bytes · {dec.rawLen} b64 chars</span>
              {dec.detected.length > 0
                ? dec.detected.map(d => (
                    <span key={d} style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: 'var(--success-bg)', color: 'var(--success-text)', border: '1px solid var(--success-border)' }}>{d}</span>
                  ))
                : <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: 'var(--warn-bg)', color: 'var(--warn-text)', border: '1px solid var(--warn-border)' }}>no known signal source detected</span>}
              <button
                type="button"
                title="Copy raw a3p (base64)"
                onClick={(e) => { const ok = copyToClipboard(dec.raw); const b = e.currentTarget; b.textContent = ok ? 'copied' : 'failed'; setTimeout(() => { try { b.textContent = 'copy b64'; } catch {} }, 1200); }}
                style={{ marginLeft: 'auto', fontSize: 10, padding: '1px 6px', borderRadius: 4, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--muted)', cursor: 'pointer' }}
              >copy b64</button>
            </div>
            {dec.adapters && dec.adapters.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                  {dec.adapters.length} secure-signal adapter{dec.adapters.length > 1 ? 's' : ''}:
                </span>
                {dec.adapters.map(a => (
                  <span key={a.cls} title={a.cls}
                    style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 999, background: 'var(--neutral-bg)', color: 'var(--neutral-text)', border: '1px solid var(--line)', cursor: 'default' }}>
                    {a.label}
                  </span>
                ))}
              </div>
            )}
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 10, color: 'var(--muted)' }}>Show decoded bytes</summary>
              <div style={{ fontSize: 10, color: 'var(--muted)', margin: '4px 0 3px' }}>Decoded (printable bytes; · = non-printable)</div>
              <pre style={{
                margin: 0, fontFamily: 'var(--font-mono)', fontSize: 10, lineHeight: 1.5,
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                maxHeight: 220, overflowY: 'auto',
                background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6, padding: '6px 8px',
              }}>{dec.printable}</pre>
            </details>
          </>
        )}
      </Section>
    );
  };

  return (
    <div style={{ fontSize: 12 }}>

      {/* Status bar */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
          background: failed ? 'var(--error-bg)' : 'var(--success-bg)',
          color: failed ? 'var(--error-text)' : 'var(--success-text)',
          border: `1px solid ${failed ? 'var(--error-border)' : 'var(--success-border)'}`,
        }}>{status || (failed ? 'ERR' : 'OK')}</span>
        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: 'var(--neutral-bg)', color: 'var(--neutral-text)', border: '1px solid var(--line)', flexShrink: 0 }}>{(req.method || 'GET').toUpperCase()}</span>
        <span style={{ fontSize: 11, color: 'var(--muted)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayUrl}</span>
        <span style={{ fontSize: 10, color: 'var(--muted)', flexShrink: 0 }}>{fmtMs(req.durationMs)}</span>
      </div>

      {/* Error detail — only when request failed */}
      {failed && (
        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 6, background: 'var(--error-bg)', border: '1px solid var(--error-border)' }}>
          <div style={{ fontWeight: 700, color: 'var(--error-text)', marginBottom: 4 }}>✗ HTTP {status} — Request Failed</div>
          {req.responseHeaders && (
            <details>
              <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--muted)' }}>Response headers</summary>
              <div style={{ marginTop: 6 }}>
                {Object.entries(req.responseHeaders).map(([k, v]) => (
                  <div key={k} style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--muted)', padding: '1px 0' }}>
                    <b>{k}:</b> {v}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Video Implementation Passport — at-a-glance video request summary */}
      {['gam_video', 'ima', 'amazon_video', 'springserve_ad_request'].includes(rt) && <VideoPassport row={req} />}

      {/* Type-specific parsed sections */}
      {(rt === 'google' || rt === 'google_impression' || rt === 'google_click') && <GamInspect />}
      {rt === 'prebid' && <PrebidInspect />}
      {(rt === 'amazon' || rt === 'amazon_video') && <AmazonInspect />}
      {(rt === 'ima' || rt === 'gam_video') && <ImaInspect />}
      {!['google','google_impression','google_click','prebid','amazon','amazon_video','ima','gam_video'].includes(rt) && <GenericInspect />}

      {/* Decoded a3p secure signals — every GAM and IMA request */}
      {(rt === 'google' || rt === 'ima' || rt === 'gam_video') && <A3pSection />}

      {/* Raw — collapsed by default */}
      <details onToggle={e => setShowRaw(e.target.open)} style={{ marginTop: 8 }}>
        <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--muted)', padding: '4px 0' }}>
          📋 Raw headers &amp; body
        </summary>
        {showRaw && (
          <div style={{ marginTop: 8, fontSize: 11 }}>
            {req.requestHeaders && Object.keys(req.requestHeaders).length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Request Headers</div>
                {Object.entries(req.requestHeaders).map(([k, v]) => (
                  <div key={k} style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted)', fontSize: 10, padding: '1px 0' }}>
                    <b>{k}:</b> {v}
                  </div>
                ))}
              </div>
            )}
            {req.responseHeaders && Object.keys(req.responseHeaders).length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Response Headers</div>
                {Object.entries(req.responseHeaders).map(([k, v]) => (
                  <div key={k} style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted)', fontSize: 10, padding: '1px 0' }}>
                    <b>{k}:</b> {v}
                  </div>
                ))}
              </div>
            )}
            {(() => {
              const bs = bodyState(req, 'request');
              const showNote = bs.state === 'trimmed' || (bs.state === 'absent' && methodExpectsBody(req.method));
              // Bodiless GAM GETs carry their params at validData.queryParams
              // (WP1 of body-coverage-spec removed the proxy-side hack that
              // stuffed them into requestBody). Show them honestly here
              // instead of a blank/"missing body" note — they are NOT a body.
              const qp = req.validData?.queryParams;
              const hasQueryParams = qp && typeof qp === 'object' && !Array.isArray(qp) && Object.keys(qp).length > 0;
              const showQueryParams = bs.state !== 'present' && hasQueryParams;
              if (bs.state !== 'present' && !showNote && !showQueryParams) return null;
              return (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    {bs.state === 'present'
                      ? 'Request Body'
                      : showQueryParams
                        ? 'Query parameters (no request body — GAM GET)'
                        : 'Request Body'}
                  </div>
                  {bs.state === 'present'
                    ? <pre style={BODY_PRE_STYLE}>
                        {(() => { try { return JSON.stringify(JSON.parse(req.requestBodyParsed || req.requestBody), null, 2); } catch { return req.requestBody; } })()}
                      </pre>
                    : showQueryParams
                      ? <pre style={BODY_PRE_STYLE}>
                          {JSON.stringify(qp, null, 2)}
                        </pre>
                      : <MissingBodyNote row={req} which="request" />}
                </div>
              );
            })()}
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Response Body</div>
              {req.responseBody
                ? <pre style={BODY_PRE_STYLE}>
                    {(() => { try { return JSON.stringify(JSON.parse(req.responseBody), null, 2); } catch { return String(req.responseBody).slice(0, 2000); } })()}
                  </pre>
                : <MissingBodyNote row={req} which="response" />}
            </div>
          </div>
        )}
      </details>
    </div>
  );
}

/* ── RawTab — Proxyman-style request + response viewer ─────────────────────
   Shows the full URL, all query params, request body (pretty-printed JSON),
   response headers, and response body. This is the "source of truth" view —
   no parsing or validation, just the raw captured data.
────────────────────────────────────────────────────────────────────────── */
function RawTab({ req, focus }) {
  const [a3pOpen, setA3pOpen] = React.useState(() => new Set()); // query-row indexes decoded in place
  const mono = { fontFamily: 'var(--font-mono)', fontSize: 11.5 };
  const label = { fontWeight: 700, fontSize: 11, color: 'var(--ink)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '18px 0 7px', display: 'flex', alignItems: 'center', gap: 6 };
  // Aligned key/value header row grid — key column is fixed-ish but shrinkable so
  // it never pushes the pane wide; value wraps/scrolls inside its own cell.
  const hdrRowStyle = (i, len) => ({
    display: 'grid', gridTemplateColumns: 'minmax(120px, 0.4fr) minmax(0, 1fr)', gap: 12,
    padding: '4px 10px', fontSize: 11.5,
    borderBottom: i < len - 1 ? '1px solid var(--line)' : 'none',
    background: i % 2 === 0 ? 'var(--bg)' : 'var(--bg-raised)',
  });
  const hdrKeyStyle = { ...mono, color: 'var(--muted)', wordBreak: 'break-word', overflowWrap: 'anywhere' };
  const hdrValStyle = { ...mono, color: 'var(--ink)', wordBreak: 'break-word', overflowWrap: 'anywhere', minWidth: 0 };
  const pre = BODY_PRE_STYLE;

  // Pretty-print helpers
  const prettyJson = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'object') {
      try { return JSON.stringify(raw, null, 2); } catch { return String(raw); }
    }
    if (typeof raw === 'string' && raw.startsWith('__BINARY__')) return describeBinaryBody(raw) || '[Binary]';
    try { return JSON.stringify(JSON.parse(raw), null, 2); } catch {}
    return String(raw);
  };

  // Parse URL query params into sorted rows
  const queryRows = (() => {
    try {
      const rows = [];
      new URL(req.url).searchParams.forEach((v, k) => rows.push([k, v]));
      return rows;
    } catch { return []; }
  })();

  const hdrRows = (obj) => Object.entries(obj || {});

  const requestBodyText  = prettyJson(req.requestBody  || req.requestBodyParsed);
  const responseBodyText = prettyJson(req.responseBody);

  // ── Deep-link focus: a validation verdict asked us to show the exact bytes it
  // fired on. We locate that field across query params / request body / response
  // body, highlight every occurrence, and auto-scroll to the first hit. This is
  // what lets a QA confirm a verdict in-tool instead of re-checking in Proxyman.
  const focusToken = (focus?.field || '').toLowerCase().trim();
  const focusLeaf  = focusToken.includes('.') ? focusToken.split('.').pop() : focusToken;
  const tokOk = (t) => t && t.length >= 2;
  const hitsToken = (s) => {
    const str = String(s).toLowerCase();
    return (tokOk(focusToken) && str.includes(focusToken)) || (tokOk(focusLeaf) && str.includes(focusLeaf));
  };
  const matchParam = (k, v) => hitsToken(k) || hitsToken(v);
  const firstParamHit = (tokOk(focusToken) || tokOk(focusLeaf)) ? queryRows.findIndex(([k, v]) => matchParam(k, v)) : -1;
  const reqBodyHit  = firstParamHit < 0 && requestBodyText  && hitsToken(requestBodyText);
  const respBodyHit = firstParamHit < 0 && !reqBodyHit && responseBodyText && hitsToken(responseBodyText);
  const focusActive = !!(focus?.field) && (tokOk(focusToken) || tokOk(focusLeaf));
  const focusFound  = firstParamHit >= 0 || reqBodyHit || respBodyHit;
  const focusRef = React.useRef(null);
  React.useEffect(() => {
    if (focus?.ts && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focus?.ts]);
  // Wrap every occurrence of the focus token in <mark> for visible evidence.
  const renderHl = (text) => {
    if (!focusActive) return text;
    const t = String(text);
    const low = t.toLowerCase();
    const tok = tokOk(focusToken) && low.includes(focusToken) ? focusToken
      : (tokOk(focusLeaf) && low.includes(focusLeaf) ? focusLeaf : null);
    if (!tok) return text;
    const out = []; let from = 0, idx, key = 0;
    while ((idx = low.indexOf(tok, from)) !== -1) {
      if (idx > from) out.push(t.slice(from, idx));
      out.push(<mark key={key++} style={{ background: 'var(--warn-bg)', color: 'var(--ink)', borderRadius: 2, padding: '0 1px' }}>{t.slice(idx, idx + tok.length)}</mark>);
      from = idx + tok.length;
    }
    if (from < t.length) out.push(t.slice(from));
    return out;
  };

  return (
    <div style={{ padding: '10px 12px', fontSize: 12 }}>

      {/* ── Evidence banner — shown when a validation verdict deep-linked here ── */}
      {focusActive && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
          padding: '6px 10px', borderRadius: 6, fontSize: 11,
          background: focusFound ? 'var(--success-bg, #ecfdf5)' : 'var(--warn-bg, #fffbeb)',
          border: `1px solid ${focusFound ? 'var(--success-border, #6ee7b7)' : 'var(--warn-border, #fde68a)'}`,
          color: focusFound ? 'var(--success-text, #065f46)' : 'var(--warn-text, #92400e)',
        }}>
          <span style={{ fontWeight: 700 }}>{focusFound ? '🔍 Evidence' : '🔍 Looking for'}:</span>
          <code style={{ fontFamily: 'var(--font-mono)' }}>{focus.field}</code>
          <span style={{ opacity: 0.8 }}>
            {focusFound
              ? `— highlighted in ${firstParamHit >= 0 ? 'query params' : reqBodyHit ? 'request body' : 'response body'}`
              : '— not present in this request (the rule may have evaluated its absence)'}
          </span>
        </div>
      )}

      {/* ── URL ── */}
      <div style={label}>URL</div>
      <pre style={{ ...pre, maxHeight: 80 }}>{req.url || '—'}</pre>
      {req.url && (
        <button
          onClick={() => { copyToClipboard(req.url); }}
          style={{ fontSize: 10, marginTop: 4, padding: '2px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}
        >Copy URL</button>
      )}

      {/* ── Query Params ── */}
      {queryRows.length > 0 && (
        <>
          <div style={label}>Query Params ({queryRows.length})</div>
          <div style={{ border: '1px solid var(--line)', borderRadius: 4, overflow: 'hidden' }}>
            {queryRows.map(([k, v], i) => {
              const isHit = focusActive && matchParam(k, v);
              return (
              <div key={k + i} ref={i === firstParamHit ? focusRef : null} style={{
                display: 'grid', gridTemplateColumns: '160px 1fr',
                padding: '3px 8px', fontSize: 11,
                borderBottom: i < queryRows.length - 1 ? '1px solid var(--line)' : 'none',
                background: isHit ? 'var(--success-bg, #ecfdf5)' : (i % 2 === 0 ? 'var(--bg)' : 'var(--bg-raised)'),
                boxShadow: i === firstParamHit ? 'inset 3px 0 0 var(--success-text, #10b981)' : 'none',
              }}>
                <span style={{ ...mono, color: 'var(--primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{renderHl(k)}</span>
                <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
                  <span style={{ ...mono, color: 'var(--ink)', wordBreak: 'break-all', flex: 1, minWidth: 0, maxHeight: 120, overflowY: 'auto' }}>{renderHl(v)}</span>
                  {v.length > 120 && (
                    <button
                      type="button"
                      title="Copy full value"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const ok = copyToClipboard(v);
                        const b = e.currentTarget;
                        b.textContent = ok ? 'copied' : 'failed';
                        setTimeout(() => { try { b.textContent = 'copy'; } catch {} }, 1200);
                      }}
                      style={{ flex: '0 0 auto', fontSize: 10, padding: '1px 6px', borderRadius: 4, border: '1px solid var(--line)', background: 'var(--card)', color: 'var(--muted)', cursor: 'pointer' }}
                    >copy</button>
                  )}
                  {(k === 'a3p' || k === 'a3') && v && (
                    <button
                      type="button"
                      title="Decode the adapters registered in this a3p, here"
                      aria-pressed={a3pOpen.has(i)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setA3pOpen((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
                      }}
                      style={{ flex: '0 0 auto', fontSize: 10, padding: '1px 6px', borderRadius: 4, border: '1px solid var(--line)', background: a3pOpen.has(i) ? 'var(--info-bg)' : 'var(--card)', color: 'var(--muted)', cursor: 'pointer' }}
                    >{a3pOpen.has(i) ? 'hide' : 'decode'}</button>
                  )}
                </span>
                {(k === 'a3p' || k === 'a3') && a3pOpen.has(i) && (
                  <div style={{ gridColumn: '1 / -1' }}><A3pInlineDecode value={v} /></div>
                )}
              </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Request Headers ── */}
      {hdrRows(req.requestHeaders).length > 0 && (
        <>
          <div style={label}>Request Headers</div>
          <div style={{ border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden', background: 'var(--bg)' }}>
            {hdrRows(req.requestHeaders).map(([k, v], i) => (
              <div key={k} style={hdrRowStyle(i, hdrRows(req.requestHeaders).length)}>
                <span style={hdrKeyStyle}>{k}</span>
                <span style={hdrValStyle}>{String(v)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Request Body ── */}
      {requestBodyText ? (
        <>
          <div style={label}>Request Body ({req.method || 'GET'})</div>
          <pre ref={reqBodyHit ? focusRef : null} style={pre}>{renderHl(requestBodyText.slice(0, 20000))}{requestBodyText.length > 20000 ? '\n…(truncated)' : ''}</pre>
          <button
            onClick={() => { copyToClipboard(requestBodyText); }}
            style={{ fontSize: 10, marginTop: 4, padding: '2px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}
          >Copy</button>
        </>
      ) : (bodyState(req, 'request').state === 'trimmed'
          || (bodyState(req, 'request').state === 'absent' && methodExpectsBody(req.method))) ? (
        <>
          <div style={label}>Request Body ({req.method || 'GET'})</div>
          <MissingBodyNote row={req} which="request" />
        </>
      ) : null}

      {/* ── Response Status + Headers ── */}
      <div style={label}>Response Headers</div>
      <div style={{ ...mono, fontWeight: 600, padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: '6px 6px 0 0', color: (req.status >= 400 ? 'var(--error-text)' : 'var(--success-text)') }}>
        HTTP {req.status || '?'} {req.statusText || ''} · {req.durationMs != null ? `${Math.round(req.durationMs)}ms` : '?'}
      </div>
      {hdrRows(req.responseHeaders).length > 0 && (
        <div style={{ border: '1px solid var(--line)', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden', background: 'var(--bg)' }}>
          {hdrRows(req.responseHeaders).map(([k, v], i) => (
            <div key={k} style={hdrRowStyle(i, hdrRows(req.responseHeaders).length)}>
              <span style={hdrKeyStyle}>{k}</span>
              <span style={hdrValStyle}>{String(v)}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Response Body ── */}
      {(() => {
        // Try to extract ad_html from ad_networks for Download Creative button
        const adHtml = (() => {
          if (!req.responseBody || typeof req.responseBody !== 'string') return null;
          if (req.responseBody.startsWith('__BINARY__')) return null;
          try {
            const j = JSON.parse(req.responseBody);
            return j?.ad_networks?.[0]?.ad?.ad_html || j?.ad_networks?.[0]?.ad?.html || null;
          } catch { return null; }
        })();

        const dlBlob = (content, name, type = 'text/plain') => {
          const b = new Blob([content], { type });
          const a = document.createElement('a');
          a.href = URL.createObjectURL(b);
          a.download = name;
          a.click();
          URL.revokeObjectURL(a.href);
        };

        const btnStyle = { fontSize: 10, marginTop: 4, padding: '2px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' };

        return (
          <>
            <div style={label}>Response Body</div>
            {responseBodyText ? (
              <>
                <pre ref={respBodyHit ? focusRef : null} style={pre}>{renderHl(responseBodyText.slice(0, 20000))}{responseBodyText.length > 20000 ? '\n…(truncated)' : ''}</pre>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  {!responseBodyText.startsWith('[Binary') && (
                    <button style={btnStyle} onClick={() => { copyToClipboard(responseBodyText); }}>
                      Copy JSON
                    </button>
                  )}
                  {!responseBodyText.startsWith('[Binary') && (
                    <button style={btnStyle} onClick={() => {
                      const slug = (req.url ? (() => { try { return new URL(req.url).hostname.replace(/\./g, '_'); } catch { return 'response'; } })() : 'response');
                      dlBlob(responseBodyText, `${slug}_body.json`, 'application/json');
                    }}>
                      ⬇ JSON
                    </button>
                  )}
                  {adHtml && (
                    <button
                      style={{ ...btnStyle, color: 'var(--primary)', borderColor: 'var(--primary)', fontWeight: 600 }}
                      onClick={() => {
                        const unit = (req.url ? (() => { try { return new URL(req.url).searchParams.get('iu') || 'creative'; } catch { return 'creative'; } })() : 'creative')
                          .replace(/\//g, '_').replace(/^_/, '');
                        dlBlob(adHtml, `creative_${unit}.html`, 'text/html');
                      }}
                    >
                      ⬇ Creative HTML
                    </button>
                  )}
                </div>
              </>
            ) : (
              <MissingBodyNote row={req} which="response" />
            )}
          </>
        );
      })()}
    </div>
  );
}

/* ── AiVerdictBanner ────────────────────────────────────────────────────────
   Advisory (source:'ai') display of Phase 1b adjudicator verdicts merged onto
   the row via the `ai_verdict` WS message. Purely informational — never gates
   the build or the UI severity. Renders nothing until verdicts arrive. */
function AiVerdictBanner({ verdicts }) {
  if (!Array.isArray(verdicts) || verdicts.length === 0) return null;
  const color = (v) =>
    v === 'confirmed' ? '#b71c1c' : v === 'refuted' ? '#2e7d32' : '#ffb300';
  const icon = (v) =>
    v === 'confirmed' ? '✕' : v === 'refuted' ? '✓' : '?';
  return (
    <div style={{
      margin: '6px 0 10px', padding: '8px 10px', borderRadius: 6,
      border: '1px solid var(--border, var(--line))', background: 'rgba(120,120,200,0.06)',
      fontSize: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{
          background: '#5c6bc0', color: '#fff', borderRadius: 8,
          fontSize: 10, padding: '1px 6px', fontWeight: 600,
        }}>AI · advisory</span>
        <span style={{ opacity: 0.7 }}>{verdicts.length} verdict{verdicts.length !== 1 ? 's' : ''}</span>
      </div>
      {verdicts.map((v, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0' }}>
          <span style={{ color: color(v.verdict), fontWeight: 700, minWidth: 14, textAlign: 'center' }}>{icon(v.verdict)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div>
              <span style={{ fontWeight: 600 }}>{v.code || v.rubric || 'finding'}</span>
              <span style={{ marginLeft: 6, color: color(v.verdict), textTransform: 'uppercase', fontSize: 10 }}>{v.verdict}</span>
              {typeof v.confidence === 'number' && (
                <span style={{ marginLeft: 6, opacity: 0.6 }}>conf {Math.round(v.confidence * 100)}%</span>
              )}
              {v.tier && <span style={{ marginLeft: 6, opacity: 0.5, fontSize: 10 }}>· {v.tier}</span>}
            </div>
            {v.rationale && <div style={{ opacity: 0.75, marginTop: 1 }}>{v.rationale}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function RequestDetails({ req, onDiffClick, onReplayClick, headline }) {
  const [tab, setTab] = useState("inspect");
  const [openDebug, setOpenDebug] = useState(false); // modal visibility

  // Check verdicts for the Full analysis tab — computed ON DEMAND for just
  // this row (buildRowParamConformance.js), not read from AgentPanel's
  // session-wide Checks run (which this component tree has no access to).
  // The BU/App-Pack layer's rule evaluators are pure and single-row, so this
  // reuses them client-side instead of plumbing session state or adding a
  // server call. Dictionaries are fetched the same way AgentPanel.jsx does.
  const [apsDictionary, setApsDictionary] = useState([]);
  const [prebidDictionary, setPrebidDictionary] = useState({ params: {}, aliases: {} });
  useEffect(() => {
    let cancelled = false;
    getApsParamDictionary()
      .then((d) => { if (!cancelled) setApsDictionary(Array.isArray(d) ? d : []); })
      .catch(() => { /* getApsParamDictionary already degrades to [] */ });
    getPrebidParamDictionary()
      .then((d) => { if (!cancelled && d) setPrebidDictionary(d); })
      .catch(() => { /* getPrebidParamDictionary already degrades to empty maps */ });
    return () => { cancelled = true; };
  }, []);

  const rowConformance = useMemo(
    () => buildRowParamConformance(req, getPacks(), { apsDictionary, prebidDictionary }),
    [req, apsDictionary, prebidDictionary]
  );
  // Deep-link target for the Raw tab — set when a validation verdict's "View raw"
  // button is clicked, so the user can verify the exact bytes a rule fired on
  // without leaving for Proxyman. { field, ts } — ts forces re-scroll on repeat clicks.
  const [rawFocus, setRawFocus] = useState(null);
  const viewRaw = (field) => { setRawFocus({ field: field || '', ts: Date.now() }); setTab('raw'); };

  // Download full request+response as a HAR entry (same format as Proxyman export)
  const detailReq = req;
  const downloadHar = async () => {
    const [req] = await withStashedBodies([detailReq]);
    const started = req.startedAt || req.startedAtMs || req.timestamp || Date.now();
    const startedDate = typeof started === "number" ? new Date(started) : new Date(Date.parse(started) || Date.now());

    const har = buildHarFromRequests([req], { title: req.url || 'request' });

    const rtype = (req.requestType || "request").replace(/\s+/g, "-");
    let host = "";
    try { host = "-" + new URL(req.url).hostname.replace(/^www\./, "").split(".")[0]; } catch {}
    const ts = startedDate.toISOString().slice(0, 19).replace(/:/g, "-");
    const filename = `${rtype}${host}-${ts}.har`;

    const blob = new Blob([JSON.stringify(har, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Copy a markdown ticket summary of this row to the clipboard — pure
  // clipboard action, never crashes on malformed/partial request objects.
  const copyAsTicket = () => {
    try {
      const heading = `${req?.requestType || "request"} — ${(() => {
        try { return new URL(req?.url).hostname; } catch { return req?.url || "(unknown host)"; }
      })()}`;
      const lines = [`### ${heading}`, ""];
      lines.push(`- **URL**: ${req?.url || "(none)"}`);
      // WP6 (launch-signoff-spec.md §8) — thread the requestId + deep link
      // into the row-level ticket too, matching buildFindingTicket.js's
      // check-result tickets.
      if (req?.requestId) lines.push(`- **Request**: ${req.requestId} — ${requestLink(req.requestId)}`);
      lines.push(`- **Method / status**: ${req?.method || "?"} / ${req?.status ?? "?"}`);
      lines.push(`- **Time**: ${req?.startedAt || req?.startedAtMs || req?.timestamp || "(unknown)"}`);
      if (req?.step) lines.push(`- **Step**: ${req.step}`);
      if (req?.device) lines.push(`- **Device**: ${typeof req.device === "string" ? req.device : JSON.stringify(req.device)}`);

      const issues = Array.isArray(req?.issues) ? req.issues : [];
      lines.push("", "**Issues**");
      if (issues.length) {
        issues.forEach((iss) => {
          const sev = iss?.severity || iss?.level || "info";
          const msg = iss?.message || iss?.note || JSON.stringify(iss);
          lines.push(`- [${sev}] ${msg}`);
        });
      } else {
        lines.push("- none");
      }

      let vdBlock = "(validData omitted, too large)";
      try {
        const vdStr = JSON.stringify(req?.validData ?? {});
        if (vdStr.length <= 2000) vdBlock = vdStr;
      } catch { /* leave omitted note */ }
      lines.push("", "```json", vdBlock, "```");

      copyToClipboard(lines.join("\n"));
    } catch { /* never crash the detail pane over a copy action */ }
  };

  const showDebugBtn = isGAMRequest(req);
  // USB/logcat rows are request-only: no body, no response, maybe a truncated URL.
  const usbBlock = logcatActionBlockReason(req);
  const usbBtn = usbBlock ? { disabled: true, 'aria-disabled': true } : {};
  const usbBtnStyle = usbBlock ? { opacity: 0.4, cursor: 'not-allowed' } : {};

  const rewriteLines = describeRewrite(req && req.iuRewrite);
  const patchLines = describeChaosPatch(req && req.chaos);
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}>
      {rewriteLines.length > 0 && (
        <div role="note" style={{ fontSize: 12, padding: '6px 12px', borderBottom: '1px solid var(--line)', background: 'rgba(30,120,220,.08)', color: 'var(--ink)' }}>
          🔀 <b>Rewritten by IU Rewrite</b> before it left the proxy: {rewriteLines.join(' · ')}. The bodies and URL below are what was sent.
        </div>
      )}
      {patchLines.length > 0 && (
        <div role="note" style={{ fontSize: 12, padding: '6px 12px', borderBottom: '1px solid var(--line)', background: 'var(--warn-bg)', color: 'var(--ink)' }}>
          🌀 <b>Response patched by a Chaos rule</b> before it reached the device: {patchLines.join(' · ')}. The response below is what the device received.
        </div>
      )}
      <div style={{ ...styles.tabbar, justifyContent: "space-between" }}>
        <div style={{ display: "flex" }}>
        {/* The Validate tab is gone: per-request validator findings are now the
            Checks engine's job, and its own `issues` are what those rules read.
            The requests-table severity badges still come from runValidations,
            so nothing about detection changed — only this one display of it. */}
        {["inspect", "raw", "full"].map((t) => {
          let badge = null;
          if (t === "raw") {
            const hasBody = !!(req.requestBody || req.responseBody);
            if (hasBody) badge = <span style={{ marginLeft: 4, width: 6, height: 6, borderRadius: '50%', background: 'var(--primary)', display: 'inline-block', verticalAlign: 'middle' }} />;
          }
          const label = t === "full" ? "Full analysis" : t[0].toUpperCase() + t.slice(1);
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{ ...styles.tabBtn, ...(tab === t ? styles.tabActive : {}) }}
            >
              {label}
              {badge}
            </button>
          );
        })}
        </div>
        {/* wrap, never clip: six actions do not fit a narrow detail pane, and
            an overflowing row silently cut 'Copy link' in half. */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0 }}>
          {showDebugBtn && (
            <button
              onClick={() => setOpenDebug(true)}
              {...usbBtn}
              style={{ ...styles.btn, fontSize: 12, padding: "5px 10px", color: 'var(--primary)', borderColor: 'var(--primary)', ...usbBtnStyle }}
              title={usbBlock || "Open Creative Debugger — inspect ad HTML, wrappers, DV, Mobian, OMID"}
            >
              🔍 Debug
            </button>
          )}
          <button
            onClick={downloadHar}
            style={{ ...styles.btn, fontSize: 12, padding: "5px 10px" }}
            title="Download full request + response as HAR"
          >
            ↓ HAR
          </button>
          <button
            onClick={onDiffClick}
            {...usbBtn}
            style={{ ...styles.btn, fontSize: 12, padding: "5px 10px", ...usbBtnStyle }}
            title={usbBlock || "Diff this request's URL/body against pasted JSON or a URL"}
          >
            Diff vs pasted
          </button>
          <button
            onClick={onReplayClick}
            {...usbBtn}
            style={{ ...styles.btn, fontSize: 12, padding: "5px 10px", ...usbBtnStyle }}
            title={usbBlock || "Fire this request again through the proxy"}
          >
            Replay
          </button>
          <button
            onClick={copyAsTicket}
            style={{ ...styles.btn, fontSize: 12, padding: "5px 10px" }}
            title="Copy a markdown ticket summary of this request to the clipboard"
          >
            Copy ticket
          </button>
          <button
            onClick={() => { if (req?.requestId) copyToClipboard(requestLink(req.requestId)); }}
            disabled={!req?.requestId}
            style={{
              ...styles.btn, fontSize: 12, padding: "5px 10px",
              ...(req?.requestId ? {} : { opacity: 0.4, cursor: "not-allowed" }),
            }}
            title={req?.requestId
              ? "Copy a deep link that jumps straight to this request"
              : "No request id captured for this row — deep link unavailable"}
          >
            Copy link
          </button>
        </div>
      </div>

      <div style={styles.detailsScroll}>
        <AiVerdictBanner verdicts={req?.validData?.aiVerdicts} />
        {tab === "inspect" && (
          headline
            ? <RequestHeadline headline={headline}><AdInspectView req={req} /></RequestHeadline>
            : <AdInspectView req={req} />
        )}
        {tab === "raw" && <RawTab req={req} focus={rawFocus} />}
        {tab === "full" && (
          <FullRequestAnalysisPane
            row={req}
            gamParamRows={rowConformance.gamParamRows}
            apsParamRows={rowConformance.apsParamRows}
            sessionPlatform={rowConformance.sessionPlatform}
          />
        )}
      </div>

      {/* Creative debugger modal */}
      {showDebugBtn && <AdDebugModal open={openDebug} onClose={() => setOpenDebug(false)} req={req} />}
    </div >
  );
}

/* ---------------- styles ---------------- */

const styles = {
  wrap: { display: "grid", gap: 12, flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden" },
  leftPane: {
    border: "1px solid var(--line)",
    borderRadius: 12,
    background: "var(--card)",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,       // critical: lets flex child shrink so inner div scrolls
    overflow: "hidden", // clip content to panel boundary
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  rightPane: {
    border: "1px solid var(--line)",
    borderRadius: 12,
    // Opaque surface so left-hand table/summary content never shows through the
    // detail pane. Own stacking context (isolation) keeps it above the underlying grid.
    background: "var(--surface, var(--bg))",
    isolation: "isolate",
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,       // critical: lets flex child shrink so inner div scrolls
    overflow: "hidden", // clip content to panel boundary
    flex: 1,
    width: "100%",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
  },
  resizer: {
    cursor: "col-resize",
    background: "var(--line)",
    borderRadius: 6,
    boxShadow: "none",
  },

  toolbar: {
    borderBottom: "1px solid var(--line)",
    background: "var(--card)",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  search: { width: "100%", padding: "8px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, outline: "none", background: "var(--bg)", color: "var(--ink)" },
  filters: { display: "flex", gap: 12, alignItems: "center" },
  chk: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink)" },
  iconBtn: {
    fontSize: 14, padding: "3px 7px", borderRadius: 6, cursor: "pointer",
    border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink)",
    display: "inline-flex", alignItems: "center", gap: 3, lineHeight: 1,
    transition: "background 0.1s",
  },

  validationBar: {
    borderBottom: "1px solid var(--line)",
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start", // Fix: align left
    gap: 16, // Fix: more space
    flexWrap: "wrap",
    minHeight: "auto",
  },
  countChip: { display: "inline-flex", alignItems: "center", gap: 6, color: "var(--ink)", fontSize: 12, fontWeight: 500 },
  countDot: { width: 10, height: 10, borderRadius: 999, display: "inline-block" },
  segWrap: {
    display: "inline-flex",
    gap: 6,
    background: "var(--card)",
    padding: 4,
    borderRadius: 10,
    border: "1px solid var(--line)",
  },
  segBtn: { border: "none", background: "transparent", padding: "6px 10px", borderRadius: 8, fontSize: 12, cursor: "pointer", color: "var(--ink)" },
  segActive: { background: "var(--bg)", boxShadow: "0 1px 2px rgba(0,0,0,.06)" },

  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },

  groupHeader: {
    background: "var(--surface, var(--bg))",
    borderTop: "2px solid var(--line)",
    borderBottom: "1px solid var(--line)",
    padding: "7px 12px",
  },
  groupToggle: {
    border: "none",
    background: "transparent",
    borderRadius: 4,
    fontSize: 10,
    lineHeight: "16px",
    padding: "2px 5px",
    cursor: "pointer",
    color: "var(--muted)"
  },

  visitHeader: {
    background: "var(--bg)",
    borderBottom: "1px solid var(--line)",
    padding: "5px 12px 5px 28px",
    /* borderLeft color is set inline per visit using the accent palette */
  },

  visitToggle: {
    border: "none",
    background: "transparent",
    borderRadius: 4,
    fontSize: 10,
    lineHeight: "16px",
    padding: "2px 5px",
    cursor: "pointer",
    color: "var(--muted)"
  },

  detailsHeader: {
    borderBottom: "1px solid var(--line)",
    background: "var(--card)",
    padding: "8px 12px",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    minWidth: 0,
  },
  emptyPane: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)" },

  tabbar: { borderBottom: "1px solid var(--line)", background: "var(--card)", padding: 8, display: "flex", gap: 8, flexWrap: "wrap" },
  tabBtn: {
    border: "1px solid transparent",
    background: "transparent",
    padding: "6px 12px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 8,
    cursor: "pointer",
    color: "var(--muted)",
    transition: "all 0.2s"
  },
  tabActive: { background: "var(--bg)", color: "var(--ink)", boxShadow: "0 1px 2px rgba(0,0,0,.05)", borderColor: "var(--line)" },

  detailsScroll: { padding: 16, overflowY: "auto", overflowX: "hidden", flex: 1, minHeight: 0, minWidth: 0, background: "var(--surface, var(--bg))" },

  sectionHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6, minWidth: 0 },
  h3: { margin: "0 0 6px", fontSize: 13, color: "var(--ink)" },

  kvBox: { border: "1px solid var(--line)", borderRadius: 10 },
  kvRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) minmax(0,2fr)",
    gap: 8,
    padding: "8px 10px",
    borderBottom: "1px solid var(--line)",
    alignItems: "start",
    minWidth: 0,
  },
  kvKey: {
    fontWeight: 600,
    color: "var(--ink)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  kvVal: {
    color: "var(--ink)",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },

  code: {
    border: "1px solid var(--line)",
    borderRadius: 10,
    background: "var(--bg)",
    padding: 12,
    maxHeight: "52vh",
    overflowY: "auto",
    overflowX: "auto", // Fix: allow horizontal scroll
    fontFamily: "var(--font-mono)",
    fontSize: 12.5,
    lineHeight: 1.55,
    letterSpacing: "0.003em",
    fontVariantLigatures: "none",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  empty: { color: "var(--muted)", fontStyle: "italic" },
  btn: {
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 500,
    border: "1px solid var(--line)",
    borderRadius: 6,
    background: "var(--card)",
    cursor: "pointer",
    whiteSpace: "nowrap",
    color: "var(--ink)"
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 7px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: "15px",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  },
  kvGrid: { display: "grid", gap: 6, gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", minWidth: 0 },
};
